import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  FaBookmark,
  FaCalendarAlt,
  FaBan,
  FaChevronDown,
  FaEllipsisH,
  FaEye,
  FaPrint,
  FaGripVertical,
  FaHashtag,
  FaPaperPlane,
  FaPen,
  FaPlus,
  FaTrashAlt,
  FaUser,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Loader from "../Loader/Loader";
import { CURRENCY_OPTIONS } from "../Applications/ApplicationCostSection";
import { INVOICE_DOCUMENT_TYPES } from "../../utilis/invoiceDocumentTypes";
import { mapRowToInvoiceBrandingForm } from "../../utilis/invoiceBrandingHelpers";
import {
  buildInvoiceSettingPayload,
  getInvoiceSettingApiMessage,
  isInvoiceSettingApiSuccess,
  mapRowToInvoiceForm,
  normalizeInvoiceSettingRecord,
} from "../../utilis/invoiceSettingHelpers";
import {
  fetchInvoiceCatalog,
  invalidateInvoiceCatalog,
} from "../../utilis/invoiceWorkspaceApi";
import {
  buildClientDisplayProfile,
  getClientIdFromRecord,
  getClientInitials,
  mergeInvoiceClientProfiles,
  normalizeClientRecordSource,
} from "../../utilis/applicationCostHelpers";
import {
  buildDraftInvoiceNumber,
  buildInvoiceNumberWithClientId,
  extractNextInvoiceNumberFromResponse,
  replaceDocumentTypePrefixInInvoiceNumber,
} from "../../utilis/invoiceNumberHelpers";
import {
  calculateInvoiceLineTotals,
  createEmptyLineItem,
  createInitialLineItemsFromApplication,
  displayLineItemInputValue,
  formatInvoiceMoney,
  formatInvoiceNumberDisplay,
  getDefaultTaxTypeFromApplication,
  getLineDisplayTotalAmount,
  getLineGrossAmount,
  getLineTotalBeforeDiscount,
  resolveExTaxFooterTotals,
  resolveInvoiceRecordPayableTotals,
  resolvePayableInvoiceTotal,
  invoiceUsesSegregatedLineDiscounts,
  resolveApplicationGstPercentage,
  resolveLineItemTaxRate,
  withSyncedLineDiscount,
  resolveInvoiceDocumentTaxType,
  normalizeTaxType,
  normalizeDecimalInput,
  normalizeDiscountAmountInput,
  normalizeInvoiceAmountInput,
  normalizePercentInput,
  normalizeQuantityInput,
  resolveLineDiscountPercent,
  roundInvoiceAmount,
  sanitizeDecimalInput,
  sanitizeDiscountDecimalInput,
  sanitizeIntegerInput,
  sanitizePercentInput,
  isCommissionTuitionInvoiceLine,
  isBonusInvoiceLine,
  applyDocumentTaxToInstallmentLines,
  computeCommissionSuggestionInvoiceTotals,
  validateLineItemDiscounts,
  validateLineItemDiscountForm,
} from "../../utilis/invoiceLineItemHelpers";
import { syncCommissionInvoiceLineMetrics } from "../reports/commissions/detail/commissionDiscountUtils";
import { DEFAULT_COMMISSION_GST_PERCENT } from "../reports/commissions/detail/commissionPlanUtils";
import { buildInstallmentSuggestions } from "../reports/commissions/commissionInvoiceLineSuggestions";
import {
  DEFAULT_INVOICE_STATUS,
  getInvoiceStatusBadgeClass,
  getInvoiceStatusLabel,
  INVOICE_STATUSES,
  isInvoiceEditableWorkflowStatus,
  normalizeInvoiceStatus,
  resolveAwaitingApprovalActionStatus,
  resolveCreateInvoiceStatusFromPayment,
} from "../../utilis/invoiceStatusHelpers";
import { canEditFinanceInvoiceByStatus } from "../../utilis/invoicePermissionHelpers";
import {
  resolveInvoiceDisplayTitle,
  resolveInvoiceDocumentAmountDue,
} from "../../utilis/invoicePreviewHelpers";
import {
  buildInvoiceCreatePayload,
  sumCreateInvoicePayments,
} from "../../utilis/invoiceCreateHelpers";
import { buildInvoiceEmailHtml } from "../../utilis/invoiceEmailHtmlHelpers";
import {
  buildInvoiceUpdatePayload,
  filterInvoiceLedgerTransactions,
  resolveFinanceInvoiceTransactions,
  resolveInvoiceDueAmount,
  getInvoiceDiscountAmount,
  getInvoiceDiscountReason,
  getInvoiceDiscountLedgerId,
  getActiveInvoiceDiscountTransaction,
  inferDocumentTypeFromInvoice,
  mapClientSnapshotToProfile,
  mapInvoiceNotesToHistoryEntries,
  mapSavedLineItemsToForm,
  hydrateEditLineItemDiscounts,
  resolveLineDiscountTransactionId,
  resolvePrimaryInvoiceLineItemId,
  resolveInvoiceLineItemsFromRecord,
  resolveInvoicePaidAmount,
  resolveInvoicePreviewPaymentDate,
  toInvoiceDateInputValue,
} from "../../utilis/invoiceEditHelpers";
import InvoiceEditHistoryNotesPanel from "./InvoiceEditHistoryNotesPanel";
import InvoiceEditPaymentsPanel from "./InvoiceEditPaymentsPanel";
import InvoiceEditSummaryPanel from "./InvoiceEditSummaryPanel";
import InvoiceLineDiscountRow from "./InvoiceLineDiscountRow";
import InvoiceLineItemDiscountSection from "./InvoiceLineItemDiscountSection";
import InvoiceXeroPreview from "./InvoiceXeroPreview";
import FinanceInvoiceApproveEmailModal from "./FinanceInvoiceApproveEmailModal";
import {
  buildInvoiceApplyDiscountRequest,
  buildInvoiceUpdateDiscountRequest,
  buildInvoiceDiscountPayload,
  calculateInvoiceDiscountAmount,
  mapInvoiceDiscountToFormState,
  mergeInvoiceDiscountIntoTotals,
  validateInvoiceDiscountForm,
  validateInvoiceDiscountExceedsTotal,
  getInvoiceDiscountExceedsTotalMessage,
  isValidCustomDiscountAmount,
} from "../../utilis/invoiceDiscountHelpers";
import {
  INVOICE_AMOUNTS_ARE_OPTIONS,
  resolveGstPercentageForCurrency,
} from "../../utilis/invoiceCurrencyHelpers";
import {
  buildClientApplicationFeeSuggestionOptions,
  buildLineItemsFromClientApplication,
} from "../../utilis/invoiceClientApplicationHelpers";
import { resolveCommissionPaymentTargetForIndex } from "../reports/commissions/commissionInvoicePaymentHelpers";
import CommissionBillToContactPopover from "../reports/commissions/CommissionBillToContactPopover";
import {
  mergeBillToPrintOptionsIntoSnapshot,
  normalizeCommissionBillToPrintOptions,
  readBillToPrintOptionsFromSnapshot,
} from "../reports/commissions/commissionBillToUtils";
import {
  mergeInvoiceDisplayOptionsIntoSnapshot,
  normalizeInvoiceDisplayOptions,
  readInvoiceDisplayOptionsFromSnapshot,
  toCommissionPartyPrintOptions,
} from "../reports/commissions/commissionInvoiceDisplayOptions";
import {
  updateCommissionInvoiceBillToPrintOptions,
  updateFinanceInvoiceBillToPrintOptions,
  updateCommissionProviderName,
  updateProviderBillingAddress,
} from "../reports/commissions/commissionsApi";
import InvoicePreviewCustomizePanel from "./InvoicePreviewCustomizePanel";
import { prepareInvoicePaymentLineItems } from "../../utilis/financeInvoicePaymentLineHelpers";
import {
  buildReceiptPreviewUrl,
  isAllowedReceiptFile,
} from "../../utilis/financeInvoicePaymentHelpers";

const CREATE_SAVE_STATUS_OPTIONS = [
  { value: "awaiting_payment", label: "Save & Awaiting Payment" },
  { value: "awaiting_approval", label: "Save & Awaiting Approval" },
];

const COMMISSION_CREATE_SAVE_MENU_OPTIONS = [
  {
    value: "save_and_another",
    label: "Save & add another",
    saveAndAnother: true,
  },
  {
    value: "awaiting_approval",
    label: "Submit for approval",
    status: "awaiting_approval",
  },
];

const COMMISSION_CREATE_APPROVE_MENU_OPTIONS = [
  { value: "approve", label: "Approve", status: "awaiting_payment" },
  {
    value: "approve_and_another",
    label: "Approve & add another",
    status: "awaiting_payment",
    saveAndAnother: true,
  },
];

const COMMISSION_AWAITING_APPROVAL_SAVE_MENU_OPTIONS = [
  {
    value: "save_and_another",
    label: "Save & add another",
    saveAndAnother: true,
  },
];

const TAX_TYPE_OPTIONS = [
  { value: "inclusive", label: "Tax inclusive" },
  { value: "exclusive", label: "Tax exclusive" },
  { value: "notax", label: "No tax" },
];

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addMonthsToDate(date, months = 1) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function dueDateFromIssueDate(issueDateValue, months = 1) {
  if (!issueDateValue) return "";
  const [year, month, day] = issueDateValue.split("-").map(Number);
  if (!year || !month || !day) return "";
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return "";
  return toDateInputValue(addMonthsToDate(parsed, months));
}

function XeroField({ label, required, error, className = "", children }) {
  return (
    <div className={`invoice-xero-field ${className}`.trim()}>
      <label className="invoice-xero-field__label">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error && <div className="invoice-xero-field__error">{error}</div>}
    </div>
  );
}

function XeroIconField({
  label,
  required,
  error,
  className = "",
  icon: Icon,
  children,
}) {
  return (
    <XeroField
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <div className="invoice-xero-icon-control">
        {Icon ? (
          <span className="invoice-xero-icon-control__icon" aria-hidden="true">
            <Icon />
          </span>
        ) : null}
        <div className="invoice-xero-icon-control__body">{children}</div>
      </div>
    </XeroField>
  );
}

const INQUIRY_INVOICE_FEE_TYPES = new Set(["application_fee", "material_fee"]);

function isInquiryInvoiceFeeType(feeType) {
  return INQUIRY_INVOICE_FEE_TYPES.has(String(feeType || "").toLowerCase());
}

const DESCRIPTION_SUGGESTION_SECTIONS = [
  { key: "installments", label: "Commissionable fee" },
  { key: "bonus", label: "Bonus" },
  { key: "other_fees", label: "Application & material fees" },
];

function resolveDescriptionSuggestionPresentation(option = {}) {
  if (option.displayTitle || option.displayAmount) {
    return {
      title: option.displayTitle || option.label || "Line item",
      subtitle: option.displaySubtitle || "",
      amount: option.displayAmount || "",
      amountNote: option.displayAmountNote || "",
    };
  }

  const fields = option.fields || {};
  const feeType = String(fields.fee_type || "").toLowerCase();
  if (feeType === "application_fee") {
    return {
      title: "Application fee",
      subtitle: "",
      amount: "",
      amountNote: "",
    };
  }
  if (feeType === "material_fee") {
    return {
      title: "Material fee",
      subtitle: "",
      amount: "",
      amountNote: "",
    };
  }
  if (Number(fields.commission_installment_id)) {
    return {
      title: "Commissionable fee",
      subtitle: "",
      amount: "",
      amountNote: "",
    };
  }
  if (Number(fields.bonus_installment_id)) {
    return {
      title: "Bonus",
      subtitle: "",
      amount: "",
      amountNote: "",
    };
  }

  return {
    title: option.label || "Line item",
    subtitle: "",
    amount: "",
    amountNote: "",
  };
}

function resolveDescriptionSuggestionSectionKey(option = {}) {
  if (Number(option?.fields?.commission_installment_id)) return "installments";

  if (Number(option?.fields?.bonus_installment_id)) return "bonus";

  const feeType = String(option?.fields?.fee_type || "").toLowerCase();
  if (feeType === "tuition") return "installments";
  if (feeType === "bonus") return "bonus";
  if (isInquiryInvoiceFeeType(feeType)) return "other_fees";

  return "other_fees";
}

function groupDescriptionSuggestionOptionsWithSections(options = []) {
  const grouped = new Map(
    DESCRIPTION_SUGGESTION_SECTIONS.map(({ key }) => [key, []]),
  );

  for (const option of options) {
    const sectionKey = resolveDescriptionSuggestionSectionKey(option);
    if (!grouped.has(sectionKey)) {
      grouped.set(sectionKey, []);
    }
    grouped.get(sectionKey).push(option);
  }

  const result = [];
  for (const { key, label } of DESCRIPTION_SUGGESTION_SECTIONS) {
    const sectionOptions = grouped.get(key) || [];
    if (!sectionOptions.length) continue;

    result.push({
      id: `section-${key}`,
      label,
      kind: "divider",
    });
    result.push(...sectionOptions);
  }

  return result;
}

function buildCommissionDescriptionSuggestionOptions(
  lineItemSuggestions,
  lineItems,
  { editingRowId = null } = {},
) {
  const usedApplicationFees = new Set();

  for (const row of lineItems || []) {
    if (editingRowId && row.id === editingRowId) continue;

    const applicationId = row?.application_id;
    const feeType = String(row?.fee_type || "").toLowerCase();
    if (applicationId && isInquiryInvoiceFeeType(feeType)) {
      usedApplicationFees.add(`${applicationId}:${feeType}`);
    }
  }

  const availableOptions = (
    Array.isArray(lineItemSuggestions) ? lineItemSuggestions : []
  )
    .filter(
      (option) => option?.id && option?.label && option?.kind !== "divider",
    )
    .filter((option) => {
      const applicationId = option?.fields?.application_id;
      const feeType = String(option?.fields?.fee_type || "").toLowerCase();
      if (
        applicationId &&
        isInquiryInvoiceFeeType(feeType) &&
        usedApplicationFees.has(`${applicationId}:${feeType}`)
      ) {
        return false;
      }
      return true;
    })
    .map((option) => ({
      id: option.id,
      label: option.label,
      kind: "suggestion",
      fields: option.fields || {},
      displayTitle: option.displayTitle,
      displaySubtitle: option.displaySubtitle,
      displayAmount: option.displayAmount,
      displayAmountNote: option.displayAmountNote,
    }));

  return groupDescriptionSuggestionOptionsWithSections(availableOptions);
}

function renderDescriptionSuggestionOptions(options, rowId, onSelect) {
  return options.map((option) => {
    if (option.kind === "divider") {
      return (
        <li
          key={option.id}
          className="invoice-xero-desc-suggest__divider"
          role="separator"
        >
          <span>{option.label}</span>
        </li>
      );
    }

    const presentation = resolveDescriptionSuggestionPresentation(option);

    return (
      <li key={option.id}>
        <button
          type="button"
          className="invoice-xero-desc-suggest__option invoice-xero-desc-suggest__option--rich"
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(rowId, option);
          }}
        >
          <span className="invoice-xero-desc-suggest__option-main">
            <span className="invoice-xero-desc-suggest__option-title">
              {presentation.title}
            </span>
            {presentation.subtitle ? (
              <span className="invoice-xero-desc-suggest__option-subtitle">
                {presentation.subtitle}
              </span>
            ) : null}
          </span>
          {presentation.amount ? (
            <span className="invoice-xero-desc-suggest__option-amount">
              <span>{presentation.amount}</span>
              {presentation.amountNote ? (
                <span className="invoice-xero-desc-suggest__option-amount-note">
                  {presentation.amountNote}
                </span>
              ) : null}
            </span>
          ) : null}
        </button>
      </li>
    );
  });
}

function DescriptionSuggestionList({
  open,
  getAnchorElement,
  rowId,
  options,
  onSelect,
}) {
  const listRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({ visibility: "hidden" });

  const updateMenuPosition = useCallback(() => {
    const anchor = getAnchorElement?.();
    if (!open || !anchor) {
      setMenuStyle({ visibility: "hidden" });
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 12;
    const maxPreferredHeight = 420;
    const minVisibleHeight = 160;
    const spaceBelow =
      window.innerHeight - anchorRect.bottom - gap - viewportPadding;
    const spaceAbove = anchorRect.top - gap - viewportPadding;
    const openAbove = spaceBelow < minVisibleHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      maxPreferredHeight,
      Math.max(120, openAbove ? spaceAbove : spaceBelow),
    );
    const minWidth = Math.max(anchorRect.width, 420);
    const maxWidth = Math.min(720, window.innerWidth - viewportPadding * 2);

    setMenuStyle({
      position: "fixed",
      top: openAbove ? anchorRect.top - gap : anchorRect.bottom + gap,
      left: Math.min(
        anchorRect.left,
        window.innerWidth - maxWidth - viewportPadding,
      ),
      transform: openAbove ? "translateY(-100%)" : "none",
      minWidth,
      maxWidth,
      width: "max-content",
      maxHeight,
      zIndex: 10050,
      visibility: "visible",
    });
  }, [getAnchorElement, open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle({ visibility: "hidden" });
      return undefined;
    }

    updateMenuPosition();
    const rafId = window.requestAnimationFrame(updateMenuPosition);

    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open, options, updateMenuPosition]);

  if (!open || !options.length) return null;

  return createPortal(
    <ul
      ref={listRef}
      className="invoice-xero-desc-suggest__list invoice-xero-desc-suggest__list--portal"
      role="listbox"
      data-desc-suggest-menu={rowId}
      style={menuStyle}
    >
      {renderDescriptionSuggestionOptions(options, rowId, onSelect)}
    </ul>,
    document.body,
  );
}

function InvoiceCreateWizard({
  show,
  pageMode = false,
  pageLoading = false,
  onClose,
  onCreate,
  onUpdate,
  onUpdateDiscount,
  existingInvoice = null,
  editMode = false,
  application,
  applicationType = "",
  applicationCost = null,
  applicationGstInfo = null,
  departmentGstInfo = null,
  clientData = null,
  invoiceSetting: invoiceSettingProp = null,
  saving = false,
  updatingDiscount = false,
  loadingInvoice = false,
  paymentForm = null,
  onSaveFinanceInvoicePayment = null,
  onSaveFinanceInvoiceBulkPayment = null,
  savingFinanceInvoicePayment = false,
  onReverseFinanceInvoicePayment = null,
  reversingFinanceInvoicePayment = false,
  ledgerEntries = [],
  applicationInvoices = [],
  existingInvoicesForNumbering = [],
  clientApplications = [],
  clientFinanceInvoices = [],
  enableContactSearch = false,
  contactSearchValue = "",
  contactSearchLoading = false,
  contactSearchResults = [],
  onContactSearchChange = null,
  onContactSearchFocus = null,
  onContactSearchBlur = null,
  onContactSearchSelect = null,
  contactReferringAgentId = "",
  contactProcessingAgentId = "",
  enableAgentSelection = false,
  agentSearchMode = false,
  referringAgentOptions = [],
  processingAgentOptions = [],
  selectedReferringAgentId = "",
  selectedProcessingAgentId = "",
  onReferringAgentChange = null,
  onProcessingAgentChange = null,
  referringAgentSearchValue = "",
  referringAgentSearchLoading = false,
  referringAgentSearchOpen = false,
  referringAgentSearchResults = [],
  onReferringAgentSearchChange = null,
  onReferringAgentSearchFocus = null,
  onReferringAgentSearchBlur = null,
  onReferringAgentSearchSelect = null,
  processingAgentSearchValue = "",
  processingAgentSearchLoading = false,
  processingAgentSearchOpen = false,
  processingAgentSearchResults = [],
  onProcessingAgentSearchChange = null,
  onProcessingAgentSearchFocus = null,
  onProcessingAgentSearchBlur = null,
  onProcessingAgentSearchSelect = null,
  softLoading = false,
  onReversePayment = null,
  reversingPayment = false,
  onAddInvoiceNote = null,
  addingInvoiceNote = false,
  invoiceNotes = [],
  loadingInvoiceNotes = false,
  onHistoryVisible = null,
  invoiceFollowupConfig = null,
  discountFeeRules = [],
  gstSettings = [],
  nextInvoiceSequence: nextInvoiceSequenceProp = null,
  loadingNextInvoiceNumber: loadingNextInvoiceNumberProp = false,
  invoiceDataLoadedFromPage = false,
  /** Optional installment / custom suggestions for description picker.
   *  Pass an array (even empty) to disable application-fee fallback. */
  lineItemSuggestions = undefined,
  /** Dynamic commission/bonus suggestions (remaining-aware per row). */
  installmentSuggestionSource = null,
  /** Force first line blank (commission create invoice). */
  startWithEmptyLines = false,
  /** Prefill currency in page mode (e.g. commission plan currency). */
  defaultInvoiceCurrency = "",
  /** Hide payments / transactions panel (e.g. commission invoice edit). */
  hidePaymentsPanel = false,
  /**
   * Commission create-invoice: pick installment for description/qty,
   * description + fee remain editable; qty stays locked to rate.
   * Student discount is shown but does not reduce the invoice total
   * (commission stays on full tuition).
   */
  suggestionLinesOnly = false,
  /** Plan defaults for commission discount % fallback on invoice lines. */
  commissionPlanMeta = {},
  billToProfile = null,
  /** @deprecated Use billToProfile — overrides display name only when billToProfile is absent. */
  contactDisplayName = "",
  /** Reload plan/installments after Save & another (commission create). */
  onAfterSaveAndAnother = null,
  /** Refresh invoice data after approve-and-email modal sends. */
  onInvoiceEmailSent = null,
  /** Navigate away after a successful create (not Save & another). */
  onSaveComplete = null,
  /** While loading, which commission header buttons to show (draft, awaiting_approval, …). */
  commissionShellStatus = null,
  onVoidInvoice = null,
  voidingInvoice = false,
  onMoveInvoiceToDraft = null,
  movingInvoiceToDraft = false,
  /** Education provider id for commission bill-to popover / address save */
  commissionProviderId = null,
  onProviderBillToProfileChange = null,
  /** Keep parent invoice record in sync after modal auto-saves print toggles */
  onBillToClientSnapshotUpdate = null,
  onInvoiceSettingChange = null,
  invoiceCatalog = null,
  onCatalogLoaded = null,
}) {
  const isEditMode = editMode || Boolean(existingInvoice?.id);
  const isXeroPageMode = pageMode;
  const isXeroPageCreate = pageMode && !isEditMode;
  /** Commission + finance page invoices share draft/approve/payment workflow UX. */
  const useWorkflowInvoiceFlow = suggestionLinesOnly || isXeroPageMode;
  /** Commission + application finance edit: preview customize panel + display options. */
  const showInvoicePreviewCustomize =
    suggestionLinesOnly || (isEditMode && isXeroPageMode);
  const lineIdRef = useRef(1);
  /** Commission create: keep form after save/approve (avoid re-bootstrap on parent re-render). */
  const commissionCreateFormBootstrappedRef = useRef(false);
  /** User edited due date — do not overwrite from installment / line picks. */
  const dueDateUserTouchedRef = useRef(false);
  const lastHydratedInvoiceSeedRef = useRef("");
  const [documentType, setDocumentType] = useState("");
  const [branchId, setBranchId] = useState("");
  const [brandingId, setBrandingId] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reference, setReference] = useState("");
  const [storedInvoiceNumber, setStoredInvoiceNumber] = useState("");
  const [branches, setBranches] = useState([]);
  const [brandings, setBrandings] = useState([]);
  const [invoiceSetting, setInvoiceSetting] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const onCatalogLoadedRef = useRef(onCatalogLoaded);
  onCatalogLoadedRef.current = onCatalogLoaded;
  const [showPreview, setShowPreview] = useState(false);
  const [previewAutoPrint, setPreviewAutoPrint] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState(DEFAULT_INVOICE_STATUS);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showApproveMenu, setShowApproveMenu] = useState(false);
  const [showCommissionActionsMenu, setShowCommissionActionsMenu] =
    useState(false);
  const commissionContactAnchorRef = useRef(null);
  const [commissionBillToPopoverOpen, setCommissionBillToPopoverOpen] =
    useState(false);
  const [invoiceDisplayOptions, setInvoiceDisplayOptions] = useState(() =>
    readInvoiceDisplayOptionsFromSnapshot(existingInvoice?.client_snapshot),
  );
  const commissionBillToPrintOptions = useMemo(
    () => toCommissionPartyPrintOptions(invoiceDisplayOptions),
    [invoiceDisplayOptions],
  );
  const [savingProviderBillingAddress, setSavingProviderBillingAddress] =
    useState(false);
  const [savingBillToPrintOptions, setSavingBillToPrintOptions] =
    useState(false);
  const [draftPayments, setDraftPayments] = useState([]);
  const draftPaymentIdRef = useRef(1);
  const [isDraftEditing, setIsDraftEditing] = useState(false);
  const [isCommissionPaymentFlowEditing, setIsCommissionPaymentFlowEditing] =
    useState(false);
  const saveMenuRef = useRef(null);
  const approveMenuRef = useRef(null);
  const commissionActionsMenuRef = useRef(null);
  const [approveEmailModal, setApproveEmailModal] = useState(null);
  const [openingApproveEmailModal, setOpeningApproveEmailModal] =
    useState(false);
  const pendingApprovePayloadRef = useRef(null);

  useEffect(() => {
    if (!showSaveMenu && !showApproveMenu && !showCommissionActionsMenu) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(event.target)) {
        setShowSaveMenu(false);
      }
      if (
        approveMenuRef.current &&
        !approveMenuRef.current.contains(event.target)
      ) {
        setShowApproveMenu(false);
      }
      if (
        commissionActionsMenuRef.current &&
        !commissionActionsMenuRef.current.contains(event.target)
      ) {
        setShowCommissionActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSaveMenu, showApproveMenu, showCommissionActionsMenu]);

  const [discountTab, setDiscountTab] = useState("predefined");
  const [discountRuleId, setDiscountRuleId] = useState("");
  const [discountCustomAmount, setDiscountCustomAmount] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [lineDiscountErrorId, setLineDiscountErrorId] = useState("");
  const [editingLineDiscountId, setEditingLineDiscountId] = useState("");
  const [lineDiscountSnapshot, setLineDiscountSnapshot] = useState(null);
  const [invoiceCurrency, setInvoiceCurrency] = useState("");
  const [amountsAreType, setAmountsAreType] = useState("inclusive");
  const [loadedGstSettings, setLoadedGstSettings] = useState([]);
  const [descriptionSuggestionRowId, setDescriptionSuggestionRowId] =
    useState("");
  const descSuggestAnchorsRef = useRef(new Map());
  const descSuggestBlurTimeoutRef = useRef(null);
  const [nextInvoiceSequence, setNextInvoiceSequence] = useState("");
  const [loadingNextInvoiceNumber, setLoadingNextInvoiceNumber] =
    useState(false);

  const usesExternalInvoiceNumber =
    invoiceDataLoadedFromPage || nextInvoiceSequenceProp !== null;
  const effectiveNextInvoiceSequence = usesExternalInvoiceNumber
    ? String(nextInvoiceSequenceProp || "")
    : nextInvoiceSequence;
  const isLoadingNextInvoiceNumber = usesExternalInvoiceNumber
    ? loadingNextInvoiceNumberProp
    : loadingNextInvoiceNumber;

  const getDescriptionSuggestionOptionsForRow = useCallback(
    (rowId) => {
      const usedApplicationFees = new Set(
        (lineItems || [])
          .map((row) => {
            if (rowId && row.id === rowId) return "";
            const applicationId = row?.application_id;
            const feeType = String(row?.fee_type || "").toLowerCase();
            if (!applicationId || !isInquiryInvoiceFeeType(feeType)) return "";
            return `${applicationId}:${feeType}`;
          })
          .filter(Boolean),
      );

      if (installmentSuggestionSource) {
        const suggestions = buildInstallmentSuggestions({
          ...installmentSuggestionSource,
          invoiceContext: {
            currentInvoiceId:
              installmentSuggestionSource.currentInvoiceId ??
              existingInvoice?.id ??
              null,
            lineItems,
            editingRowId: rowId,
          },
        });

        const availableOptions = suggestions
          .filter((option) => option?.id && option?.label)
          .filter((option) => {
            const applicationId = option?.fields?.application_id;
            const feeType = String(
              option?.fields?.fee_type || "",
            ).toLowerCase();
            if (
              applicationId &&
              isInquiryInvoiceFeeType(feeType) &&
              usedApplicationFees.has(`${applicationId}:${feeType}`)
            ) {
              return false;
            }
            return true;
          })
          .map((option) => ({
            id: option.id,
            label: option.label,
            kind: "suggestion",
            fields: option.fields || {},
            displayTitle: option.displayTitle,
            displaySubtitle: option.displaySubtitle,
            displayAmount: option.displayAmount,
            displayAmountNote: option.displayAmountNote,
          }));

        return groupDescriptionSuggestionOptionsWithSections(availableOptions);
      }

      if (Array.isArray(lineItemSuggestions)) {
        return buildCommissionDescriptionSuggestionOptions(
          lineItemSuggestions,
          lineItems,
          { editingRowId: rowId },
        );
      }

      return buildClientApplicationFeeSuggestionOptions(clientApplications, {
        financeInvoices: clientFinanceInvoices,
      })
        .filter((option) => {
          const applicationId = option?.fields?.application_id;
          const feeType = String(option?.fields?.fee_type || "").toLowerCase();
          if (!applicationId || !feeType) return true;
          return !usedApplicationFees.has(`${applicationId}:${feeType}`);
        })
        .map((option) => ({
          id: option.id,
          label: option.label,
          kind: "suggestion",
          fields: option.fields || {},
        }));
    },
    [
      installmentSuggestionSource,
      lineItemSuggestions,
      clientApplications,
      clientFinanceInvoices,
      lineItems,
      existingInvoice?.id,
    ],
  );

  const descriptionSuggestionOptions = useMemo(
    () => getDescriptionSuggestionOptionsForRow(null),
    [getDescriptionSuggestionOptionsForRow],
  );

  const hasCommissionDescriptionPicker = useMemo(
    () =>
      descriptionSuggestionOptions.some((option) => option.kind === "suggestion"),
    [descriptionSuggestionOptions],
  );

  const clientId = useMemo(
    () =>
      getClientIdFromRecord(clientData) ||
      application?.clientid ||
      application?.client_id,
    [clientData, application],
  );

  const clientProfile = useMemo(() => {
    const { record, addressList } = normalizeClientRecordSource(clientData);
    const liveProfile = buildClientDisplayProfile(record, addressList);
    const snapshotProfile = mapClientSnapshotToProfile(
      existingInvoice?.client_snapshot,
    );

    const overrideName = String(contactDisplayName || "").trim();
    if (!snapshotProfile?.fullName && !snapshotProfile?.id) {
      if (overrideName && !billToProfile) {
        return { ...liveProfile, fullName: overrideName };
      }
      return liveProfile;
    }

    const merged = mergeInvoiceClientProfiles(snapshotProfile, liveProfile);
    if (overrideName && !billToProfile) {
      return { ...merged, fullName: overrideName };
    }
    return merged;
  }, [existingInvoice, clientData, contactDisplayName, billToProfile]);

  const billToPartyProfile = useMemo(() => {
    if (billToProfile && String(billToProfile.fullName || "").trim()) {
      return billToProfile;
    }
    return clientProfile;
  }, [billToProfile, clientProfile]);

  useEffect(() => {
    if (!showInvoicePreviewCustomize) return;
    setInvoiceDisplayOptions(
      readInvoiceDisplayOptionsFromSnapshot(existingInvoice?.client_snapshot),
    );
  }, [
    showInvoicePreviewCustomize,
    existingInvoice?.id,
    existingInvoice?.client_snapshot,
  ]);

  const handleSaveProviderBillingAddress = useCallback(
    async (address) => {
      if (!commissionProviderId) {
        throw new Error("Provider is not linked to this invoice.");
      }
      setSavingProviderBillingAddress(true);
      try {
        await updateProviderBillingAddress(commissionProviderId, address);
        const trimmed = String(address || "").trim();
        onProviderBillToProfileChange?.({ address: trimmed });
        Swal.fire("Saved", "Provider billing address updated.", "success");
      } catch (error) {
        Swal.fire(
          "Error",
          error?.message || "Failed to update provider address.",
          "error",
        );
        throw error;
      } finally {
        setSavingProviderBillingAddress(false);
      }
    },
    [commissionProviderId, onProviderBillToProfileChange],
  );

  const handleInvoiceDisplayOptionsChange = useCallback(
    async (nextDisplay) => {
      const normalized = normalizeInvoiceDisplayOptions(nextDisplay);
      setInvoiceDisplayOptions(normalized);

      if (!showInvoicePreviewCustomize) return;

      const invoiceId = existingInvoice?.id;
      if (!invoiceId) return;

      setSavingBillToPrintOptions(true);
      try {
        const updated = suggestionLinesOnly
          ? await updateCommissionInvoiceBillToPrintOptions(
              invoiceId,
              null,
              normalized,
            )
          : await updateFinanceInvoiceBillToPrintOptions(
              invoiceId,
              null,
              normalized,
            );
        const mergedSnapshot = mergeInvoiceDisplayOptionsIntoSnapshot(
          updated?.client_snapshot ?? existingInvoice?.client_snapshot,
          normalized,
        );
        onBillToClientSnapshotUpdate?.(mergedSnapshot);
      } catch (error) {
        setInvoiceDisplayOptions(
          readInvoiceDisplayOptionsFromSnapshot(
            existingInvoice?.client_snapshot,
          ),
        );
        Swal.fire(
          "Error",
          error?.message || "Failed to save invoice layout.",
          "error",
        );
      } finally {
        setSavingBillToPrintOptions(false);
      }
    },
    [
      showInvoicePreviewCustomize,
      suggestionLinesOnly,
      existingInvoice?.id,
      existingInvoice?.client_snapshot,
      onBillToClientSnapshotUpdate,
    ],
  );

  const handleCommissionBillToPrintOptionsChange = useCallback(
    (nextOptions) => {
      const normalized = normalizeCommissionBillToPrintOptions(nextOptions);
      handleInvoiceDisplayOptionsChange({
        ...invoiceDisplayOptions,
        to: {
          ...invoiceDisplayOptions.to,
          showEmail: normalized.showEmail,
          showPhone: normalized.showPhone,
          showCountry: normalized.showCountry,
          showCampus: normalized.showCampus,
          showBillingAddress: normalized.showBillingAddress,
        },
      });
    },
    [handleInvoiceDisplayOptionsChange, invoiceDisplayOptions],
  );

  const handleSaveProviderNameFromPreview = useCallback(
    async (name) => {
      if (!commissionProviderId) {
        throw new Error("Provider is not linked to this invoice.");
      }
      await updateCommissionProviderName(commissionProviderId, name);
      const trimmed = String(name || "").trim();
      onProviderBillToProfileChange?.({ fullName: trimmed });
      Swal.fire("Saved", "Provider name updated.", "success");
    },
    [commissionProviderId, onProviderBillToProfileChange],
  );

  const handleSaveAbnFromPreview = useCallback(
    async (abnValue) => {
      const settingRow = mapRowToInvoiceForm(invoiceSetting || {});
      const settingId = settingRow.id;
      if (!settingId) {
        throw new Error("Invoice settings are not available.");
      }
      const nextForm = { ...settingRow, abn: String(abnValue || "").trim() };
      const payload = buildInvoiceSettingPayload(nextForm);
      const response = await apiUrl.post(
        `updateInvoiceSetting/${settingId}`,
        payload,
      );
      if (!isInvoiceSettingApiSuccess(response)) {
        throw new Error(
          getInvoiceSettingApiMessage(response, "Unable to update ABN."),
        );
      }
      setInvoiceSetting(nextForm);
      onInvoiceSettingChange?.(nextForm);
      invalidateInvoiceCatalog();
      Swal.fire("Saved", "ABN updated.", "success");
    },
    [invoiceSetting, onInvoiceSettingChange],
  );

  const currency = useMemo(() => {
    if (isXeroPageMode) {
      return invoiceCurrency || "";
    }
    return (
      existingInvoice?.currency ||
      application?.currency ||
      applicationCost?.currency ||
      "AUD"
    );
  }, [
    isXeroPageMode,
    invoiceCurrency,
    existingInvoice,
    application,
    applicationCost,
  ]);

  const effectiveGstSettings = useMemo(() => {
    if (loadedGstSettings.length) return loadedGstSettings;
    return gstSettings;
  }, [loadedGstSettings, gstSettings]);

  const invoiceTransactions = useMemo(() => {
    if (isXeroPageMode && isEditMode) {
      return resolveFinanceInvoiceTransactions(existingInvoice);
    }

    return filterInvoiceLedgerTransactions(ledgerEntries, existingInvoice);
  }, [isXeroPageMode, isEditMode, existingInvoice, ledgerEntries]);

  const invoiceWorkflowStatus = useMemo(() => {
    if (!isEditMode) return DEFAULT_INVOICE_STATUS;
    return normalizeInvoiceStatus(
      existingInvoice?.status || existingInvoice?.payment_status,
    );
  }, [isEditMode, existingInvoice]);

  const isCommissionApprovedInvoice =
    useWorkflowInvoiceFlow && isEditMode && Boolean(existingInvoice?.approved);

  const uiWorkflowStatus = useMemo(() => {
    if (pageLoading && commissionShellStatus) {
      return normalizeInvoiceStatus(commissionShellStatus);
    }
    if (isCommissionApprovedInvoice) {
      return "approved";
    }
    return invoiceWorkflowStatus;
  }, [
    pageLoading,
    commissionShellStatus,
    invoiceWorkflowStatus,
    isCommissionApprovedInvoice,
  ]);

  /** Payment workflow status (approval is separate when `approved` is set). */
  const commissionPaymentStatus = isCommissionApprovedInvoice
    ? invoiceWorkflowStatus
    : uiWorkflowStatus;

  const isWorkflowEditableInvoice = useMemo(() => {
    if (!isEditMode) return false;
    // Permanently closed statuses stay closed.
    if (["cancelled", "void", "refunded"].includes(invoiceWorkflowStatus)) {
      return false;
    }
    // Commission + finance: paid is editable after clicking Edit.
    if (useWorkflowInvoiceFlow || invoiceWorkflowStatus === "paid") {
      return true;
    }
    return isInvoiceEditableWorkflowStatus(invoiceWorkflowStatus);
  }, [isEditMode, invoiceWorkflowStatus, suggestionLinesOnly]);

  const isDraftInvoice = isEditMode && commissionPaymentStatus === "draft";
  const isAwaitingApprovalInvoice =
    isEditMode && commissionPaymentStatus === "awaiting_approval";
  const isAwaitingPaymentInvoice =
    isEditMode && commissionPaymentStatus === "awaiting_payment";
  const isPartialInvoice = isEditMode && commissionPaymentStatus === "partial";
  const isPaidInvoice = isEditMode && commissionPaymentStatus === "paid";

  /** Commission create page, or reopening a saved draft. */
  const useCommissionCreateFlow =
    useWorkflowInvoiceFlow && (!isEditMode || isDraftInvoice);
  /** Awaiting approval edit — same save/approve UX as create (approve → awaiting payment). */
  const useCommissionAwaitingApprovalFlow =
    useWorkflowInvoiceFlow && isEditMode && isAwaitingApprovalInvoice;
  const useCommissionDraftLikeFlow =
    useCommissionCreateFlow || useCommissionAwaitingApprovalFlow;
  /** Approved invoice: keep approval/details visible; payment section below (not awaiting-payment edit mode). */
  const useCommissionApprovedWithPaymentPanel = isCommissionApprovedInvoice;
  /** Approved commission invoices show separate approval/payment labels. */
  const showCommissionDualApprovalStatus =
    useCommissionApprovedWithPaymentPanel && suggestionLinesOnly;
  const useCommissionAwaitingPaymentFlow =
    useWorkflowInvoiceFlow &&
    isEditMode &&
    isAwaitingPaymentInvoice &&
    !existingInvoice?.approved;
  const useCommissionPartialFlow =
    useWorkflowInvoiceFlow &&
    isEditMode &&
    isPartialInvoice &&
    !existingInvoice?.approved;
  const useCommissionPaidFlow =
    useWorkflowInvoiceFlow &&
    isEditMode &&
    isPaidInvoice &&
    !existingInvoice?.approved;
  /** User clicked Edit — hide share actions until Cancel edit / save. */
  const isInvoiceFieldEditSessionActive =
    isDraftEditing || isCommissionPaymentFlowEditing;
  const useCommissionPrintPdfFlow =
    useWorkflowInvoiceFlow &&
    isEditMode &&
    !isInvoiceFieldEditSessionActive &&
    (isAwaitingPaymentInvoice ||
      isPartialInvoice ||
      isPaidInvoice ||
      isCommissionApprovedInvoice);
  /** Send email on commission + finance/client application invoice edit pages. */
  const showInvoiceSendEmailButton =
    isEditMode &&
    !isInvoiceFieldEditSessionActive &&
    (["draft", "awaiting_payment", "partial", "paid", "overdue"].includes(
      invoiceWorkflowStatus,
    ) ||
      isCommissionApprovedInvoice);
  const useCommissionPaymentViewFlow =
    useCommissionAwaitingPaymentFlow || useCommissionPartialFlow;

  const showCommissionInvoiceSummaryDiscount = !suggestionLinesOnly;
  const supportsPaymentSaveButtons =
    isEditMode &&
    [
      "draft",
      "awaiting_payment",
      "partial",
      "overdue",
      "awaiting_approval",
      "paid",
    ].includes(invoiceWorkflowStatus);

  const useDraftPaymentFlow = !isEditMode || isWorkflowEditableInvoice;

  // Existing invoices (including paid) lock fields until Edit is clicked.
  const needsFieldEditUnlock =
    isEditMode &&
    isWorkflowEditableInvoice &&
    (isDraftInvoice ||
      isAwaitingPaymentInvoice ||
      isAwaitingApprovalInvoice ||
      isPartialInvoice ||
      isPaidInvoice ||
      invoiceWorkflowStatus === "overdue");

  const allowInvoicePayment =
    !isEditMode || Boolean(supportsPaymentSaveButtons);

  const canShowApproveButton = isAwaitingApprovalInvoice;

  // Draft: everyone can use Edit. After draft: only edit_invoice.
  const canClickWorkflowEdit = canEditFinanceInvoiceByStatus(
    invoiceWorkflowStatus,
  );

  const showWorkflowEditMenuItem = needsFieldEditUnlock && !isDraftEditing;

  const isInvoiceLocked = useMemo(() => {
    if (!isEditMode) return false;
    return ["cancelled", "void", "refunded"].includes(invoiceWorkflowStatus);
  }, [isEditMode, invoiceWorkflowStatus]);

  const isVoidInvoice = isEditMode && invoiceWorkflowStatus === "void";

  const invoicePaymentsReadOnly = isInvoiceLocked;

  /** Commission edit after draft/approval: lock header + lines until Edit is clicked. */
  const useCommissionLockedUntilEditFlow =
    useWorkflowInvoiceFlow &&
    isEditMode &&
    !useCommissionDraftLikeFlow &&
    !isInvoiceLocked &&
    (isAwaitingPaymentInvoice ||
      isPartialInvoice ||
      isPaidInvoice ||
      isCommissionApprovedInvoice);

  const invoiceAwaitingPaymentActionInProgress =
    voidingInvoice || movingInvoiceToDraft;

  const showCommissionAwaitingPaymentActionsMenu =
    isAwaitingPaymentInvoice &&
    useCommissionLockedUntilEditFlow &&
    !isCommissionPaymentFlowEditing &&
    (Boolean(onVoidInvoice) || Boolean(onMoveInvoiceToDraft));

  const showCommissionStandardEditButton =
    useCommissionLockedUntilEditFlow &&
    !isCommissionPaymentFlowEditing &&
    !showCommissionAwaitingPaymentActionsMenu;

  const useFinanceFieldEditUnlockFlow =
    !useWorkflowInvoiceFlow &&
    isEditMode &&
    needsFieldEditUnlock &&
    !isInvoiceLocked;

  const isFormReadOnly = useMemo(() => {
    if (useCommissionDraftLikeFlow) return false;
    if (useCommissionLockedUntilEditFlow) {
      return !isCommissionPaymentFlowEditing;
    }
    if (!isEditMode) return false;
    if (isInvoiceLocked) return true;
    if (!needsFieldEditUnlock) return false;
    // Without edit access after draft, fields stay locked.
    if (!canClickWorkflowEdit) return true;
    // Locked until user clicks Edit (paid + awaiting_* + draft/partial).
    return !isDraftEditing;
  }, [
    isEditMode,
    isInvoiceLocked,
    needsFieldEditUnlock,
    canClickWorkflowEdit,
    isDraftEditing,
    useCommissionDraftLikeFlow,
    useCommissionLockedUntilEditFlow,
    isCommissionPaymentFlowEditing,
  ]);

  /** Paid/partial commission invoice with payments: Edit unlocks line description only. */
  const commissionInvoicePaidAmount = isEditMode
    ? resolveInvoicePaidAmount(existingInvoice)
    : 0;
  const isPaidInvoiceDescriptionOnlyEdit =
    (useCommissionPaidFlow || useCommissionPartialFlow) &&
    isCommissionPaymentFlowEditing &&
    commissionInvoicePaidAmount > 0;
  const isHeaderFormReadOnly =
    isFormReadOnly || isPaidInvoiceDescriptionOnlyEdit;
  const isCommissionLineItemsReadOnly =
    isFormReadOnly && !isPaidInvoiceDescriptionOnlyEdit;
  const isCommissionLineDescriptionEditable =
    isPaidInvoiceDescriptionOnlyEdit || !isFormReadOnly;
  const isPaidInvoiceLineFieldLocked =
    isCommissionLineItemsReadOnly || isPaidInvoiceDescriptionOnlyEdit;

  useEffect(() => {
    setDraftPayments([]);
    setIsDraftEditing(false);
    setIsCommissionPaymentFlowEditing(false);
  }, [existingInvoice?.id]);

  const workflowSaveMenuOptions = useMemo(() => {
    if (isDraftInvoice || !isEditMode) {
      return CREATE_SAVE_STATUS_OPTIONS;
    }
    if (isAwaitingPaymentInvoice || isPartialInvoice) {
      return [
        {
          value: "awaiting_approval",
          label: "Save & Awaiting Approval",
        },
      ];
    }
    if (isAwaitingApprovalInvoice) return [];
    return CREATE_SAVE_STATUS_OPTIONS;
  }, [
    isEditMode,
    isDraftInvoice,
    isAwaitingPaymentInvoice,
    isPartialInvoice,
    isAwaitingApprovalInvoice,
  ]);

  const showWorkflowSaveChevron =
    showWorkflowEditMenuItem || workflowSaveMenuOptions.length > 0;

  const lockedStatusLabel = useMemo(
    () => getInvoiceStatusLabel(invoiceWorkflowStatus),
    [invoiceWorkflowStatus],
  );

  const activeDiscountTransaction = useMemo(
    () => getActiveInvoiceDiscountTransaction(ledgerEntries, existingInvoice),
    [ledgerEntries, existingInvoice],
  );

  const statusOptions = INVOICE_STATUSES;

  const savedDiscountAmount = useMemo(() => {
    if (!isEditMode) return 0;

    if (activeDiscountTransaction) {
      return Math.abs(Number(activeDiscountTransaction.amount) || 0);
    }

    return getInvoiceDiscountAmount(existingInvoice);
  }, [isEditMode, activeDiscountTransaction, existingInvoice]);

  const savedDiscountReason = useMemo(() => {
    if (!isEditMode) return "";

    return (
      activeDiscountTransaction?.description ||
      getInvoiceDiscountReason(existingInvoice) ||
      ""
    );
  }, [isEditMode, activeDiscountTransaction, existingInvoice]);

  const hasExistingDiscount =
    isEditMode &&
    (savedDiscountAmount > 0 || Boolean(savedDiscountReason.trim()));

  const invoiceActivityHistory = useMemo(
    () => mapInvoiceNotesToHistoryEntries(invoiceNotes, { excludeFollowupAudit: true }),
    [invoiceNotes],
  );

  const applicationGstPercentage = useMemo(
    () =>
      resolveApplicationGstPercentage(
        applicationCost,
        applicationGstInfo,
        application,
      ),
    [applicationCost, applicationGstInfo, application],
  );

  const resolveWizardGstPercentage = useCallback(
    (currencyCode = currency) => {
      if (suggestionLinesOnly) {
        const planRate = Number(commissionPlanMeta?.defaultGstPercentage);
        return planRate > 0 ? planRate : DEFAULT_COMMISSION_GST_PERCENT;
      }
      return (
        resolveGstPercentageForCurrency(currencyCode, effectiveGstSettings) ||
        applicationGstPercentage ||
        0
      );
    },
    [
      currency,
      effectiveGstSettings,
      applicationGstPercentage,
      suggestionLinesOnly,
      commissionPlanMeta?.defaultGstPercentage,
    ],
  );

  const syncCommissionLineWithMetrics = useCallback(
    (row, options = {}) =>
      syncCommissionInvoiceLineMetrics(row, commissionPlanMeta, options),
    [commissionPlanMeta],
  );

  const lineTotals = useMemo(() => {
    if (!suggestionLinesOnly) {
      return calculateInvoiceLineTotals(lineItems);
    }
    const displayLineItems = applyDocumentTaxToInstallmentLines(
      lineItems,
      amountsAreType,
      resolveWizardGstPercentage(invoiceCurrency || currency),
    );
    return computeCommissionSuggestionInvoiceTotals(
      displayLineItems,
      commissionPlanMeta,
    );
  }, [
    lineItems,
    suggestionLinesOnly,
    commissionPlanMeta,
    amountsAreType,
    invoiceCurrency,
    currency,
    resolveWizardGstPercentage,
  ]);

  const documentTaxType = useMemo(() => {
    if (isXeroPageMode) {
      return amountsAreType || "inclusive";
    }
    return resolveInvoiceDocumentTaxType(lineTotals.lineItems);
  }, [isXeroPageMode, amountsAreType, lineTotals.lineItems]);

  const xeroExTaxDisplayTotals = useMemo(() => {
    if (!isXeroPageMode) return null;
    return resolveExTaxFooterTotals(lineTotals.lineItems, documentTaxType);
  }, [isXeroPageMode, lineTotals.lineItems, documentTaxType]);

  const paymentLineItems = useMemo(() => {
    const source =
      isEditMode &&
      Array.isArray(existingInvoice?.line_items) &&
      existingInvoice.line_items.length
        ? existingInvoice.line_items
        : lineTotals.lineItems;
    return prepareInvoicePaymentLineItems(source);
  }, [isEditMode, existingInvoice?.line_items, lineTotals.lineItems]);

  const usesSegregatedLineDiscounts = useMemo(() => {
    if (isXeroPageMode) return false;
    return invoiceUsesSegregatedLineDiscounts(lineItems, { isEditMode });
  }, [lineItems, isEditMode, isXeroPageMode]);

  const showInvoiceLevelDiscountEditor =
    !isXeroPageMode &&
    !usesSegregatedLineDiscounts &&
    (!isEditMode || isEditingDiscount || !hasExistingDiscount);
  const showDiscountEditTrigger =
    !usesSegregatedLineDiscounts &&
    isEditMode &&
    hasExistingDiscount &&
    !isEditingDiscount;

  const previewDiscountAmount = useMemo(
    () =>
      calculateInvoiceDiscountAmount({
        tab: discountTab,
        ruleId: discountRuleId,
        rules: discountFeeRules,
        customAmount: discountCustomAmount,
        grossSubtotal: lineTotals.gross_subtotal,
        maxTotal: lineTotals.total_amount,
      }),
    [
      discountTab,
      discountRuleId,
      discountFeeRules,
      discountCustomAmount,
      lineTotals.gross_subtotal,
      lineTotals.total_amount,
    ],
  );

  const displayedLineDiscountAmount = showInvoiceLevelDiscountEditor
    ? previewDiscountAmount
    : savedDiscountAmount;

  const pendingInvoiceDiscount = useMemo(
    () =>
      buildInvoiceDiscountPayload({
        tab: discountTab,
        ruleId: discountRuleId,
        rules: discountFeeRules,
        customAmount: discountCustomAmount,
        reason: discountReason,
        grossSubtotal: lineTotals.gross_subtotal,
        maxTotal: lineTotals.total_amount,
      }),
    [
      discountTab,
      discountRuleId,
      discountFeeRules,
      discountCustomAmount,
      discountReason,
      lineTotals.gross_subtotal,
      lineTotals.total_amount,
    ],
  );

  const discountExceedsError = useMemo(() => {
    const hasDiscountInput =
      Boolean(discountRuleId) ||
      isValidCustomDiscountAmount(discountCustomAmount) ||
      Boolean(discountReason?.trim());

    if (!hasDiscountInput) return "";

    if (
      !validateInvoiceDiscountExceedsTotal({
        tab: discountTab,
        ruleId: discountRuleId,
        customAmount: discountCustomAmount,
        grossSubtotal: lineTotals.gross_subtotal,
        maxTotal: lineTotals.total_amount,
        rules: discountFeeRules,
      })
    ) {
      return "";
    }

    return getInvoiceDiscountExceedsTotalMessage(
      lineTotals.total_amount,
      currency,
    );
  }, [
    discountTab,
    discountRuleId,
    discountCustomAmount,
    discountReason,
    lineTotals.gross_subtotal,
    lineTotals.total_amount,
    discountFeeRules,
    currency,
  ]);

  const effectiveTotals = useMemo(() => {
    if (usesSegregatedLineDiscounts) {
      return {
        ...lineTotals,
        invoice_discount_amount: 0,
      };
    }

    let discountForTotals = null;

    if (isEditMode && hasExistingDiscount && !isEditingDiscount) {
      const lineDiscount = Number(lineTotals.discount_total) || 0;
      if (lineDiscount > 0) {
        return {
          ...lineTotals,
          invoice_discount_amount: savedDiscountAmount,
        };
      }

      discountForTotals = { amount: savedDiscountAmount };
    } else {
      discountForTotals =
        pendingInvoiceDiscount ||
        (isValidCustomDiscountAmount(discountCustomAmount) ||
        previewDiscountAmount > 0
          ? { amount: previewDiscountAmount }
          : null);
    }

    const merged = mergeInvoiceDiscountIntoTotals(
      lineTotals,
      discountForTotals,
    );

    // Commission invoices: show invoice-level discount without reducing total.
    if (suggestionLinesOnly) {
      const payableTotal =
        isXeroPageMode && xeroExTaxDisplayTotals
          ? xeroExTaxDisplayTotals.total
          : lineTotals.total_amount;
      return {
        ...merged,
        total_amount: payableTotal,
        amount_due: payableTotal,
        subtotal: lineTotals.subtotal,
        tax_amount: lineTotals.tax_amount,
        inclusive_tax_amount: lineTotals.inclusive_tax_amount,
        exclusive_tax_amount: lineTotals.exclusive_tax_amount,
        discount_total:
          Number(merged.invoice_discount_amount) ||
          Number(lineTotals.discount_total) ||
          0,
      };
    }

    if (isXeroPageMode && xeroExTaxDisplayTotals) {
      return {
        ...merged,
        total_amount: xeroExTaxDisplayTotals.total,
        amount_due: xeroExTaxDisplayTotals.total,
        subtotal: xeroExTaxDisplayTotals.subtotalExTax,
        tax_amount: xeroExTaxDisplayTotals.gst,
      };
    }

    return merged;
  }, [
    lineTotals,
    usesSegregatedLineDiscounts,
    pendingInvoiceDiscount,
    previewDiscountAmount,
    discountCustomAmount,
    isEditMode,
    hasExistingDiscount,
    isEditingDiscount,
    savedDiscountAmount,
    suggestionLinesOnly,
    isXeroPageMode,
    xeroExTaxDisplayTotals,
  ]);

  const createPaidAmount = useMemo(() => {
    if (!useDraftPaymentFlow) return 0;
    const existingPaid = isEditMode
      ? resolveInvoicePaidAmount(existingInvoice)
      : 0;
    return existingPaid + sumCreateInvoicePayments(draftPayments);
  }, [useDraftPaymentFlow, isEditMode, existingInvoice, draftPayments]);

  const createAmountDue = useMemo(() => {
    if (!useDraftPaymentFlow) return 0;
    const total = resolvePayableInvoiceTotal({
      effectiveTotals,
      xeroExTaxDisplayTotals,
      lineTotals,
      storedTotal: isEditMode
        ? existingInvoice?.total_amount ?? existingInvoice?.totalAmount
        : null,
    });
    return Math.max(total - createPaidAmount, 0);
  }, [
    useDraftPaymentFlow,
    effectiveTotals,
    xeroExTaxDisplayTotals,
    lineTotals,
    createPaidAmount,
    isEditMode,
    existingInvoice,
  ]);

  /** Room left for a new draft payment. Commission never ignores existing paid. */
  const paymentHeadroom = useMemo(() => {
    if (!useDraftPaymentFlow) return 0;
    if (useWorkflowInvoiceFlow) return createAmountDue;
    return createAmountDue;
  }, [useDraftPaymentFlow, useWorkflowInvoiceFlow, createAmountDue]);

  /**
   * Commission payment modal cap:
   * - remaining due when unpaid/partial
   * - full line total when already paid (add payment will auto-reverse first)
   */
  const commissionPaymentCap = useMemo(() => {
    if (!useWorkflowInvoiceFlow) return 0;
    const total = resolvePayableInvoiceTotal({
      effectiveTotals,
      xeroExTaxDisplayTotals,
      lineTotals,
      storedTotal: isEditMode
        ? existingInvoice?.total_amount ?? existingInvoice?.totalAmount
        : null,
    });
    if (createAmountDue > 0.0001) return createAmountDue;
    const paid = isEditMode ? resolveInvoicePaidAmount(existingInvoice) : 0;
    if (paid > 0 && total > 0) return total;
    return 0;
  }, [
    useWorkflowInvoiceFlow,
    effectiveTotals,
    xeroExTaxDisplayTotals,
    lineTotals,
    isEditMode,
    existingInvoice,
    createAmountDue,
  ]);

  const createFinancialSummary = useMemo(() => {
    if (!useDraftPaymentFlow) return null;

    const lineItemsForPayable = effectiveTotals?.lineItems || [];
    const payable = resolveInvoiceRecordPayableTotals({
      line_items: lineItemsForPayable,
      total_amount: resolvePayableInvoiceTotal({
        effectiveTotals,
        xeroExTaxDisplayTotals,
        lineTotals,
        storedTotal: isEditMode
          ? existingInvoice?.total_amount ?? existingInvoice?.totalAmount
          : null,
      }),
      paid_amount: createPaidAmount,
    });
    const discount = suggestionLinesOnly
      ? Math.max(
          Number(lineTotals?.discount_total) || 0,
          Number(effectiveTotals?.invoice_discount_amount) || 0,
          Number(effectiveTotals?.discount_total) || 0,
        )
      : usesSegregatedLineDiscounts
        ? Number(lineTotals?.discount_total) || 0
        : Number(effectiveTotals?.invoice_discount_amount) ||
          Number(effectiveTotals?.discount_total) ||
          0;

    return {
      total: payable.total_amount,
      discount,
      paid: createPaidAmount,
      remaining: resolveInvoiceDocumentAmountDue({
        displayTotal: payable.total_amount,
        paidTotal: createPaidAmount,
      }),
    };
  }, [
    useDraftPaymentFlow,
    effectiveTotals,
    xeroExTaxDisplayTotals,
    lineTotals,
    usesSegregatedLineDiscounts,
    suggestionLinesOnly,
    createPaidAmount,
    isEditMode,
    existingInvoice,
  ]);

  const invoiceFinancialSummary = useMemo(() => {
    if (!isEditMode || !existingInvoice) return null;

    const lineItemsForPayable =
      effectiveTotals?.lineItems?.length > 0
        ? effectiveTotals.lineItems
        : resolveInvoiceLineItemsFromRecord(existingInvoice);
    const paid = resolveInvoicePaidAmount(existingInvoice);
    const payable = resolveInvoiceRecordPayableTotals({
      ...existingInvoice,
      line_items: lineItemsForPayable,
      paid_amount: paid,
    });
    const discount = suggestionLinesOnly
      ? Math.max(
          Number(lineTotals?.discount_total) || 0,
          Number(effectiveTotals?.discount_total) || 0,
          Number(effectiveTotals?.commission_discount_total) || 0,
        )
      : Number(
          existingInvoice?.discount_total ??
            existingInvoice?.discountTotal ??
            savedDiscountAmount,
        ) || 0;

    return {
      total: payable.total_amount,
      discount,
      paid: payable.paid_amount,
      remaining: payable.amount_due,
    };
  }, [
    isEditMode,
    existingInvoice,
    savedDiscountAmount,
    effectiveTotals,
    suggestionLinesOnly,
    lineTotals,
  ]);

  const invoiceAmountDue = useMemo(() => {
    if (!isEditMode || !existingInvoice) return 0;

    const total = resolvePayableInvoiceTotal({
      effectiveTotals,
      xeroExTaxDisplayTotals,
      lineTotals,
      storedTotal:
        existingInvoice?.total_amount ?? existingInvoice?.totalAmount,
    });

    return resolveInvoiceDueAmount(existingInvoice, total);
  }, [
    isEditMode,
    existingInvoice,
    effectiveTotals,
    xeroExTaxDisplayTotals,
    lineTotals,
  ]);

  const commissionDraftLikeSaveMenuOptions = useCommissionAwaitingApprovalFlow
    ? COMMISSION_AWAITING_APPROVAL_SAVE_MENU_OPTIONS
    : COMMISSION_CREATE_SAVE_MENU_OPTIONS;
  const commissionDraftLikeCloseStatus = useCommissionAwaitingApprovalFlow
    ? "awaiting_approval"
    : "draft";

  const draftPaymentTransactions = useMemo(
    () =>
      draftPayments.map((payment) => ({
        id: payment.id,
        type: "payment",
        amount: payment.amount,
        transaction_date: payment.transaction_date,
        description: payment.description || "Payment received",
        payment_method: payment.payment_method,
        reference: payment.reference,
        line_number: payment.line_number,
        attachment_url: payment.receiptPreviews?.[0] || "",
        attachment_urls: (payment.receiptPreviews || []).filter(Boolean),
        receipt_url: payment.receiptPreviews?.[0] || "",
        user: { name: "You" },
        created_by_name: "You",
      })),
    [draftPayments],
  );

  useEffect(() => {
    if (!useDraftPaymentFlow || sumCreateInvoicePayments(draftPayments) <= 0) {
      return;
    }

    const nextStatus = resolveCreateInvoiceStatusFromPayment({
      requestedStatus: "awaiting_payment",
      totalAmount: effectiveTotals?.total_amount,
      paidAmount: createPaidAmount,
    });

    setSaveStatus((prev) => (prev === nextStatus ? prev : nextStatus));
  }, [
    useDraftPaymentFlow,
    draftPayments,
    createPaidAmount,
    effectiveTotals?.total_amount,
  ]);

  /** User's normal chosen status (draft / awaiting_payment / …), not auto-paid. */
  const userStatusForDerivation = useMemo(() => {
    const normalized = normalizeInvoiceStatus(saveStatus);
    if (normalized === "awaiting_approval") return normalized;
    // Sticky paid/partial only when cash is present; otherwise use draft as base
    // so clearing a full discount returns to a normal status at runtime.
    if (normalized === "paid" || normalized === "partial") {
      const hasCash =
        sumCreateInvoicePayments(draftPayments) > 0 ||
        (isEditMode && resolveInvoicePaidAmount(existingInvoice) > 0);
      return hasCash ? normalized : DEFAULT_INVOICE_STATUS;
    }
    return normalized;
  }, [saveStatus, draftPayments, isEditMode, existingInvoice]);

  const financeDiscountForStatus = useMemo(() => {
    if (suggestionLinesOnly) return 0;
    return (
      Number(effectiveTotals?.invoice_discount_amount) ||
      Number(effectiveTotals?.discount_total) ||
      Number(lineTotals?.discount_total) ||
      0
    );
  }, [suggestionLinesOnly, effectiveTotals, lineTotals]);

  const financeGrossForStatus = useMemo(() => {
    if (suggestionLinesOnly) return 0;
    return (
      Number(effectiveTotals?.gross_subtotal) ||
      Number(lineTotals?.gross_subtotal) ||
      0
    );
  }, [suggestionLinesOnly, effectiveTotals, lineTotals]);

  /**
   * Runtime status: full discount → paid; otherwise user's normal status.
   * Updates as line items / discount change (does not stick when discount cleared).
   */
  const derivedInvoiceStatus = useMemo(() => {
    if (suggestionLinesOnly) {
      return resolveCreateInvoiceStatusFromPayment({
        requestedStatus: userStatusForDerivation,
        totalAmount: effectiveTotals?.total_amount,
        paidAmount: createPaidAmount,
      });
    }

    return resolveCreateInvoiceStatusFromPayment({
      requestedStatus: userStatusForDerivation,
      totalAmount: effectiveTotals?.total_amount,
      paidAmount: createPaidAmount,
      discountAmount: financeDiscountForStatus,
      grossSubtotal: financeGrossForStatus,
    });
  }, [
    suggestionLinesOnly,
    userStatusForDerivation,
    effectiveTotals?.total_amount,
    createPaidAmount,
    financeDiscountForStatus,
    financeGrossForStatus,
  ]);

  const applyTaxSettingsToLineItems = useCallback(
    (taxType, currencyCode) => {
      const gstPercentage = resolveWizardGstPercentage(currencyCode);
      const nextRate = resolveLineItemTaxRate(taxType, gstPercentage);
      setLineItems((prev) =>
        prev.map((row) => {
          return {
            ...row,
            tax_type: taxType,
            tax_rate: nextRate,
            tax_type_locked:
              isXeroPageMode && !suggestionLinesOnly
                ? false
                : row.tax_type_locked,
            unit_price_locked: suggestionLinesOnly
              ? row.unit_price_locked
              : isXeroPageMode
                ? false
                : row.unit_price_locked,
            quantity_locked: suggestionLinesOnly
              ? row.quantity_locked
              : row.quantity_locked,
          };
        }),
      );
    },
    [isXeroPageMode, resolveWizardGstPercentage, suggestionLinesOnly],
  );

  const handleInvoiceCurrencyChange = (event) => {
    const nextCurrency = event.target.value;
    setInvoiceCurrency(nextCurrency);
    setErrors((prev) => ({ ...prev, currency: "" }));
    const gstPercentage = resolveWizardGstPercentage(nextCurrency);
    setLineItems((prev) =>
      prev.map((row) => {
        const lineTaxType =
          normalizeTaxType(row.tax_type) || amountsAreType || "notax";

        if (lineTaxType === "notax") {
          return { ...row, tax_rate: "" };
        }

        return {
          ...row,
          tax_rate: resolveLineItemTaxRate(lineTaxType, gstPercentage),
          tax_type_locked: suggestionLinesOnly ? row.tax_type_locked : false,
          unit_price_locked: suggestionLinesOnly
            ? row.unit_price_locked
            : false,
          quantity_locked: suggestionLinesOnly
            ? row.quantity_locked
            : row.quantity_locked,
        };
      }),
    );
  };

  const preservePerLineTaxInEdit =
    isEditMode && (suggestionLinesOnly || isXeroPageMode);

  const handleAmountsAreChange = (value) => {
    const nextType = value || "inclusive";
    setAmountsAreType(nextType);
    if (preservePerLineTaxInEdit) {
      const gstPercentage = resolveWizardGstPercentage(currency);
      const nextRate =
        nextType === "notax"
          ? ""
          : resolveLineItemTaxRate(nextType, gstPercentage);
      setLineItems((prev) =>
        prev.map((row) => {
          if (normalizeTaxType(row.tax_type)) return row;
          return {
            ...row,
            tax_type: nextType,
            tax_rate: nextRate,
          };
        }),
      );
      return;
    }
    applyTaxSettingsToLineItems(nextType, currency);
  };

  const applyClientApplicationToLine = useCallback(
    (rowId, application) => {
      if (!application) return;

      const lineFieldsList = buildLineItemsFromClientApplication(application);
      if (!lineFieldsList.length) return;

      const primaryFields = lineFieldsList[0];
      const primaryCurrency =
        primaryFields.currency || invoiceCurrency || currency;

      if (isXeroPageMode) {
        setAmountsAreType(primaryFields.tax_type || "notax");
        if (primaryCurrency) {
          setInvoiceCurrency(primaryCurrency);
        }
      }

      setLineItems((prev) => {
        const rowIndex = prev.findIndex((row) => row.id === rowId);
        if (rowIndex === -1) return prev;

        let nextLineCounter = lineIdRef.current;
        const builtRows = lineFieldsList.map((fields, index) => {
          const nextId = index === 0 ? rowId : `line-${(nextLineCounter += 1)}`;

          return {
            ...createEmptyLineItem(nextId, fields.tax_rate, fields.tax_type),
            description: fields.description,
            unit_price: fields.unit_price,
            quantity: "1",
            application_id: fields.application_id,
            application_type: fields.application_type,
            fee_type: fields.fee_type,
          };
        });

        lineIdRef.current = Math.max(lineIdRef.current, nextLineCounter);

        const next = [...prev];
        next.splice(rowIndex, 1, ...builtRows);
        return next;
      });

      setDescriptionSuggestionRowId("");
      setErrors((prev) => ({ ...prev, lineItems: "" }));
    },
    [isXeroPageMode, invoiceCurrency, currency],
  );

  const applyLineItemSuggestionToLine = useCallback(
    (rowId, fields = {}) => {
      if (!fields || typeof fields !== "object") return;

      const isInquiryFeeLine = isInquiryInvoiceFeeType(fields.fee_type);
      const isBonusLine =
        Boolean(fields.bonus_installment_id) || fields.fee_type === "bonus";
      const isCommissionOrBonusLine =
        Boolean(fields.commission_installment_id) ||
        isBonusLine ||
        fields.fee_type === "tuition" ||
        fields.fee_type === "bonus";

      const gstPercentage = resolveWizardGstPercentage(
        fields.currency || invoiceCurrency || currency,
      );
      const installmentTaxType =
        normalizeTaxType(fields.tax_type) ||
        normalizeTaxType(fields.gst_type) ||
        normalizeTaxType(fields.gst_status) ||
        "";
      const taxType = isInquiryFeeLine
        ? "notax"
        : suggestionLinesOnly && isCommissionOrBonusLine
          ? installmentTaxType ||
            normalizeTaxType(amountsAreType) ||
            "notax"
          : isXeroPageMode
            ? installmentTaxType ||
              normalizeTaxType(amountsAreType) ||
              "notax"
            : isBonusLine
              ? installmentTaxType ||
                normalizeTaxType(amountsAreType) ||
                "notax"
              : normalizeTaxType(fields.tax_type) || "notax";
      const taxRate =
        taxType === "notax"
          ? ""
          : (() => {
              const raw = fields.tax_rate ?? fields.gst_percentage ?? null;
              if (raw != null && String(raw).trim() !== "") {
                return String(raw);
              }
              return resolveLineItemTaxRate(taxType, gstPercentage);
            })();

      if (isXeroPageMode && fields.currency) {
        setInvoiceCurrency(fields.currency);
      }
      if (
        isXeroPageMode &&
        !suggestionLinesOnly &&
        fields.tax_type &&
        !isCommissionOrBonusLine
      ) {
        setAmountsAreType(normalizeTaxType(fields.tax_type) || "notax");
      }
      // Commission invoice: due date stays issue date + 1 month (or user-edited value).
      // Do not replace with installment due date when picking a line.
      if (!suggestionLinesOnly && fields.invoice_due_date) {
        const nextDue = String(fields.invoice_due_date).slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(nextDue)) {
          setDueDate(nextDue);
        }
      }

      const nextQuantity =
        fields.quantity != null && String(fields.quantity).trim() !== ""
          ? String(fields.quantity)
          : "1";
      const nextUnitPrice =
        fields.unit_price != null && String(fields.unit_price).trim() !== ""
          ? String(fields.unit_price)
          : "";

      const normalizedTaxType = normalizeTaxType(taxType) || "notax";
      const resolvedTaxRate =
        normalizedTaxType === "notax"
          ? ""
          : taxRate || resolveLineItemTaxRate(normalizedTaxType, gstPercentage);

      setLineItems((prev) =>
        prev.map((row) => {
          let nextRow = row;

          if (row.id === rowId) {
            nextRow = {
              ...createEmptyLineItem(row.id, resolvedTaxRate, normalizedTaxType),
              description: fields.description || "",
              unit_price: nextUnitPrice,
              quantity: nextQuantity,
              unit_price_locked: Boolean(fields.unit_price_locked),
              quantity_locked: Boolean(fields.quantity_locked),
              application_id: fields.application_id || null,
              application_type: fields.application_type || "",
              fee_type: fields.fee_type || "",
              commission_installment_id:
                fields.commission_installment_id || null,
              bonus_installment_id: fields.bonus_installment_id || null,
              tax_type: normalizedTaxType,
              tax_rate: resolvedTaxRate,
              discount: "",
              discount_custom_amount: "",
              discount_tab: "percentage",
              discount_rule_id: "",
              discount_percent: "",
            };
            if (suggestionLinesOnly && isCommissionTuitionInvoiceLine(nextRow)) {
              nextRow = syncCommissionLineWithMetrics(nextRow);
            }
          }

          return nextRow;
        }),
      );

      setDescriptionSuggestionRowId("");
      setErrors((prev) => ({ ...prev, lineItems: "" }));
    },
    [
      isXeroPageMode,
      invoiceCurrency,
      currency,
      resolveWizardGstPercentage,
      amountsAreType,
      suggestionLinesOnly,
      syncCommissionLineWithMetrics,
    ],
  );

  const computedLineById = useMemo(() => {
    const map = new Map();
    lineTotals.lineItems.forEach((row) => map.set(row.id, row));
    return map;
  }, [lineTotals.lineItems]);

  const invoiceLineItemSeed = useMemo(() => {
    if (!isEditMode || !existingInvoice) return "";
    const items = resolveInvoiceLineItemsFromRecord(existingInvoice);
    const fingerprint = items
      .map(
        (row) =>
          `${row.quantity ?? row.qty ?? ""}:${row.unit_price ?? row.unitPrice ?? ""}:${row.commission_installment_id ?? row.commissionInstallmentId ?? ""}:${row.bonus_installment_id ?? row.bonusInstallmentId ?? ""}`,
      )
      .join("|");
    return [
      existingInvoice.id,
      items.length,
      fingerprint,
      existingInvoice?.issue_date || existingInvoice?.issueDate || "",
      existingInvoice?.due_date || existingInvoice?.dueDate || "",
      existingInvoice?.branch_id || existingInvoice?.branchId || "",
      existingInvoice?.branding_id || existingInvoice?.brandingId || "",
      existingInvoice?.reference || "",
    ].join("|");
  }, [isEditMode, existingInvoice]);

  const invoiceDetailsSeed = useMemo(() => {
    if (!isEditMode || !existingInvoice?.id) return "";
    return [
      getInvoiceDiscountAmount(existingInvoice),
      getInvoiceDiscountReason(existingInvoice),
      existingInvoice?.discount_rule_id ??
        existingInvoice?.discountRuleId ??
        "",
      normalizeInvoiceStatus(existingInvoice?.status || ""),
      existingInvoice?.approved ? "1" : "0",
      existingInvoice?.total_amount ?? existingInvoice?.totalAmount ?? "",
      existingInvoice?.paid_amount ?? existingInvoice?.paidAmount ?? "",
      existingInvoice?.amount_due ?? existingInvoice?.amountDue ?? "",
    ].join("|");
  }, [isEditMode, existingInvoice]);

  const applyCatalog = useCallback(
    (catalog) => {
      const branchesList = Array.isArray(catalog?.branches)
        ? catalog.branches
        : [];
      const brandingList = Array.isArray(catalog?.brandings)
        ? catalog.brandings
        : [];
      setBranches(branchesList);
      setBrandings(
        brandingList.map((row) => ({
          ...mapRowToInvoiceBrandingForm(row),
          id: row.id,
        })),
      );
      if (!invoiceSettingProp && catalog?.setting) {
        setInvoiceSetting(
          normalizeInvoiceSettingRecord({
            data: { data: catalog.setting, status: true },
            status: true,
          }),
        );
      }
      if (Array.isArray(catalog?.gst_settings)) {
        setLoadedGstSettings(catalog.gst_settings);
      }
    },
    [invoiceSettingProp],
  );

  const loadCatalog = useCallback(
    async ({ force = false } = {}) => {
      setLoadingOptions(true);
      setCatalogError("");
      try {
        const data = await fetchInvoiceCatalog({ force });
        applyCatalog(data);
        onCatalogLoadedRef.current?.(data);
      } catch (error) {
        console.error("Failed to load invoice catalog", error);
        setCatalogError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load invoice options.",
        );
      } finally {
        setLoadingOptions(false);
      }
    },
    [applyCatalog],
  );

  const beginInvoiceFieldEdit = useCallback(
    (source = "draft") => {
      if (source === "commission") {
        setIsCommissionPaymentFlowEditing(true);
      } else {
        setIsDraftEditing(true);
      }
      loadCatalog();
    },
    [loadCatalog],
  );

  useEffect(() => {
    if (!show) return;
    if (invoiceCatalog) {
      applyCatalog(invoiceCatalog);
      setLoadingOptions(false);
      setCatalogError("");
      return;
    }
    if (pageLoading || (isEditMode && loadingInvoice)) return;
    // Existing invoice view: wait for Edit. Create and unlocked draft-like need options immediately.
    const editingExistingInvoice =
      isDraftEditing || isCommissionPaymentFlowEditing;
    if (isEditMode && !editingExistingInvoice && !useCommissionDraftLikeFlow) {
      return;
    }
    loadCatalog();
  }, [
    show,
    invoiceCatalog,
    pageLoading,
    loadingInvoice,
    isEditMode,
    isDraftEditing,
    isCommissionPaymentFlowEditing,
    useCommissionDraftLikeFlow,
    applyCatalog,
    loadCatalog,
  ]);

  useEffect(() => {
    if (!show) {
      commissionCreateFormBootstrappedRef.current = false;
      dueDateUserTouchedRef.current = false;
      lastHydratedInvoiceSeedRef.current = "";
      return;
    }
    if (isEditMode && loadingInvoice) return;
    if (isEditMode && invoiceDataLoadedFromPage && pageLoading) return;
    if (!isEditMode && invoiceDataLoadedFromPage && pageLoading) return;

    if (commissionCreateFormBootstrappedRef.current && !isEditMode) {
      if (invoiceSettingProp) {
        setInvoiceSetting(mapRowToInvoiceForm(invoiceSettingProp));
      }
      return;
    }

    if (
      isEditMode &&
      invoiceLineItemSeed &&
      lastHydratedInvoiceSeedRef.current === invoiceLineItemSeed
    ) {
      return;
    }

    const today = new Date();
    const defaultTaxType = getDefaultTaxTypeFromApplication(
      applicationCost,
      applicationGstInfo,
    );

    setShowPreview(false);
    setErrors({});
    setEditingLineDiscountId("");
    setLineDiscountSnapshot(null);
    setLineDiscountErrorId("");

    if (isEditMode) {
      if (!existingInvoice) return;

      const approveFromCreateTransition =
        isXeroPageMode &&
        !suggestionLinesOnly &&
        commissionCreateFormBootstrappedRef.current &&
        Boolean(existingInvoice?.approved) &&
        !lastHydratedInvoiceSeedRef.current;

      if (approveFromCreateTransition) {
        setSaveStatus(
          normalizeInvoiceStatus(
            existingInvoice?.status || DEFAULT_INVOICE_STATUS,
          ),
        );
        setStoredInvoiceNumber(
          existingInvoice?.invoice_number ||
            existingInvoice?.invoice_no ||
            existingInvoice?.invoiceNo ||
            "",
        );
        if (invoiceLineItemSeed) {
          lastHydratedInvoiceSeedRef.current = invoiceLineItemSeed;
        }
        if (invoiceSettingProp) {
          setInvoiceSetting(mapRowToInvoiceForm(invoiceSettingProp));
        }
        return;
      }

      lineIdRef.current = 0;
      const mappedLines = mapSavedLineItemsToForm(
        null,
        lineIdRef,
        existingInvoice,
      );
      const hydratedLines = hydrateEditLineItemDiscounts(
        mappedLines,
        existingInvoice,
        discountFeeRules,
      ).map((line) => {
        if (!isXeroPageMode) return line;

        if (suggestionLinesOnly) {
          const linked = Boolean(
            line.commission_installment_id || line.bonus_installment_id,
          );
          const discountPercent =
            line.discount_percent != null &&
            String(line.discount_percent).trim() !== ""
              ? String(line.discount_percent)
              : resolveLineDiscountPercent(line) > 0
                ? String(resolveLineDiscountPercent(line))
                : "";
          const hydratedLine = {
            ...line,
            // Fee stays editable; qty stays locked to commission rate / bonus 1.
            // Preserve saved tax on commission and bonus lines.
            unit_price_locked: false,
            quantity_locked: linked || Boolean(line.quantity_locked),
            tax_type_locked: false,
            discount_tab: "percentage",
            discount_rule_id: "",
            discount_custom_amount: "",
            discount_percent: discountPercent,
          };
          if (isCommissionTuitionInvoiceLine(hydratedLine)) {
            return syncCommissionLineWithMetrics(hydratedLine, {
              forceRecalculate: true,
            });
          }
          return hydratedLine;
        }

        return {
          ...line,
          unit_price_locked: false,
          tax_type_locked: false,
          discount_custom_amount:
            line.discount_custom_amount || line.discount || "",
        };
      });

      setDocumentType(inferDocumentTypeFromInvoice(existingInvoice));
      setBranchId(
        String(existingInvoice?.branch_id || existingInvoice?.branchId || ""),
      );
      setBrandingId(
        String(
          existingInvoice?.branding_id || existingInvoice?.brandingId || "",
        ),
      );
      setLineItems(hydratedLines);
      if (isXeroPageMode) {
        setInvoiceCurrency(String(existingInvoice?.currency || "AUD"));
        setAmountsAreType(
          normalizeTaxType(
            existingInvoice?.tax_type ||
              existingInvoice?.gst_status ||
              hydratedLines[0]?.tax_type,
          ) || "inclusive",
        );
      }
      setIssueDate(
        toInvoiceDateInputValue(
          existingInvoice?.issue_date ||
            existingInvoice?.issueDate ||
            existingInvoice?.cost_date ||
            existingInvoice?.created_at,
        ),
      );
      setDueDate(
        toInvoiceDateInputValue(
          existingInvoice?.due_date || existingInvoice?.dueDate,
        ),
      );
      setReference(String(existingInvoice?.reference || ""));
      setStoredInvoiceNumber(
        existingInvoice?.invoice_number ||
          existingInvoice?.invoice_no ||
          existingInvoice?.invoiceNo ||
          "",
      );
      setSaveStatus(
        normalizeInvoiceStatus(
          existingInvoice?.status || DEFAULT_INVOICE_STATUS,
        ),
      );

      const existingDiscountAmount = getInvoiceDiscountAmount(existingInvoice);
      const existingDiscountReason = getInvoiceDiscountReason(existingInvoice);
      const existingDiscountRuleId =
        existingInvoice?.discount_rule_id ??
        existingInvoice?.discountRuleId ??
        "";
      const discountForm = mapInvoiceDiscountToFormState({
        amount: existingDiscountAmount,
        reason: existingDiscountReason,
        ruleId: existingDiscountRuleId,
      });

      setDiscountTab(discountForm.tab);
      setDiscountRuleId(discountForm.ruleId);
      setDiscountCustomAmount(discountForm.customAmount);
      setDiscountReason(discountForm.reason);
      setIsEditingDiscount(false);

      if (
        suggestionLinesOnly &&
        (uiWorkflowStatus === "draft" ||
          uiWorkflowStatus === "awaiting_approval")
      ) {
        commissionCreateFormBootstrappedRef.current = true;
      }
      if (invoiceLineItemSeed) {
        lastHydratedInvoiceSeedRef.current = invoiceLineItemSeed;
      }
    } else {
      const initialAmountsAre = suggestionLinesOnly
        ? normalizeTaxType(commissionPlanMeta?.defaultTaxType) || "notax"
        : normalizeTaxType(defaultTaxType) || "inclusive";
      const initialCurrency = isXeroPageMode
        ? String(defaultInvoiceCurrency || "").trim()
        : application?.currency || applicationCost?.currency || "AUD";
      const initialGstPercentage = isXeroPageMode
        ? resolveWizardGstPercentage(initialCurrency)
        : resolveApplicationGstPercentage(
            applicationCost,
            applicationGstInfo,
            application,
          );
      const initialTaxRate = resolveLineItemTaxRate(
        initialAmountsAre,
        initialGstPercentage,
      );

      if (isXeroPageMode) {
        setAmountsAreType(initialAmountsAre);
        setInvoiceCurrency(initialCurrency);
      }

      const initialLines =
        application && !startWithEmptyLines
          ? createInitialLineItemsFromApplication({
              application,
              applicationCost,
              applicationGstInfo,
              departmentGstInfo,
              applicationType,
              taxType: isXeroPageMode ? initialAmountsAre : defaultTaxType,
            })
          : [createEmptyLineItem("line-1", initialTaxRate, initialAmountsAre)];

      if (isXeroPageMode && application && !startWithEmptyLines) {
        initialLines.forEach((line) => {
          line.unit_price_locked = false;
          line.tax_type_locked = false;
          line.tax_type = initialAmountsAre;
          line.tax_rate = initialTaxRate;
        });
      }

      lineIdRef.current = initialLines.length;
      setDocumentType(isXeroPageMode ? "invoice" : "");
      setBranchId("");
      setBrandingId("");
      setLineItems(initialLines);
      const initialIssueDate = toDateInputValue(today);
      dueDateUserTouchedRef.current = false;
      setIssueDate(initialIssueDate);
      setDueDate(dueDateFromIssueDate(initialIssueDate, 1));
      setReference(clientId ? String(clientId) : "");
      setStoredInvoiceNumber("");
      setSaveStatus(DEFAULT_INVOICE_STATUS);
      setDiscountTab("predefined");
      setDiscountRuleId("");
      setDiscountCustomAmount("");
      setDiscountReason("");
      setIsEditingDiscount(false);

      commissionCreateFormBootstrappedRef.current = true;
    }

    if (invoiceSettingProp) {
      setInvoiceSetting(mapRowToInvoiceForm(invoiceSettingProp));
    }
  }, [
    show,
    isEditMode,
    loadingInvoice,
    pageLoading,
    invoiceDataLoadedFromPage,
    invoiceLineItemSeed,
    uiWorkflowStatus,
    existingInvoice?.id,
    application?.id,
    application?.currency,
    applicationCost,
    applicationGstInfo,
    departmentGstInfo,
    applicationType,
    clientId,
    invoiceSettingProp,
    discountFeeRules,
    isXeroPageMode,
    isXeroPageCreate,
    pageMode,
    clientData,
    startWithEmptyLines,
    defaultInvoiceCurrency,
    suggestionLinesOnly,
    commissionPlanMeta?.defaultTaxType,
    resolveWizardGstPercentage,
  ]);

  useEffect(() => {
    if (!show || !isXeroPageMode || !invoiceCatalog) return;
    setLoadedGstSettings(
      Array.isArray(invoiceCatalog.gst_settings)
        ? invoiceCatalog.gst_settings
        : [],
    );
  }, [show, isXeroPageMode, invoiceCatalog]);

  useEffect(() => {
    if (!show || !isXeroPageCreate || usesExternalInvoiceNumber)
      return undefined;

    let cancelled = false;

    const loadNextInvoiceNumber = async () => {
      setLoadingNextInvoiceNumber(true);
      try {
        const response = await apiUrl.get("getNextInvoiceNumber");
        if (!cancelled) {
          setNextInvoiceSequence(
            extractNextInvoiceNumberFromResponse(response),
          );
        }
      } catch (error) {
        console.error("Failed to load next invoice number:", error);
        if (!cancelled) {
          setNextInvoiceSequence("");
        }
      } finally {
        if (!cancelled) {
          setLoadingNextInvoiceNumber(false);
        }
      }
    };

    loadNextInvoiceNumber();

    return () => {
      cancelled = true;
    };
  }, [show, isXeroPageCreate, usesExternalInvoiceNumber]);

  useEffect(() => {
    if (!show || !suggestionLinesOnly) return;
    if (
      !commissionPlanMeta?.studentDiscountType &&
      !commissionPlanMeta?.student_discount_type &&
      !commissionPlanMeta?.studentDiscountPercent &&
      !commissionPlanMeta?.student_discount_percent &&
      !commissionPlanMeta?.studentDiscountAmount &&
      !commissionPlanMeta?.student_discount_amount
    ) {
      return;
    }

    setLineItems((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!isCommissionTuitionInvoiceLine(row)) return row;
        const synced = syncCommissionInvoiceLineMetrics(
          row,
          commissionPlanMeta,
          {
            // Plan student discount can be added after the invoice was created.
            forceRecalculate: true,
          },
        );
        if (
          synced.discount !== row.discount ||
          synced.student_discount !== row.student_discount
        ) {
          changed = true;
        }
        return synced;
      });
      return changed ? next : prev;
    });
  }, [show, suggestionLinesOnly, commissionPlanMeta, isEditMode]);

  useEffect(() => {
    if (!show || !isXeroPageMode || !currency) return;
    if (!suggestionLinesOnly && !effectiveGstSettings.length) return;

    const gstPercentage = resolveWizardGstPercentage();

    setLineItems((prev) =>
      prev.map((row) => {
        const rowTax = normalizeTaxType(row.tax_type);
        const docTax = normalizeTaxType(amountsAreType) || "notax";
        const taxType =
          rowTax ||
          (suggestionLinesOnly
            ? isCommissionTuitionInvoiceLine(row)
              ? docTax
              : ""
            : docTax) ||
          "notax";

        if (taxType === "notax") {
          if (rowTax === "notax" && !row.tax_rate) return row;
          return { ...row, tax_type: "notax", tax_rate: "" };
        }

        const existingRate = Number(row.tax_rate) || 0;
        const nextRate =
          !suggestionLinesOnly || isEditMode
            ? existingRate > 0
              ? String(row.tax_rate)
              : resolveLineItemTaxRate(taxType, gstPercentage)
            : existingRate > 0
              ? String(row.tax_rate)
              : resolveLineItemTaxRate(taxType, gstPercentage);

        if (row.tax_type === taxType && String(row.tax_rate || "") === nextRate) {
          return row;
        }

        return {
          ...row,
          tax_type: taxType,
          tax_rate: nextRate,
        };
      }),
    );
  }, [
    show,
    isXeroPageMode,
    effectiveGstSettings,
    currency,
    suggestionLinesOnly,
    amountsAreType,
    isEditMode,
    resolveWizardGstPercentage,
  ]);

  useEffect(() => {
    if (!show || !isEditMode || loadingInvoice || !existingInvoice) return;
    if (isEditingDiscount) return;

    setSaveStatus(
      normalizeInvoiceStatus(existingInvoice?.status || DEFAULT_INVOICE_STATUS),
    );

    const discountForm = mapInvoiceDiscountToFormState({
      amount: getInvoiceDiscountAmount(existingInvoice),
      reason: getInvoiceDiscountReason(existingInvoice),
      ruleId:
        existingInvoice?.discount_rule_id ??
        existingInvoice?.discountRuleId ??
        "",
    });

    setDiscountTab(discountForm.tab);
    setDiscountRuleId(discountForm.ruleId);
    setDiscountCustomAmount(discountForm.customAmount);
    setDiscountReason(discountForm.reason);
    setIsEditingDiscount(false);
  }, [show, isEditMode, loadingInvoice, invoiceDetailsSeed, isEditingDiscount]);

  const effectiveBranches = useMemo(() => {
    if (!isEditMode || !existingInvoice?.branch_snapshot) return branches;
    const snapshot = existingInvoice.branch_snapshot;
    if (!snapshot?.id) return branches;
    const exists = branches.some(
      (item) => String(item.id) === String(snapshot.id),
    );
    if (exists) return branches;
    return [...branches, snapshot];
  }, [branches, isEditMode, existingInvoice]);

  const effectiveBrandings = useMemo(() => {
    if (!isEditMode || !existingInvoice?.branding_snapshot) return brandings;
    const snapshot = existingInvoice.branding_snapshot;
    if (!snapshot?.id) return brandings;
    const exists = brandings.some(
      (item) => String(item.id) === String(snapshot.id),
    );
    if (exists) return brandings;
    return [
      ...brandings,
      { ...mapRowToInvoiceBrandingForm(snapshot), id: snapshot.id },
    ];
  }, [brandings, isEditMode, existingInvoice]);

  const selectedType = useMemo(
    () => INVOICE_DOCUMENT_TYPES.find((item) => item.value === documentType),
    [documentType],
  );

  const selectedBranch = useMemo(
    () =>
      effectiveBranches.find((item) => String(item.id) === String(branchId)),
    [effectiveBranches, branchId],
  );

  const selectedBranding = useMemo(
    () =>
      effectiveBrandings.find((item) => String(item.id) === String(brandingId)),
    [effectiveBrandings, brandingId],
  );

  const numberingInvoices =
    existingInvoicesForNumbering?.length > 0
      ? existingInvoicesForNumbering
      : applicationInvoices;

  const resolveDraftInvoiceNumber = useCallback(() => {
    if (isEditMode && storedInvoiceNumber) {
      return replaceDocumentTypePrefixInInvoiceNumber(
        storedInvoiceNumber,
        documentType,
      );
    }

    if (isXeroPageCreate && effectiveNextInvoiceSequence) {
      return buildInvoiceNumberWithClientId({
        invoiceNumberTemplate: effectiveNextInvoiceSequence,
        clientId: clientProfile.id || clientId,
        documentType: documentType || "invoice",
      });
    }

    const year = issueDate
      ? new Date(`${issueDate}T00:00:00`).getFullYear()
      : new Date().getFullYear();

    return buildDraftInvoiceNumber({
      documentType,
      clientId: clientProfile.id || clientId,
      year,
      existingInvoices: numberingInvoices,
      excludeInvoiceId: isEditMode ? existingInvoice?.id : null,
    });
  }, [
    isEditMode,
    storedInvoiceNumber,
    isXeroPageCreate,
    documentType,
    effectiveNextInvoiceSequence,
    clientProfile.id,
    clientId,
    issueDate,
    numberingInvoices,
    existingInvoice?.id,
  ]);

  const invoiceNumber = useMemo(
    () => resolveDraftInvoiceNumber(),
    [resolveDraftInvoiceNumber],
  );

  const handleOpenSendEmailModal = useCallback(() => {
    const targetInvoiceId = existingInvoice?.id;
    if (!targetInvoiceId) return;

    setApproveEmailModal({
      invoiceId: targetInvoiceId,
      clientId:
        clientId ||
        clientProfile?.id ||
        existingInvoice?.client_id ||
        existingInvoice?.clientId ||
        null,
      isCommission: suggestionLinesOnly,
      fallbackRecipientEmail: suggestionLinesOnly
        ? ""
        : String(clientProfile?.email || "").trim(),
      fallbackRecipientName: suggestionLinesOnly
        ? String(contactDisplayName || "").trim()
        : String(clientProfile?.fullName || "").trim(),
      fallbackInvoiceNumber: invoiceNumber,
      requiresApprove: false,
    });
  }, [
    existingInvoice,
    clientId,
    clientProfile,
    suggestionLinesOnly,
    contactDisplayName,
    invoiceNumber,
  ]);

  const previewDraft = useMemo(
    () => ({
      documentType,
      documentTypeLabel: selectedType?.label || "",
      brandingTitle: resolveInvoiceDisplayTitle({
        branding: selectedBranding,
        documentType,
      }),
      invoiceNumber,
      issueDate,
      dueDate,
      reference,
      clientProfile: billToPartyProfile,
      previewDisplay: {
        showLineDiscount: !suggestionLinesOnly,
        showDiscountTotal: !suggestionLinesOnly,
        useCommissionLineLayout: false,
        useExTaxLineLayout: isXeroPageMode,
        commissionBillToPrintOptions: showInvoicePreviewCustomize
          ? commissionBillToPrintOptions
          : undefined,
        invoiceDisplayOptions: showInvoicePreviewCustomize
          ? invoiceDisplayOptions
          : undefined,
      },
      currency,
      taxType: documentTaxType,
      status: saveStatus,
      branding: selectedBranding,
      branch: selectedBranch,
      invoiceSetting,
      lineItems: effectiveTotals.lineItems,
      gross_subtotal: effectiveTotals.gross_subtotal,
      discount_total: isEditMode
        ? Number(
            existingInvoice?.discount_total ??
              existingInvoice?.discountTotal ??
              savedDiscountAmount,
          ) || 0
        : suggestionLinesOnly
          ? Number(lineTotals.discount_total) ||
            Number(effectiveTotals.invoice_discount_amount) ||
            0
          : usesSegregatedLineDiscounts
            ? Number(lineTotals.discount_total) || 0
            : Number(effectiveTotals.invoice_discount_amount) ||
              Number(effectiveTotals.discount_total) ||
              0,
      subtotal: isXeroPageMode
        ? (xeroExTaxDisplayTotals?.subtotalExTax ?? effectiveTotals.subtotal)
        : effectiveTotals.subtotal,
      tax_amount: isXeroPageMode
        ? (xeroExTaxDisplayTotals?.gst ?? effectiveTotals.tax_amount)
        : effectiveTotals.tax_amount,
      inclusive_tax_amount: effectiveTotals.inclusive_tax_amount,
      exclusive_tax_amount: effectiveTotals.exclusive_tax_amount,
      total_amount: (() => {
        if (isXeroPageMode && xeroExTaxDisplayTotals) {
          return xeroExTaxDisplayTotals.total;
        }
        if (isEditMode) {
          return resolvePayableInvoiceTotal({
            effectiveTotals,
            xeroExTaxDisplayTotals,
            lineTotals,
            storedTotal:
              existingInvoice?.total_amount ?? existingInvoice?.totalAmount,
          });
        }
        return effectiveTotals.total_amount;
      })(),
      paid_amount: useDraftPaymentFlow
        ? createPaidAmount
        : isEditMode
          ? resolveInvoicePaidAmount(existingInvoice)
          : 0,
      amount_due: (() => {
        const previewTotal =
          isXeroPageMode && xeroExTaxDisplayTotals
            ? xeroExTaxDisplayTotals.total
            : isEditMode
              ? resolvePayableInvoiceTotal({
                  effectiveTotals,
                  xeroExTaxDisplayTotals,
                  lineTotals,
                  storedTotal:
                    existingInvoice?.total_amount ??
                    existingInvoice?.totalAmount,
                })
              : effectiveTotals.total_amount;
        const previewPaid = useDraftPaymentFlow
          ? createPaidAmount
          : isEditMode
            ? resolveInvoicePaidAmount(existingInvoice)
            : 0;

        return resolveInvoiceDocumentAmountDue({
          displayTotal: previewTotal,
          paidTotal: previewPaid,
          explicitAmountDue: useDraftPaymentFlow
            ? createAmountDue
            : isEditMode
              ? existingInvoice?.amount_due ?? existingInvoice?.amountDue
              : previewTotal,
        });
      })(),
      invoice_discount_amount: effectiveTotals.invoice_discount_amount || 0,
      invoice_discount_description: pendingInvoiceDiscount?.description || "",
      paymentDate: resolveInvoicePreviewPaymentDate(
        isEditMode ? existingInvoice : null,
        draftPayments,
      ),
    }),
    [
      documentType,
      selectedType,
      invoiceNumber,
      issueDate,
      dueDate,
      reference,
      clientProfile,
      billToPartyProfile,
      currency,
      documentTaxType,
      selectedBranding,
      selectedBranch,
      invoiceSetting,
      lineTotals,
      effectiveTotals,
      pendingInvoiceDiscount,
      saveStatus,
      isEditMode,
      existingInvoice,
      savedDiscountAmount,
      usesSegregatedLineDiscounts,
      useDraftPaymentFlow,
      createPaidAmount,
      createAmountDue,
      suggestionLinesOnly,
      isXeroPageMode,
      xeroExTaxDisplayTotals,
      commissionBillToPrintOptions,
      invoiceDisplayOptions,
      draftPayments,
    ],
  );

  const updateLineItem = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (
          field === "unit_price" &&
          row.unit_price_locked &&
          (!isXeroPageMode || suggestionLinesOnly)
        ) {
          return row;
        }
        if (
          field === "quantity" &&
          row.quantity_locked &&
          suggestionLinesOnly
        ) {
          return row;
        }
        return { ...row, [field]: value };
      }),
    );
    if (errors.lineItems || lineDiscountErrorId === id) {
      setErrors((prev) => ({ ...prev, lineItems: "", invoiceDiscount: "" }));
      if (lineDiscountErrorId === id) {
        setLineDiscountErrorId("");
      }
    }
  };

  const updateLineDiscountMeta = (id, updates) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        return withSyncedLineDiscount({ ...row, ...updates }, discountFeeRules);
      }),
    );
    if (errors.lineItems || lineDiscountErrorId === id) {
      setErrors((prev) => ({ ...prev, lineItems: "", invoiceDiscount: "" }));
      if (lineDiscountErrorId === id) {
        setLineDiscountErrorId("");
      }
    }
  };

  const handleLineNumberChange = (id, field, rawValue, sanitizer) => {
    updateLineItem(id, field, sanitizer(rawValue));
  };

  const handleLineNumberBlur = (id, field) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (
          field === "unit_price" &&
          row.unit_price_locked &&
          (!isXeroPageMode || suggestionLinesOnly)
        ) {
          return row;
        }
        if (
          field === "quantity" &&
          row.quantity_locked &&
          suggestionLinesOnly
        ) {
          return row;
        }

        let nextRow = row;
        if (field === "quantity") {
          nextRow = {
            ...row,
            quantity: isXeroPageMode
              ? normalizeInvoiceAmountInput(row.quantity) ||
                row.quantity ||
                "1.00"
              : normalizeQuantityInput(row.quantity),
          };
        } else if (field === "unit_price") {
          nextRow = {
            ...row,
            unit_price: normalizeInvoiceAmountInput(row.unit_price),
          };
        } else {
          nextRow = { ...row, [field]: normalizeDecimalInput(row[field]) };
        }

        if (
          usesSegregatedLineDiscounts &&
          (field === "quantity" || field === "unit_price")
        ) {
          return withSyncedLineDiscount(nextRow, discountFeeRules);
        }

        if (
          suggestionLinesOnly &&
          isCommissionTuitionInvoiceLine(nextRow) &&
          (field === "quantity" || field === "unit_price")
        ) {
          return syncCommissionLineWithMetrics(
            resyncCommissionLineDiscountIfNeeded(nextRow),
          );
        }

        if (
          isXeroPageMode &&
          (field === "quantity" || field === "unit_price") &&
          (Number(nextRow.discount) > 0 ||
            nextRow.discount_custom_amount !== "" ||
            nextRow.discount_percent !== "")
        ) {
          return suggestionLinesOnly
            ? resyncCommissionLineDiscountIfNeeded(nextRow)
            : syncSimpleLineDiscount(nextRow);
        }

        return nextRow;
      }),
    );
  };

  const handleLineDescriptionChange = (rowId, value) => {
    updateLineItem(rowId, "description", value);
  };

  const handleApplicationSuggestionSelect = (rowId, application) => {
    applyClientApplicationToLine(rowId, application);
  };

  const handleDescriptionSuggestionSelect = (rowId, option) => {
    if (!option) return;
    if (option.kind === "suggestion") {
      applyLineItemSuggestionToLine(rowId, option.fields);
      return;
    }
    handleApplicationSuggestionSelect(rowId, option.application);
  };

  const bindDescSuggestAnchor = useCallback((rowId, node) => {
    if (node) {
      descSuggestAnchorsRef.current.set(String(rowId), node);
    } else {
      descSuggestAnchorsRef.current.delete(String(rowId));
    }
  }, []);

  const getOpenDescSuggestAnchor = useCallback(() => {
    if (!descriptionSuggestionRowId) return null;
    return descSuggestAnchorsRef.current.get(
      String(descriptionSuggestionRowId),
    );
  }, [descriptionSuggestionRowId]);

  const openDescriptionSuggestions = useCallback(
    (rowId) => {
      if (isCommissionLineItemsReadOnly || isPaidInvoiceDescriptionOnlyEdit) {
        return;
      }
      if (descSuggestBlurTimeoutRef.current) {
        window.clearTimeout(descSuggestBlurTimeoutRef.current);
        descSuggestBlurTimeoutRef.current = null;
      }

      const options = getDescriptionSuggestionOptionsForRow(rowId);
      if (options.some((option) => option.kind === "suggestion")) {
        setDescriptionSuggestionRowId(String(rowId));
      }
    },
    [
      getDescriptionSuggestionOptionsForRow,
      isCommissionLineItemsReadOnly,
      isPaidInvoiceDescriptionOnlyEdit,
    ],
  );

  const closeDescriptionSuggestions = useCallback(() => {
    if (descSuggestBlurTimeoutRef.current) {
      window.clearTimeout(descSuggestBlurTimeoutRef.current);
      descSuggestBlurTimeoutRef.current = null;
    }
    setDescriptionSuggestionRowId("");
  }, []);

  useEffect(() => {
    if (isCommissionLineItemsReadOnly) {
      closeDescriptionSuggestions();
    }
  }, [isCommissionLineItemsReadOnly, closeDescriptionSuggestions]);

  const toggleDescriptionSuggestions = useCallback(
    (rowId) => {
      if (isCommissionLineItemsReadOnly || isPaidInvoiceDescriptionOnlyEdit) {
        return;
      }
      if (descriptionSuggestionRowId === String(rowId)) {
        closeDescriptionSuggestions();
        return;
      }
      openDescriptionSuggestions(rowId);
    },
    [
      closeDescriptionSuggestions,
      descriptionSuggestionRowId,
      openDescriptionSuggestions,
      isCommissionLineItemsReadOnly,
      isPaidInvoiceDescriptionOnlyEdit,
    ],
  );

  const handleDescriptionBlur = useCallback(() => {
    if (descSuggestBlurTimeoutRef.current) {
      window.clearTimeout(descSuggestBlurTimeoutRef.current);
    }
    descSuggestBlurTimeoutRef.current = window.setTimeout(() => {
      descSuggestBlurTimeoutRef.current = null;
      setDescriptionSuggestionRowId("");
    }, 150);
  }, []);

  useEffect(() => {
    if (!descriptionSuggestionRowId) return undefined;

    const handleDocumentMouseDown = (event) => {
      const rowId = String(descriptionSuggestionRowId);
      const insideOpenAnchor = event.target.closest?.(
        `[data-desc-suggest-anchor="${rowId}"]`,
      );
      const insideOpenMenu = event.target.closest?.(
        `[data-desc-suggest-menu="${rowId}"]`,
      );
      if (insideOpenAnchor || insideOpenMenu) return;
      closeDescriptionSuggestions();
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener("mousedown", handleDocumentMouseDown);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [closeDescriptionSuggestions, descriptionSuggestionRowId]);

  const syncSimpleLineDiscount = (row) => {
    if (suggestionLinesOnly) {
      return syncCommissionLineWithMetrics(row);
    }

    const gross = getLineTotalBeforeDiscount({
      ...row,
      discount: 0,
      discount_custom_amount: "",
    });
    const maxDiscount = gross;
    const raw = row.discount_custom_amount ?? row.discount ?? "";
    if (raw === "" || raw === null || raw === undefined) {
      return { ...row, discount: "", discount_custom_amount: "" };
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      return { ...row, discount: "", discount_custom_amount: raw };
    }
    const amount = Math.min(Math.max(parsed, 0), maxDiscount);
    return {
      ...row,
      discount_tab: "custom",
      discount_rule_id: "",
      discount_custom_amount:
        raw === String(row.discount_custom_amount)
          ? row.discount_custom_amount
          : String(raw),
      discount: amount > 0 ? String(amount) : "",
    };
  };

  const resyncCommissionLineDiscountIfNeeded = (row) => {
    if (!suggestionLinesOnly) return row;
    if (!isCommissionTuitionInvoiceLine(row)) return row;
    return syncCommissionLineWithMetrics(row, { forceRecalculate: true });
  };

  const handleSimpleLineDiscountChange = (id, rawValue) => {
    const cleaned = suggestionLinesOnly
      ? sanitizePercentInput(rawValue)
      : sanitizeDiscountDecimalInput(rawValue);
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        return syncSimpleLineDiscount({
          ...row,
          ...(suggestionLinesOnly
            ? { discount_percent: cleaned }
            : { discount_custom_amount: cleaned }),
        });
      }),
    );
    if (errors.lineItems || lineDiscountErrorId === id) {
      setErrors((prev) => ({ ...prev, lineItems: "", invoiceDiscount: "" }));
      if (lineDiscountErrorId === id) {
        setLineDiscountErrorId("");
      }
    }
  };

  const handleSimpleLineDiscountBlur = (id) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const normalized = suggestionLinesOnly
          ? normalizePercentInput(row.discount_percent ?? "")
          : normalizeDiscountAmountInput(
              row.discount_custom_amount ?? row.discount ?? "",
            );
        return syncSimpleLineDiscount({
          ...row,
          ...(suggestionLinesOnly
            ? { discount_percent: normalized }
            : { discount_custom_amount: normalized }),
        });
      }),
    );
  };

  const getDefaultLineTaxType = () => {
    if (isXeroPageMode) {
      return amountsAreType || "inclusive";
    }
    return (
      lineItems[0]?.tax_type ||
      getDefaultTaxTypeFromApplication(applicationCost, applicationGstInfo) ||
      "notax"
    );
  };

  const addLineItem = () => {
    if (isCommissionLineItemsReadOnly) return;
    lineIdRef.current += 1;
    const defaultType = getDefaultLineTaxType();
    const nextRate = resolveLineItemTaxRate(
      defaultType,
      resolveWizardGstPercentage(),
    );
    setLineItems((prev) => [
      ...prev,
      createEmptyLineItem(`line-${lineIdRef.current}`, nextRate, defaultType),
    ]);
  };

  const removeLineItem = (id) => {
    const defaultType = getDefaultTaxTypeFromApplication(
      applicationCost,
      applicationGstInfo,
    );
    const nextRate = resolveLineItemTaxRate(
      defaultType,
      applicationGstPercentage,
    );
    setLineItems((prev) => {
      if (prev.length <= 1) {
        return [
          createEmptyLineItem(
            `line-${lineIdRef.current}`,
            nextRate,
            defaultType,
          ),
        ];
      }
      return prev.filter((row) => row.id !== id);
    });
  };

  const handleLineTaxTypeChange = (id, nextTaxType) => {
    const targetRow = lineItems.find((row) => row.id === id);
    if (targetRow?.tax_type_locked && !isXeroPageMode) return;
    if (isCommissionLineItemsReadOnly) return;

    const normalizedType = normalizeTaxType(nextTaxType) || "notax";
    const gstPercentage = resolveWizardGstPercentage();

    if (isXeroPageMode && !preservePerLineTaxInEdit) {
      setAmountsAreType(normalizedType);
    }

    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        let nextRow;
        if (normalizedType === "notax") {
          nextRow = {
            ...row,
            tax_type: "notax",
            tax_rate: "",
            ...(isBonusInvoiceLine(row)
              ? {
                  gst_type: "notax",
                  gst_status: "notax",
                  gst_percentage: 0,
                }
              : {}),
          };
        } else {
          const existingRate = Number(row.tax_rate) || 0;
          const nextRate =
            existingRate > 0
              ? String(existingRate)
              : resolveLineItemTaxRate(normalizedType, gstPercentage);

          nextRow = {
            ...row,
            tax_type: normalizedType,
            tax_rate: nextRate,
            ...(isBonusInvoiceLine(row)
              ? {
                  gst_type: normalizedType,
                  gst_status: normalizedType,
                  gst_percentage: nextRate ? Number(nextRate) : 0,
                }
              : {}),
          };
        }

        return resyncCommissionLineDiscountIfNeeded(nextRow);
      }),
    );
  };

  const handleLineTaxRateChange = (id, value) => {
    const cleaned = sanitizeDecimalInput(value);
    setLineItems((prev) =>
      prev.map((row) => (row.id === id ? { ...row, tax_rate: cleaned } : row)),
    );
  };

  const handleLineTaxRateBlur = (id) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const taxType = normalizeTaxType(row.tax_type) || "notax";
        if (taxType === "notax") {
          return { ...row, tax_rate: "" };
        }

        const normalized = normalizeDecimalInput(row.tax_rate);
        const parsedRate = Number(normalized);
        if (!normalized || Number.isNaN(parsedRate) || parsedRate < 0) {
          const fallbackRate = resolveLineItemTaxRate(
            taxType,
            resolveWizardGstPercentage(),
          );
          return resyncCommissionLineDiscountIfNeeded({
            ...row,
            tax_rate: fallbackRate,
          });
        }

        return resyncCommissionLineDiscountIfNeeded({
          ...row,
          tax_rate: normalized,
        });
      }),
    );
  };

  const handlePreview = () => {
    setPreviewAutoPrint(false);
    setShowPreview(true);
  };

  const handlePrintPdf = () => {
    setPreviewAutoPrint(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewAutoPrint(false);
  };

  const handlePdfExportComplete = () => {
    setPreviewAutoPrint(false);
  };

  const resetFormForAnotherInvoice = useCallback(() => {
    const taxType = normalizeTaxType(amountsAreType) || "notax";
    const initialTaxRate = resolveLineItemTaxRate(
      taxType,
      resolveWizardGstPercentage(),
    );

    lineIdRef.current = 1;
    draftPaymentIdRef.current = 1;
    setLineItems([createEmptyLineItem("line-1", initialTaxRate, taxType)]);
    setDraftPayments([]);
    setSaveStatus(DEFAULT_INVOICE_STATUS);
    setStoredInvoiceNumber("");
    setErrors({});
    setShowSaveMenu(false);
    setShowApproveMenu(false);
    setEditingLineDiscountId("");
    setLineDiscountSnapshot(null);
    setLineDiscountErrorId("");
  }, [amountsAreType, resolveWizardGstPercentage]);

  const handleDiscountTabChange = (nextTab) => {
    if (nextTab === discountTab) return;
    setDiscountTab(nextTab);
    setDiscountRuleId("");
    setDiscountCustomAmount("");
    setDiscountReason("");
    setErrors((prev) => ({
      ...prev,
      invoiceDiscount: "",
    }));
  };

  if (!show) return null;

  const showInitialLoader =
    pageLoading ||
    (isEditMode && loadingInvoice) ||
    (!isEditMode && loadingOptions);

  const validate = () => {
    const nextErrors = {};
    if (!documentType) {
      nextErrors.documentType = "Select document type.";
    }
    if (!issueDate) {
      nextErrors.issueDate = "Required.";
    }
    if (dueDate && issueDate && dueDate < issueDate) {
      nextErrors.dueDate = "Due date cannot be before issue date.";
    }
    if (!branchId) {
      nextErrors.branchId = "Select branch.";
    }
    if (!brandingId) {
      nextErrors.brandingId = "Select branding.";
    }
    if (isXeroPageMode && !invoiceCurrency) {
      nextErrors.currency = "Please select currency";
    }

    const hasValidLine = lineItems.some(
      (row) =>
        String(row.description || "").trim() &&
        Number(row.unit_price) > 0 &&
        Number(row.quantity) > 0,
    );
    if (!hasValidLine) {
      nextErrors.lineItems =
        "Add at least one line item with description, quantity, and price.";
    }

    if (usesSegregatedLineDiscounts && !isEditMode) {
      const lineDiscountValidation = validateLineItemDiscounts(
        lineItems,
        discountFeeRules,
      );
      if (!lineDiscountValidation.valid) {
        nextErrors.lineItems = lineDiscountValidation.error;
        setLineDiscountErrorId(lineDiscountValidation.lineId || "");
      } else {
        setLineDiscountErrorId("");
      }
    } else if (showInvoiceLevelDiscountEditor) {
      const discountValidation = validateInvoiceDiscountForm({
        tab: discountTab,
        ruleId: discountRuleId,
        customAmount: discountCustomAmount,
        reason: discountReason,
        grossSubtotal: lineTotals.gross_subtotal,
        maxTotal: lineTotals.total_amount,
        rules: discountFeeRules,
        currency,
      });

      if (!discountValidation.valid) {
        nextErrors.invoiceDiscount = discountValidation.error;
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleIssueDateChange = (value) => {
    setIssueDate(value);
    setErrors((prev) => ({ ...prev, issueDate: "", dueDate: "" }));
    dueDateUserTouchedRef.current = false;
    setDueDate(value ? dueDateFromIssueDate(value, 1) : "");
  };

  const handleDueDateChange = (value) => {
    dueDateUserTouchedRef.current = true;
    if (issueDate && value && value < issueDate) {
      setDueDate(value);
      setErrors((prev) => ({
        ...prev,
        dueDate: "Due date cannot be before issue date.",
      }));
      return;
    }
    setDueDate(value);
    setErrors((prev) => ({ ...prev, dueDate: "" }));
  };

  const handleOpenDiscountEditor = () => {
    const discountForm = mapInvoiceDiscountToFormState({
      amount: savedDiscountAmount,
      reason: savedDiscountReason,
      ruleId:
        existingInvoice?.discount_rule_id ??
        existingInvoice?.discountRuleId ??
        "",
    });

    setDiscountTab(discountForm.tab);
    setDiscountRuleId(discountForm.ruleId);
    setDiscountCustomAmount(discountForm.customAmount);
    setDiscountReason(discountForm.reason);
    setIsEditingDiscount(true);
    setErrors((prev) => ({ ...prev, invoiceDiscount: "" }));
  };

  const handleCancelDiscountEdit = () => {
    const discountForm = mapInvoiceDiscountToFormState({
      amount: savedDiscountAmount,
      reason: savedDiscountReason,
      ruleId:
        existingInvoice?.discount_rule_id ??
        existingInvoice?.discountRuleId ??
        "",
    });

    setDiscountTab(discountForm.tab);
    setDiscountRuleId(discountForm.ruleId);
    setDiscountCustomAmount(discountForm.customAmount);
    setDiscountReason(discountForm.reason);
    setIsEditingDiscount(false);
    setErrors((prev) => ({ ...prev, invoiceDiscount: "" }));
  };

  const handleOpenLineDiscountEdit = (row) => {
    setLineDiscountSnapshot({ ...row });
    setEditingLineDiscountId(row.id);
    setLineDiscountErrorId("");
    setErrors((prev) => ({ ...prev, lineItems: "" }));
  };

  const handleCancelLineDiscountEdit = (rowId) => {
    if (lineDiscountSnapshot && lineDiscountSnapshot.id === rowId) {
      setLineItems((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...lineDiscountSnapshot } : row,
        ),
      );
    }
    setEditingLineDiscountId("");
    setLineDiscountSnapshot(null);
    setLineDiscountErrorId("");
    setErrors((prev) => ({ ...prev, lineItems: "" }));
  };

  const handleUpdateLineDiscount = async (row) => {
    const lineTotal = getLineTotalBeforeDiscount(row);
    const discountValidation = validateLineItemDiscountForm(
      row,
      discountFeeRules,
    );

    if (!discountValidation.valid) {
      setLineDiscountErrorId(row.id);
      setErrors((prev) => ({
        ...prev,
        lineItems: discountValidation.error,
      }));
      return;
    }

    const invoiceItemId =
      row.line_item_id ||
      resolvePrimaryInvoiceLineItemId([row], existingInvoice);

    if (!invoiceItemId) {
      setLineDiscountErrorId(row.id);
      setErrors((prev) => ({
        ...prev,
        lineItems: "Invoice line item could not be found.",
      }));
      return;
    }

    const discountTransactionId = resolveLineDiscountTransactionId(
      row,
      ledgerEntries,
      activeDiscountTransaction,
    );

    const discountRequest = discountTransactionId
      ? buildInvoiceUpdateDiscountRequest({
          invoiceId: existingInvoice?.id,
          invoiceItemId,
          discountTransactionId,
          tab: row.discount_tab || "predefined",
          ruleId: row.discount_rule_id || "",
          rules: discountFeeRules,
          customAmount: row.discount_custom_amount || "",
          reason: row.discount_reason || "",
          grossSubtotal: lineTotal,
          maxTotal: lineTotal,
        })
      : buildInvoiceApplyDiscountRequest({
          invoiceId: existingInvoice?.id,
          invoiceItemId,
          tab: row.discount_tab || "predefined",
          ruleId: row.discount_rule_id || "",
          rules: discountFeeRules,
          customAmount: row.discount_custom_amount || "",
          reason: row.discount_reason || "",
          grossSubtotal: lineTotal,
          maxTotal: lineTotal,
        });

    if (!discountRequest) {
      setLineDiscountErrorId(row.id);
      setErrors((prev) => ({
        ...prev,
        lineItems: "Enter a valid discount amount and reason.",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, lineItems: "" }));
    const result = await onUpdateDiscount?.(discountRequest);
    if (result !== false) {
      setEditingLineDiscountId("");
      setLineDiscountSnapshot(null);
      setLineDiscountErrorId("");
    }
  };

  const buildSharedSaveParams = (requestedStatus = saveStatus) => {
    const gstPercentage = resolveWizardGstPercentage();
    const commissionLinesForSave = suggestionLinesOnly
      ? lineItems.map((row) =>
          isCommissionTuitionInvoiceLine(row)
            ? syncCommissionInvoiceLineMetrics(row, commissionPlanMeta, {
                forceRecalculate: true,
              })
            : row,
        )
      : lineItems;
    const saveLineItems = suggestionLinesOnly
      ? applyDocumentTaxToInstallmentLines(
          commissionLinesForSave,
          documentTaxType,
          gstPercentage,
        )
      : lineItems;
    const saveLineTotals = suggestionLinesOnly
      ? computeCommissionSuggestionInvoiceTotals(
          saveLineItems,
          commissionPlanMeta,
        )
      : saveLineItems === lineItems
        ? lineTotals
        : calculateInvoiceLineTotals(saveLineItems);

    return {
      status: requestedStatus,
      invoiceTitle: resolveInvoiceDisplayTitle({
        branding: selectedBranding,
        documentType,
      }),
      invoiceNumber,
      issueDate,
      dueDate,
      reference,
      documentType,
      documentTaxType,
      branchId,
      selectedBranch,
      brandingId,
      selectedBranding,
      application,
      applicationType,
      clientProfile,
      billToProfile: billToPartyProfile,
      clientId,
      currency,
      lineTotals: suggestionLinesOnly
        ? {
            ...saveLineTotals,
            // Keep informational discount in payload; totals already exclude it.
            discount_total: Number(saveLineTotals.discount_total) || 0,
          }
        : saveLineTotals,
      invoiceDiscount: suggestionLinesOnly
        ? null
        : usesSegregatedLineDiscounts || isEditMode
          ? null
          : pendingInvoiceDiscount,
      ...(enableContactSearch
        ? {
            externalReferringAgent: contactReferringAgentId || null,
            externalProcessingAgent: contactProcessingAgentId || null,
          }
        : enableAgentSelection
          ? {
              externalReferringAgent: selectedReferringAgentId || null,
              externalProcessingAgent: selectedProcessingAgentId || null,
            }
          : {}),
      ...(suggestionLinesOnly
        ? { commissionBillToPrintOptions }
        : {}),
    };
  };

  const handleUpdateDiscount = async () => {
    const discountValidation = validateInvoiceDiscountForm({
      tab: discountTab,
      ruleId: discountRuleId,
      customAmount: discountCustomAmount,
      reason: discountReason,
      grossSubtotal: lineTotals.gross_subtotal,
      maxTotal: lineTotals.total_amount,
      rules: discountFeeRules,
      currency,
    });

    if (!discountValidation.valid) {
      setErrors((prev) => ({
        ...prev,
        invoiceDiscount: discountValidation.error,
      }));
      return;
    }

    const invoiceItemId = resolvePrimaryInvoiceLineItemId(
      lineItems,
      existingInvoice,
    );

    if (!invoiceItemId) {
      setErrors((prev) => ({
        ...prev,
        invoiceDiscount: "Invoice line item could not be found.",
      }));
      return;
    }

    const discountTransactionId =
      activeDiscountTransaction?.id ||
      getInvoiceDiscountLedgerId(existingInvoice);

    const discountRequest = discountTransactionId
      ? buildInvoiceUpdateDiscountRequest({
          invoiceId: existingInvoice?.id,
          invoiceItemId,
          discountTransactionId,
          tab: discountTab,
          ruleId: discountRuleId,
          rules: discountFeeRules,
          customAmount: discountCustomAmount,
          reason: discountReason,
          grossSubtotal: lineTotals.gross_subtotal,
          maxTotal: lineTotals.total_amount,
        })
      : buildInvoiceApplyDiscountRequest({
          invoiceId: existingInvoice?.id,
          invoiceItemId,
          tab: discountTab,
          ruleId: discountRuleId,
          rules: discountFeeRules,
          customAmount: discountCustomAmount,
          reason: discountReason,
          grossSubtotal: lineTotals.gross_subtotal,
          maxTotal: lineTotals.total_amount,
        });

    if (!discountRequest) {
      setErrors((prev) => ({
        ...prev,
        invoiceDiscount: "Enter a valid discount amount and reason.",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, invoiceDiscount: "" }));
    const result = await onUpdateDiscount?.(discountRequest);
    if (result !== false) {
      setIsEditingDiscount(false);
    }
  };

  const handleSave = async (requestedStatus = saveStatus, saveOptions = {}) => {
    const {
      saveAndAnother = false,
      sendEmail = false,
      approve = false,
      openApproveEmailModal = false,
    } = saveOptions;
    if (!validate()) return;

    const draftPaymentTotal = sumCreateInvoicePayments(draftPayments);
    const requested = normalizeInvoiceStatus(requestedStatus);
    const isCommissionApprove = useWorkflowInvoiceFlow && Boolean(approve);
    const isApproveFromAwaitingApproval =
      isAwaitingApprovalInvoice &&
      (isCommissionApprove ||
        ["awaiting_payment", "approved", "paid", "partial"].includes(
          requested,
        ));

    const paidForStatus = useDraftPaymentFlow
      ? createPaidAmount
      : isEditMode
        ? resolveInvoicePaidAmount(existingInvoice)
        : 0;

    const baseForSave =
      requested === "awaiting_approval"
        ? "awaiting_approval"
        : requested === "approved"
          ? "approved"
          : requested === "awaiting_payment" || requested === "draft"
            ? requested
            : userStatusForDerivation;

    const normalizedStatus = useWorkflowInvoiceFlow
      ? useDraftPaymentFlow && draftPaymentTotal > 0
        ? resolveCreateInvoiceStatusFromPayment({
            requestedStatus: baseForSave,
            totalAmount: effectiveTotals?.total_amount,
            paidAmount: paidForStatus,
          })
        : requested
      : resolveCreateInvoiceStatusFromPayment({
          requestedStatus: baseForSave,
          totalAmount: effectiveTotals?.total_amount,
          paidAmount: paidForStatus,
          discountAmount: financeDiscountForStatus,
          grossSubtotal: financeGrossForStatus,
        });

    if (
      useDraftPaymentFlow &&
      normalizedStatus === "awaiting_payment" &&
      !isApproveFromAwaitingApproval &&
      !isCommissionApprove &&
      !String(clientProfile?.email || "").trim()
    ) {
      Swal.fire(
        "Email required",
        "Client email is required to save as Awaiting Payment and send the invoice.",
        "warning",
      );
      return;
    }

    setShowSaveMenu(false);
    setShowApproveMenu(false);

    // Update saveStatus only from cash payments
    if (paidForStatus > 0) {
      setSaveStatus(normalizedStatus);
    } else if (
      normalizedStatus !== "paid" &&
      (saveStatus === "paid" || saveStatus === "partial")
    ) {
      setSaveStatus(normalizedStatus);
    } else if (
      requested === "draft" ||
      requested === "awaiting_payment" ||
      requested === "awaiting_approval" ||
      (requested === "approved" && !useCommissionDraftLikeFlow)
    ) {
      setSaveStatus(requested);
    }

    const resolvedInvoiceNumber = resolveDraftInvoiceNumber();
    const sharedParams = {
      ...buildSharedSaveParams(normalizedStatus),
      invoiceNumber: resolvedInvoiceNumber,
    };

    const paymentParams =
      draftPaymentTotal > 0
        ? {
            payments: draftPayments,
            receiptFiles: draftPayments.map((item) =>
              Array.isArray(item.receiptFiles) && item.receiptFiles.length
                ? item.receiptFiles
                : item.receiptFile
                  ? [item.receiptFile]
                  : [],
            ),
          }
        : {};

    const combinedPayload = {
      ...sharedParams,
      ...paymentParams,
    };

    const shouldAttachEmail = useWorkflowInvoiceFlow
      ? normalizedStatus === "awaiting_payment" &&
        sendEmail &&
        !openApproveEmailModal
      : normalizedStatus === "awaiting_payment";

    if (openApproveEmailModal && approve) {
      setShowSaveMenu(false);
      setShowApproveMenu(false);
      setOpeningApproveEmailModal(true);

      try {
        const preSaveStatus =
          normalizeInvoiceStatus(existingInvoice?.status) ||
          "awaiting_approval";
        const payloadForPreSave = {
          ...combinedPayload,
          status: ["draft", "awaiting_approval"].includes(preSaveStatus)
            ? preSaveStatus
            : preSaveStatus,
        };

        let invoiceId = existingInvoice?.id || null;

        if (!isEditMode && onCreate) {
          const createResult = await onCreate?.(
            buildInvoiceCreatePayload({
              ...payloadForPreSave,
              sendEmail: false,
            }),
            { approve: false, openApproveEmailModal: true },
          );
          if (
            !createResult ||
            createResult === false ||
            createResult?.success === false
          ) {
            return;
          }
          invoiceId =
            createResult?.invoiceId ||
            createResult?.id ||
            existingInvoice?.id ||
            null;
        }

        if (!invoiceId) {
          return;
        }

        pendingApprovePayloadRef.current = combinedPayload;

        setApproveEmailModal({
          invoiceId,
          clientId:
            clientProfile?.id ||
            existingInvoice?.client_id ||
            existingInvoice?.clientId ||
            null,
          isCommission: suggestionLinesOnly,
          fallbackRecipientEmail: suggestionLinesOnly
            ? ""
            : String(clientProfile?.email || "").trim(),
          fallbackRecipientName: suggestionLinesOnly
            ? String(contactDisplayName || "").trim()
            : String(clientProfile?.fullName || "").trim(),
          fallbackInvoiceNumber: invoiceNumber,
          requiresApprove: true,
        });
      } catch (error) {
        console.error("Failed to open approve email modal", error);
      } finally {
        setOpeningApproveEmailModal(false);
      }
      return;
    }

    if (shouldAttachEmail) {
      combinedPayload.html = buildInvoiceEmailHtml({
        ...previewDraft,
        invoiceNumber: resolvedInvoiceNumber,
        status: normalizedStatus,
        paid_amount: createPaidAmount,
        amount_due: createAmountDue,
      });
      combinedPayload.email = String(billToPartyProfile?.email || "").trim();
      combinedPayload.send_email = true;
    }

    if (isEditMode) {
      if (useWorkflowInvoiceFlow) {
        try {
          const result = await onUpdate?.(
            {
              id: existingInvoice.id,
              payload: buildInvoiceUpdatePayload({
                ...combinedPayload,
                existingInvoice,
              }),
            },
            {
              saveAndAnother,
              sendEmail: shouldAttachEmail,
              approve,
              openApproveEmailModal,
            },
          );

          if (!result || result === false || result?.success === false) {
            return;
          }

          if (saveAndAnother) {
            if (onAfterSaveAndAnother) {
              await onAfterSaveAndAnother();
            }
            return;
          }

          if (result?.stayOnPage) {
            if (approve) {
              setDraftPayments([]);
            }
            setIsEditingDiscount(false);
            setIsCommissionPaymentFlowEditing(false);
            setIsDraftEditing(false);
            return;
          }

          onSaveComplete?.();
        } catch (error) {
          console.error("Failed to save workflow invoice", error);
        }
        return;
      }

      const result = await onUpdate?.({
        id: existingInvoice.id,
        payload: buildInvoiceUpdatePayload({
          ...combinedPayload,
          existingInvoice,
        }),
      });
      if (result !== false) {
        setIsEditingDiscount(false);
        setDraftPayments([]);
        if (useCommissionLockedUntilEditFlow) {
          setIsCommissionPaymentFlowEditing(false);
          setIsDraftEditing(false);
        }
      }
      return;
    }

    try {
      const result = await onCreate?.(
        buildInvoiceCreatePayload({
          ...combinedPayload,
          sendEmail: shouldAttachEmail,
        }),
        {
          saveAndAnother,
          sendEmail: shouldAttachEmail,
          approve,
          openApproveEmailModal,
        },
      );

      if (!result || result === false || result?.success === false) {
        return;
      }

      if (saveAndAnother && !isEditMode) {
        if (onAfterSaveAndAnother) {
          await onAfterSaveAndAnother();
        }
        resetFormForAnotherInvoice();
        return;
      }

      if (result?.stayOnPage) {
        if (approve) {
          setDraftPayments([]);
        }
        return;
      }

      if (useWorkflowInvoiceFlow && !isEditMode) {
        onSaveComplete?.();
      }
    } catch (error) {
      console.error("Failed to save commission invoice", error);
    }
  };

  // const handleSave = async (requestedStatus = saveStatus) => {
  //   if (!validate()) return;

  //   const draftPaymentTotal = sumCreateInvoicePayments(draftPayments);
  //   const requested = normalizeInvoiceStatus(requestedStatus);
  //   const isApproveFromAwaitingApproval =
  //     isAwaitingApprovalInvoice &&
  //     ["awaiting_payment", "paid", "partial"].includes(requested);

  //   const paidForStatus = useDraftPaymentFlow
  //     ? createPaidAmount
  //     : isEditMode
  //       ? resolveInvoicePaidAmount(existingInvoice)
  //       : 0;

  //   // Prefer explicit button status for approval; otherwise derive from
  //   // current lines + discount + payments (full discount → paid at runtime).
  //   const baseForSave =
  //     requested === "awaiting_approval"
  //       ? "awaiting_approval"
  //       : requested === "awaiting_payment" || requested === "draft"
  //         ? requested
  //         : userStatusForDerivation;

  //   const normalizedStatus = suggestionLinesOnly
  //     ? useDraftPaymentFlow && draftPaymentTotal > 0
  //       ? resolveCreateInvoiceStatusFromPayment({
  //           requestedStatus: baseForSave,
  //           totalAmount: effectiveTotals?.total_amount,
  //           paidAmount: paidForStatus,
  //         })
  //       : requested
  //     : resolveCreateInvoiceStatusFromPayment({
  //         requestedStatus: baseForSave,
  //         totalAmount: effectiveTotals?.total_amount,
  //         paidAmount: paidForStatus,
  //         discountAmount: financeDiscountForStatus,
  //         grossSubtotal: financeGrossForStatus,
  //       });

  //   if (
  //     useDraftPaymentFlow &&
  //     normalizedStatus === "awaiting_payment" &&
  //     !isApproveFromAwaitingApproval &&
  //     !String(clientProfile?.email || "").trim()
  //   ) {
  //     Swal.fire(
  //       "Email required",
  //       "Client email is required to save as Awaiting Payment and send the invoice.",
  //       "warning",
  //     );
  //     return;
  //   }

  //   setShowSaveMenu(false);
  //   // Do not stick auto-paid from discount into the user's selected status.
  //   // Only update saveStatus from cash payment outcomes.
  //   if (paidForStatus > 0) {
  //     setSaveStatus(normalizedStatus);
  //   } else if (
  //     normalizedStatus !== "paid" &&
  //     (saveStatus === "paid" || saveStatus === "partial")
  //   ) {
  //     setSaveStatus(normalizedStatus);
  //   } else if (
  //     requested === "draft" ||
  //     requested === "awaiting_payment" ||
  //     requested === "awaiting_approval"
  //   ) {
  //     setSaveStatus(requested);
  //   }

  //   const resolvedInvoiceNumber = resolveDraftInvoiceNumber();
  //   const sharedParams = {
  //     ...buildSharedSaveParams(normalizedStatus),
  //     invoiceNumber: resolvedInvoiceNumber,
  //   };

  //   // const paymentParams =
  //   //   draftPaymentTotal > 0
  //   //     ? {
  //   //         payments: draftPayments,
  //   //         receiptFile:
  //   //           draftPayments.find((item) => item.receiptFile)?.receiptFile ||
  //   //           null,
  //   //       }
  //   //     : {};

  //   const paymentParams =
  //     draftPaymentTotal > 0
  //       ? {
  //           payments: draftPayments,
  //           receiptFiles: draftPayments
  //             .map((item) => item.receiptFile)
  //             .filter(Boolean),
  //         }
  //       : {};

  //   if (
  //     normalizedStatus === "awaiting_payment" &&
  //     !isApproveFromAwaitingApproval
  //   ) {
  //     sharedParams.html = buildInvoiceEmailHtml({
  //       ...previewDraft,
  //       invoiceNumber: resolvedInvoiceNumber,
  //       status: normalizedStatus,
  //       paid_amount: createPaidAmount,
  //       amount_due: createAmountDue,
  //     });
  //     sharedParams.email = String(clientProfile?.email || "").trim();
  //   }

  //   if (isEditMode) {
  //     const result = await onUpdate?.({
  //       id: existingInvoice.id,
  //       payload: buildInvoiceUpdatePayload({
  //         ...sharedParams,
  //         ...paymentParams,
  //         existingInvoice,
  //       }),
  //     });
  //     if (result !== false) {
  //       setIsEditingDiscount(false);
  //       setDraftPayments([]);
  //     }
  //     return;
  //   }

  //   await onCreate?.(
  //     buildInvoiceCreatePayload({
  //       ...sharedParams,
  //       ...paymentParams,
  //     }),
  //   );
  // };

  const handleAddDraftPayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
  ) => {
    const amount = Number(payment?.amount) || 0;
    if (amount <= 0) return false;
    const maxAllowed = suggestionLinesOnly
      ? commissionPaymentCap
      : paymentHeadroom;
    if (amount > maxAllowed + 0.0001) {
      Swal.fire(
        "Invalid amount",
        "Payment amount cannot exceed the remaining balance / invoice total.",
        "warning",
      );
      return false;
    }

    const files = (
      Array.isArray(receiptFiles) && receiptFiles.length
        ? receiptFiles
        : receiptFile
          ? [receiptFile]
          : []
    )
      .filter(Boolean)
      .filter(isAllowedReceiptFile);
    const receiptPreviews = files.map((file) => buildReceiptPreviewUrl(file));

    const paymentTarget = payment?.line_number
      ? {
          line_number: Number(payment.line_number),
          finance_invoice_item_id: payment.finance_invoice_item_id ?? null,
          commission_installment_id: payment.commission_installment_id ?? null,
          bonus_installment_id: payment.bonus_installment_id ?? null,
        }
      : suggestionLinesOnly
        ? resolveCommissionPaymentTargetForIndex(
            lineTotals?.lineItems || [],
            draftPayments.length,
          )
        : {};

    const nextId = `draft-${draftPaymentIdRef.current++}`;
    setDraftPayments((prev) => [
      ...prev,
      {
        id: nextId,
        amount,
        transaction_date: payment.transaction_date,
        payment_method: payment.payment_method || "cash",
        reference: payment.reference || "",
        description: payment.description || "Payment received",
        transaction_type: "payment",
        receiptFile: files[0] || null,
        receiptFiles: files,
        receiptPreviews,
        ...paymentTarget,
      },
    ]);
    return true;
  };

  const handleRemoveDraftPayment = (record) => {
    setDraftPayments((prev) => {
      const target = prev.find((item) => item.id === record?.id);
      if (target?.receiptPreview) {
        URL.revokeObjectURL(target.receiptPreview);
      }
      (target?.receiptPreviews || []).forEach((preview) => {
        if (preview) URL.revokeObjectURL(preview);
      });
      const next = prev.filter((item) => item.id !== record?.id);
      if (next.length === 0) {
        setSaveStatus(DEFAULT_INVOICE_STATUS);
      }
      return next;
    });
  };

  const selectClass = (field) =>
    `invoice-xero-control invoice-xero-control--select ${
      errors[field] ? "is-invalid" : ""
    }`;

  const clientInitials = getClientInitials(billToPartyProfile.fullName);
  const brandingDocumentTitle = resolveInvoiceDisplayTitle({
    branding: selectedBranding,
    documentType,
  });
  const pageTitle = useCommissionCreateFlow
    ? "New invoice"
    : useCommissionDraftLikeFlow
      ? "Edit invoice"
      : isEditMode
        ? isXeroPageMode
          ? "Edit invoice"
          : `Edit invoice ${invoiceNumber || ""}`.trim()
        : "New invoice";
  const pageSubtitle = isEditMode
    ? selectedBranding?.name ||
      existingInvoice?.branding_snapshot?.name ||
      "Edit invoice details"
    : brandingDocumentTitle && documentType
      ? brandingDocumentTitle
      : selectedBranding?.name ||
        (selectedType?.label
          ? `Select branding for ${selectedType.label}`
          : "");

  const draftPaymentTotalForUi = sumCreateInvoicePayments(draftPayments);

  const wizardContent = (
    <div
      className={`modal-content border-0 overflow-hidden${
        pageMode || isEditMode
          ? " invoice-wizard-content--edit shadow-sm"
          : " shadow-lg rounded-3"
      }`}
    >
      <div
        className={`invoice-wizard-header invoice-wizard-header--xero invoice-wizard-header--edit${
          isXeroPageMode ? " invoice-wizard-header--xero-create" : ""
        }`}
      >
        <div className="invoice-wizard-header__main">
          {isEditMode && !isXeroPageMode && (
            <div className="invoice-wizard-header__breadcrumb">
              Sales overview &rsaquo; Invoices
            </div>
          )}
          <div className="invoice-wizard-header__title-row">
            <h5 className="mb-0 fw-bold invoice-wizard-header__title">
              {pageTitle}
            </h5>
            <div className="invoice-wizard-header__status-wrap">
              {isEditMode ? (
                <span
                  className={`invoice-wizard-draft-badge ${getInvoiceStatusBadgeClass(
                    useWorkflowInvoiceFlow
                      ? uiWorkflowStatus
                      : invoiceWorkflowStatus,
                  )}`}
                >
                  {getInvoiceStatusLabel(
                    useWorkflowInvoiceFlow
                      ? uiWorkflowStatus
                      : invoiceWorkflowStatus,
                  )}
                </span>
              ) : isXeroPageMode ? (
                <span
                  className={`invoice-wizard-draft-badge ${getInvoiceStatusBadgeClass(derivedInvoiceStatus)}`}
                >
                  {getInvoiceStatusLabel(derivedInvoiceStatus)}
                </span>
              ) : (
                <select
                  id="invoice-create-status"
                  className={`invoice-wizard-status-select ${getInvoiceStatusBadgeClass(saveStatus)}`}
                  value={saveStatus}
                  onChange={(e) => setSaveStatus(e.target.value)}
                  disabled={saving}
                  aria-label="Invoice status"
                >
                  {statusOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {pageSubtitle && !isXeroPageMode ? (
            <div className="invoice-wizard-header__subtitle text-muted small mt-1">
              {pageSubtitle}
            </div>
          ) : null}
        </div>
        <div className="invoice-wizard-header__actions">
          {!isInvoiceFieldEditSessionActive ? (
            <button
              type="button"
              className={
                isXeroPageMode
                  ? "invoice-xero-header-btn invoice-xero-header-btn--preview"
                  : `btn btn-sm invoice-wizard-header-btn ${
                      isEditMode ? "btn-outline-secondary" : "btn-light"
                    }`
              }
              onClick={handlePreview}
              disabled={loadingOptions || loadingInvoice || pageLoading}
            >
              {(isEditMode || isXeroPageMode) && (
                <FaEye className="me-1" size={12} />
              )}
              Preview
            </button>
          ) : null}
          {useCommissionPrintPdfFlow ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--preview"
              onClick={handlePrintPdf}
              disabled={loadingOptions || loadingInvoice || pageLoading}
            >
              <FaPrint className="me-1" size={12} />
              Print PDF
            </button>
          ) : null}
          {showInvoiceSendEmailButton ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--preview"
              onClick={handleOpenSendEmailModal}
              disabled={
                loadingOptions ||
                loadingInvoice ||
                pageLoading ||
                openingApproveEmailModal
              }
            >
              <FaPaperPlane className="me-1" size={12} />
              Send email
            </button>
          ) : null}
          {showCommissionAwaitingPaymentActionsMenu ? (
            <div
              className="invoice-xero-save-split"
              ref={commissionActionsMenuRef}
            >
              <button
                type="button"
                className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
                onClick={() => setShowCommissionActionsMenu((prev) => !prev)}
                disabled={
                  loadingOptions ||
                  loadingInvoice ||
                  pageLoading ||
                  invoiceAwaitingPaymentActionInProgress
                }
                aria-haspopup="menu"
                aria-expanded={showCommissionActionsMenu}
                aria-label="Invoice actions"
                title="Invoice actions"
              >
                <FaEllipsisH size={14} />
              </button>
              {showCommissionActionsMenu ? (
                <ul className="invoice-xero-save-split__menu" role="menu">
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="invoice-xero-save-split__menu-item"
                      onClick={() => {
                        setShowCommissionActionsMenu(false);
                        beginInvoiceFieldEdit("commission");
                      }}
                      disabled={
                        !canClickWorkflowEdit ||
                        loadingOptions ||
                        loadingInvoice ||
                        pageLoading ||
                        invoiceAwaitingPaymentActionInProgress
                      }
                    >
                      <FaPen className="me-2" size={11} />
                      Edit
                    </button>
                  </li>
                  {onMoveInvoiceToDraft ? (
                    <li role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="invoice-xero-save-split__menu-item"
                        onClick={async () => {
                          setShowCommissionActionsMenu(false);
                          if (
                            !onMoveInvoiceToDraft ||
                            invoiceAwaitingPaymentActionInProgress
                          ) {
                            return;
                          }
                          const success = await onMoveInvoiceToDraft();
                          if (success) {
                            setIsCommissionPaymentFlowEditing(false);
                            setIsDraftEditing(false);
                            setShowCommissionActionsMenu(false);
                            setSaveStatus("draft");
                          }
                        }}
                        disabled={
                          invoiceAwaitingPaymentActionInProgress ||
                          loadingOptions ||
                          loadingInvoice ||
                          pageLoading
                        }
                      >
                        <FaBookmark className="me-2" size={11} />
                        {movingInvoiceToDraft ? "Moving…" : "Move to draft"}
                      </button>
                    </li>
                  ) : null}
                  {onVoidInvoice ? (
                    <li role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="invoice-xero-save-split__menu-item"
                        onClick={async () => {
                          setShowCommissionActionsMenu(false);
                          if (
                            !onVoidInvoice ||
                            invoiceAwaitingPaymentActionInProgress
                          ) {
                            return;
                          }
                          const success = await onVoidInvoice();
                          if (success) {
                            setIsCommissionPaymentFlowEditing(false);
                          }
                        }}
                        disabled={
                          invoiceAwaitingPaymentActionInProgress ||
                          loadingOptions ||
                          loadingInvoice ||
                          pageLoading
                        }
                      >
                        <FaBan className="me-2" size={11} />
                        {voidingInvoice ? "Voiding…" : "Void"}
                      </button>
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}
          {showCommissionStandardEditButton ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
              onClick={() => beginInvoiceFieldEdit("commission")}
              disabled={
                !canClickWorkflowEdit ||
                loadingOptions ||
                loadingInvoice ||
                pageLoading
              }
              title={
                canClickWorkflowEdit
                  ? "Edit invoice details"
                  : "You do not have permission to edit this invoice"
              }
            >
              <FaPen className="me-1" size={12} />
              Edit
            </button>
          ) : null}
          {useCommissionLockedUntilEditFlow &&
          isCommissionPaymentFlowEditing ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
              onClick={() => {
                setIsCommissionPaymentFlowEditing(false);
                setSaveStatus(
                  normalizeInvoiceStatus(
                    existingInvoice?.status || DEFAULT_INVOICE_STATUS,
                  ),
                );
              }}
              disabled={
                saving || loadingOptions || loadingInvoice || pageLoading
              }
            >
              Cancel edit
            </button>
          ) : null}
          {useFinanceFieldEditUnlockFlow && !isDraftEditing ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
              onClick={() => beginInvoiceFieldEdit("draft")}
              disabled={
                !canClickWorkflowEdit ||
                loadingOptions ||
                loadingInvoice ||
                pageLoading
              }
              title={
                canClickWorkflowEdit
                  ? "Edit invoice details"
                  : "You do not have permission to edit this invoice"
              }
            >
              <FaPen className="me-1" size={12} />
              Edit
            </button>
          ) : null}
          {useFinanceFieldEditUnlockFlow && isDraftEditing ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
              onClick={() => setIsDraftEditing(false)}
              disabled={
                saving || loadingOptions || loadingInvoice || pageLoading
              }
            >
              Cancel edit
            </button>
          ) : null}
          {useCommissionLockedUntilEditFlow &&
          isCommissionPaymentFlowEditing ? (
            <button
              type="button"
              className="invoice-xero-header-btn invoice-xero-header-btn--primary"
              onClick={() =>
                handleSave(
                  isAwaitingPaymentInvoice
                    ? "awaiting_payment"
                    : invoiceWorkflowStatus,
                )
              }
              disabled={
                saving || loadingOptions || loadingInvoice || softLoading
              }
            >
              {saving ? "Updating…" : "Update"}
            </button>
          ) : null}
          {isXeroPageMode ? (
            useCommissionDraftLikeFlow ? (
              <div className="invoice-xero-header-save-group">
                <div className="invoice-xero-save-split" ref={saveMenuRef}>
                  <button
                    type="button"
                    className="invoice-xero-header-btn invoice-xero-header-btn--secondary invoice-xero-save-split__main"
                    onClick={() => handleSave(commissionDraftLikeCloseStatus)}
                    disabled={
                      saving || loadingOptions || loadingInvoice || softLoading
                    }
                  >
                    {saving ? "Saving…" : "Save & close"}
                  </button>
                  <button
                    type="button"
                    className="invoice-xero-header-btn invoice-xero-header-btn--secondary invoice-xero-save-split__toggle"
                    onClick={() => {
                      setShowApproveMenu(false);
                      setShowSaveMenu((prev) => !prev);
                    }}
                    disabled={
                      saving || loadingOptions || loadingInvoice || softLoading
                    }
                    aria-haspopup="menu"
                    aria-expanded={showSaveMenu}
                    aria-label="More save options"
                  >
                    <FaChevronDown size={10} />
                  </button>
                  {showSaveMenu ? (
                    <ul className="invoice-xero-save-split__menu" role="menu">
                      {commissionDraftLikeSaveMenuOptions.map((option) => (
                        <li key={option.value} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            className="invoice-xero-save-split__menu-item"
                            onClick={() =>
                              handleSave(
                                option.status || commissionDraftLikeCloseStatus,
                                {
                                  saveAndAnother: Boolean(
                                    option.saveAndAnother,
                                  ),
                                },
                              )
                            }
                          >
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <div className="invoice-xero-save-split" ref={approveMenuRef}>
                  <button
                    type="button"
                    className="invoice-xero-header-btn invoice-xero-header-btn--primary invoice-xero-save-split__main"
                    onClick={() =>
                      handleSave("awaiting_payment", {
                        approve: true,
                        openApproveEmailModal: true,
                      })
                    }
                    disabled={
                      saving ||
                      openingApproveEmailModal ||
                      loadingOptions ||
                      loadingInvoice ||
                      softLoading
                    }
                  >
                    {saving || openingApproveEmailModal
                      ? "Saving…"
                      : "Approve & email"}
                  </button>
                  <button
                    type="button"
                    className="invoice-xero-header-btn invoice-xero-header-btn--primary invoice-xero-save-split__toggle"
                    onClick={() => {
                      setShowSaveMenu(false);
                      setShowApproveMenu((prev) => !prev);
                    }}
                    disabled={
                      saving || loadingOptions || loadingInvoice || softLoading
                    }
                    aria-haspopup="menu"
                    aria-expanded={showApproveMenu}
                    aria-label="More approve options"
                  >
                    <FaChevronDown size={10} />
                  </button>
                  {showApproveMenu ? (
                    <ul className="invoice-xero-save-split__menu" role="menu">
                      {COMMISSION_CREATE_APPROVE_MENU_OPTIONS.map((option) => (
                        <li key={option.value} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            className="invoice-xero-save-split__menu-item"
                            onClick={() =>
                              handleSave(option.status || "awaiting_payment", {
                                saveAndAnother: Boolean(option.saveAndAnother),
                                sendEmail: false,
                                approve: true,
                              })
                            }
                          >
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : useCommissionPaymentViewFlow ||
              useCommissionPaidFlow ||
              useCommissionApprovedWithPaymentPanel ? null : isEditMode ? (
              supportsPaymentSaveButtons && draftPaymentTotalForUi > 0 ? (
                <div className="invoice-xero-save-split invoice-xero-save-split--with-payment">
                  <button
                    type="button"
                    className="invoice-xero-header-btn invoice-xero-header-btn--primary"
                    onClick={() =>
                      handleSave(
                        resolveCreateInvoiceStatusFromPayment({
                          requestedStatus: "awaiting_payment",
                          totalAmount: effectiveTotals?.total_amount,
                          paidAmount: createPaidAmount,
                        }),
                      )
                    }
                    disabled={
                      saving || loadingOptions || loadingInvoice || softLoading
                    }
                  >
                    {saving
                      ? "Saving…"
                      : `Save as ${getInvoiceStatusLabel(
                          resolveCreateInvoiceStatusFromPayment({
                            requestedStatus: "awaiting_payment",
                            totalAmount: effectiveTotals?.total_amount,
                            paidAmount: createPaidAmount,
                          }),
                        )}`}
                  </button>
                  {isAwaitingApprovalInvoice && canShowApproveButton ? (
                    <button
                      type="button"
                      className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
                      onClick={() =>
                        handleSave(
                          resolveAwaitingApprovalActionStatus({
                            totalAmount: effectiveTotals?.total_amount,
                            paidAmount: createPaidAmount,
                          }),
                          { approve: true },
                        )
                      }
                      disabled={
                        saving ||
                        loadingOptions ||
                        loadingInvoice ||
                        softLoading
                      }
                    >
                      Approve
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
                      onClick={() => handleSave("awaiting_approval")}
                      disabled={
                        saving ||
                        loadingOptions ||
                        loadingInvoice ||
                        softLoading
                      }
                    >
                      Save & Awaiting Approval
                    </button>
                  )}
                </div>
              ) : supportsPaymentSaveButtons ? (
                <div className="invoice-xero-save-split invoice-xero-save-split--with-payment">
                  <div className="invoice-xero-save-split" ref={saveMenuRef}>
                    <button
                      type="button"
                      className={`invoice-xero-header-btn invoice-xero-header-btn--primary${
                        showWorkflowSaveChevron
                          ? " invoice-xero-save-split__main"
                          : ""
                      }`}
                      onClick={() =>
                        handleSave(
                          isDraftInvoice ? "draft" : invoiceWorkflowStatus,
                        )
                      }
                      disabled={
                        saving ||
                        loadingOptions ||
                        loadingInvoice ||
                        softLoading
                      }
                    >
                      {saving
                        ? "Saving…"
                        : isDraftInvoice
                          ? "Save & close"
                          : "Update & close"}
                    </button>
                    {showWorkflowSaveChevron ? (
                      <>
                        <button
                          type="button"
                          className="invoice-xero-header-btn invoice-xero-header-btn--primary invoice-xero-save-split__toggle"
                          onClick={() => setShowSaveMenu((prev) => !prev)}
                          disabled={
                            saving ||
                            loadingOptions ||
                            loadingInvoice ||
                            softLoading
                          }
                          aria-haspopup="menu"
                          aria-expanded={showSaveMenu}
                          aria-label="More save options"
                        >
                          <FaChevronDown size={10} />
                        </button>
                        {showSaveMenu ? (
                          <ul
                            className="invoice-xero-save-split__menu"
                            role="menu"
                          >
                            {showWorkflowEditMenuItem ? (
                              <li role="none">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className={`invoice-xero-save-split__menu-item${
                                    canClickWorkflowEdit ? "" : " is-disabled"
                                  }`}
                                  aria-disabled={!canClickWorkflowEdit}
                                  disabled={!canClickWorkflowEdit}
                                  title={
                                    canClickWorkflowEdit
                                      ? "Edit invoice details"
                                      : "You do not have permission to edit this invoice"
                                  }
                                  onClick={() => {
                                    if (!canClickWorkflowEdit) return;
                                    beginInvoiceFieldEdit("draft");
                                    setShowSaveMenu(false);
                                  }}
                                >
                                  Edit
                                </button>
                              </li>
                            ) : null}
                            {workflowSaveMenuOptions.map((option) => (
                              <li key={option.value} role="none">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="invoice-xero-save-split__menu-item"
                                  onClick={() => handleSave(option.value)}
                                >
                                  {option.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                  {canShowApproveButton ? (
                    <button
                      type="button"
                      className="invoice-xero-header-btn invoice-xero-header-btn--primary"
                      onClick={() =>
                        handleSave(
                          resolveAwaitingApprovalActionStatus({
                            totalAmount: effectiveTotals?.total_amount,
                            paidAmount: createPaidAmount,
                          }),
                          { approve: true },
                        )
                      }
                      disabled={
                        saving ||
                        loadingOptions ||
                        loadingInvoice ||
                        softLoading
                      }
                    >
                      Approve
                    </button>
                  ) : null}
                </div>
              ) : canShowApproveButton ? (
                <button
                  type="button"
                  className="invoice-xero-header-btn invoice-xero-header-btn--primary"
                  onClick={() =>
                    handleSave(
                      resolveAwaitingApprovalActionStatus({
                        totalAmount: effectiveTotals?.total_amount,
                        paidAmount: createPaidAmount,
                      }),
                      { approve: true },
                    )
                  }
                  disabled={
                    saving || loadingOptions || loadingInvoice || softLoading
                  }
                >
                  Approve
                </button>
              ) : null
            ) : createPaidAmount > 0 ? (
              <div className="invoice-xero-save-split invoice-xero-save-split--with-payment">
                <button
                  type="button"
                  className="invoice-xero-header-btn invoice-xero-header-btn--primary"
                  onClick={() =>
                    handleSave(
                      resolveCreateInvoiceStatusFromPayment({
                        requestedStatus: "awaiting_payment",
                        totalAmount: effectiveTotals?.total_amount,
                        paidAmount: createPaidAmount,
                      }),
                    )
                  }
                  disabled={
                    saving || loadingOptions || loadingInvoice || softLoading
                  }
                >
                  {saving
                    ? "Saving…"
                    : `Save as ${getInvoiceStatusLabel(
                        resolveCreateInvoiceStatusFromPayment({
                          requestedStatus: "awaiting_payment",
                          totalAmount: effectiveTotals?.total_amount,
                          paidAmount: createPaidAmount,
                        }),
                      )}`}
                </button>
                <button
                  type="button"
                  className="invoice-xero-header-btn invoice-xero-header-btn--secondary"
                  onClick={() => handleSave("awaiting_approval")}
                  disabled={
                    saving || loadingOptions || loadingInvoice || softLoading
                  }
                >
                  Save & Awaiting Approval
                </button>
              </div>
            ) : (
              <div className="invoice-xero-save-split" ref={saveMenuRef}>
                <button
                  type="button"
                  className={`invoice-xero-header-btn invoice-xero-header-btn--primary${
                    workflowSaveMenuOptions.length
                      ? " invoice-xero-save-split__main"
                      : ""
                  }`}
                  onClick={() => handleSave("draft")}
                  disabled={
                    saving || loadingOptions || loadingInvoice || softLoading
                  }
                >
                  {saving ? "Saving…" : "Save & close"}
                </button>
                {workflowSaveMenuOptions.length > 0 ? (
                  <>
                    <button
                      type="button"
                      className="invoice-xero-header-btn invoice-xero-header-btn--primary invoice-xero-save-split__toggle"
                      onClick={() => setShowSaveMenu((prev) => !prev)}
                      disabled={
                        saving ||
                        loadingOptions ||
                        loadingInvoice ||
                        softLoading
                      }
                      aria-haspopup="menu"
                      aria-expanded={showSaveMenu}
                      aria-label="More save options"
                    >
                      <FaChevronDown size={10} />
                    </button>
                    {showSaveMenu ? (
                      <ul className="invoice-xero-save-split__menu" role="menu">
                        {workflowSaveMenuOptions.map((option) => (
                          <li key={option.value} role="none">
                            <button
                              type="button"
                              role="menuitem"
                              className="invoice-xero-save-split__menu-item"
                              onClick={() => handleSave(option.value)}
                            >
                              {option.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                ) : null}
              </div>
            )
          ) : null}
          {isEditMode && !isXeroPageMode && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm invoice-wizard-header-btn invoice-wizard-header-btn--update"
                onClick={() => handleSave(saveStatus)}
                disabled={
                  saving || loadingOptions || loadingInvoice || softLoading
                }
              >
                {saving ? "Updating…" : "Update"}
              </button>
            </>
          )}
          {!isXeroPageMode ? (
            <button
              type="button"
              className="btn-close ms-1"
              onClick={onClose}
              disabled={saving}
            />
          ) : null}
        </div>
      </div>

      <div
        className={`modal-body invoice-wizard-body invoice-wizard-body--xero${
          isEditMode || pageMode ? " invoice-wizard-body--edit" : ""
        }`}
      >
        {showInitialLoader ? (
          <div className="client-create-invoice-page__loading py-5 text-center">
            <Loader />
            <div className="text-muted small mt-2">Loading invoice form…</div>
          </div>
        ) : (
          <>
            {isInvoiceLocked && !isVoidInvoice ? (
              <div className="invoice-xero-locked-note" role="status">
                This invoice is <strong>{lockedStatusLabel}</strong>. Invoice
                details are locked — you can only add notes.
              </div>
            ) : null}
            {loadingOptions && isEditMode ? (
              <div className="invoice-xero-locked-note" role="status">
                Loading invoice options…
              </div>
            ) : null}
            {catalogError ? (
              <div className="invoice-xero-locked-note" role="alert">
                {catalogError}{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => loadCatalog({ force: true })}
                  disabled={loadingOptions}
                >
                  Retry
                </button>
              </div>
            ) : null}
            <div
              className={`invoice-xero-locked-region${
                isCommissionLineItemsReadOnly ? " is-locked" : ""
              }`}
            >
              <div className="invoice-xero-form-card">
                <fieldset
                  disabled={isHeaderFormReadOnly}
                  className="invoice-xero-fields-fieldset"
                >
                  {isXeroPageMode ? (
                    <>
                      <div className="invoice-xero-toolbar invoice-xero-toolbar--xero-create-row1">
                        <XeroIconField
                          label="Contact"
                          icon={FaUser}
                          className="invoice-xero-field--contact"
                        >
                          {enableContactSearch ? (
                            <div className="invoice-xero-desc-suggest">
                              <input
                                type="text"
                                className="invoice-xero-icon-control__input"
                                placeholder="Type to search client or agency..."
                                value={contactSearchValue}
                                onChange={(e) =>
                                  onContactSearchChange?.(e.target.value)
                                }
                                onFocus={() => onContactSearchFocus?.()}
                                onBlur={() => onContactSearchBlur?.()}
                              />
                              {contactSearchResults?.length > 0 ||
                              contactSearchLoading ? (
                                <ul
                                  className="invoice-xero-desc-suggest__list"
                                  role="listbox"
                                >
                                  {contactSearchLoading ? (
                                    <li
                                      className="invoice-xero-desc-suggest__option"
                                      aria-disabled
                                    >
                                      Searching...
                                    </li>
                                  ) : (
                                    contactSearchResults.map((item) => {
                                      const isAgency =
                                        item?.kind &&
                                        item.kind !== "client";
                                      const agencyRoleLabel =
                                        item?.kind === "both"
                                          ? "Agency (Referring & Processing)"
                                          : item?.kind === "referring"
                                            ? "Agency (Referring)"
                                            : item?.kind === "processing"
                                              ? "Agency (Processing)"
                                              : "Agency";

                                      return (
                                        <li
                                          key={String(
                                            item.key || item.id || item.label,
                                          )}
                                        >
                                          <button
                                            type="button"
                                            className="invoice-xero-desc-suggest__option"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              onContactSearchSelect?.(item);
                                            }}
                                          >
                                            <span>{item.label}</span>
                                            {isAgency ? (
                                              <span
                                                className="invoice-xero-desc-suggest__meta"
                                                style={{
                                                  display: "block",
                                                  fontSize: "0.85em",
                                                  opacity: 0.75,
                                                }}
                                              >
                                                {agencyRoleLabel}
                                              </span>
                                            ) : item.email ? (
                                              <span
                                                className="invoice-xero-desc-suggest__meta"
                                                style={{
                                                  display: "block",
                                                  fontSize: "0.85em",
                                                  opacity: 0.75,
                                                }}
                                              >
                                                {item.email}
                                              </span>
                                            ) : null}
                                          </button>
                                        </li>
                                      );
                                    })
                                  )}
                                </ul>
                              ) : null}
                            </div>
                          ) : suggestionLinesOnly ? (
                            <div className="invoice-xero-contact-trigger-wrap">
                              <button
                                type="button"
                                ref={commissionContactAnchorRef}
                                className="invoice-xero-contact-trigger"
                                onClick={() =>
                                  setCommissionBillToPopoverOpen((open) => !open)
                                }
                                aria-expanded={commissionBillToPopoverOpen}
                              >
                                <span className="invoice-xero-contact__name invoice-xero-contact__name--field">
                                  {billToPartyProfile.fullName || ""}
                                </span>
                              </button>
                              <CommissionBillToContactPopover
                                open={commissionBillToPopoverOpen}
                                anchorRef={commissionContactAnchorRef}
                                onClose={() =>
                                  setCommissionBillToPopoverOpen(false)
                                }
                                providerId={commissionProviderId}
                                profile={billToPartyProfile}
                                printOptions={commissionBillToPrintOptions}
                                onPrintOptionsChange={
                                  handleCommissionBillToPrintOptionsChange
                                }
                                savingPrintOptions={savingBillToPrintOptions}
                                onBillingAddressSaved={
                                  handleSaveProviderBillingAddress
                                }
                                savingAddress={savingProviderBillingAddress}
                                printOptionsReadOnly={
                                  isCommissionLineItemsReadOnly
                                }
                                addressReadOnly={isHeaderFormReadOnly}
                              />
                            </div>
                          ) : (
                            <span className="invoice-xero-contact__name invoice-xero-contact__name--field">
                              {billToPartyProfile.fullName || ""}
                            </span>
                          )}
                        </XeroIconField>

                        <XeroIconField
                          label="Issue date"
                          required
                          error={errors.issueDate}
                          icon={FaCalendarAlt}
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 32, width: "100%" }}
                            />
                          ) : (
                            <input
                              type="date"
                              className={`invoice-xero-icon-control__input${
                                errors.issueDate ? " is-invalid" : ""
                              }`}
                              value={issueDate}
                              onChange={(e) =>
                                handleIssueDateChange(e.target.value)
                              }
                            />
                          )}
                        </XeroIconField>

                        <XeroIconField
                          label="Due date"
                          error={errors.dueDate}
                          icon={FaCalendarAlt}
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 32, width: "100%" }}
                            />
                          ) : (
                            <input
                              type="date"
                              className={`invoice-xero-icon-control__input${
                                errors.dueDate ? " is-invalid" : ""
                              }`}
                              value={dueDate}
                              min={issueDate || undefined}
                              onChange={(e) =>
                                handleDueDateChange(e.target.value)
                              }
                            />
                          )}
                        </XeroIconField>

                        <XeroIconField label="Invoice number" icon={FaHashtag}>
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 18, width: "68%" }}
                            />
                          ) : (
                            <span className="invoice-xero-icon-control__text">
                              {isLoadingNextInvoiceNumber
                                ? "Loading..."
                                : invoiceNumber || "—"}
                            </span>
                          )}
                        </XeroIconField>

                        <XeroIconField label="Reference" icon={FaBookmark}>
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 32, width: "100%" }}
                            />
                          ) : (
                            <input
                              type="text"
                              className="invoice-xero-icon-control__input"
                              value={reference}
                              onChange={(e) => setReference(e.target.value)}
                              placeholder="Reference"
                            />
                          )}
                        </XeroIconField>
                      </div>

                      {enableAgentSelection ? (
                        <div
                          className="invoice-xero-toolbar invoice-xero-toolbar--xero-create-agents"
                        >
                          <XeroIconField
                            label="Referring agency"
                            icon={FaUser}
                            className="invoice-xero-field--agent"
                          >
                            {softLoading ? (
                              <span
                                className="invoice-hub-skeleton__bone"
                                style={{ height: 32, width: "100%" }}
                              />
                            ) : agentSearchMode ? (
                              <div className="invoice-xero-desc-suggest">
                                <input
                                  type="text"
                                  className="invoice-xero-icon-control__input"
                                  placeholder="Type to search agency..."
                                  value={referringAgentSearchValue}
                                  onChange={(e) =>
                                    onReferringAgentSearchChange?.(
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() => onReferringAgentSearchFocus?.()}
                                  onBlur={() => onReferringAgentSearchBlur?.()}
                                />
                                {referringAgentSearchOpen &&
                                referringAgentSearchValue.trim().length >= 2 ? (
                                  <ul
                                    className="invoice-xero-desc-suggest__list"
                                    role="listbox"
                                  >
                                    {referringAgentSearchLoading ? (
                                      <li
                                        className="invoice-xero-desc-suggest__option"
                                        aria-disabled
                                      >
                                        Searching...
                                      </li>
                                    ) : referringAgentSearchResults.length ? (
                                      referringAgentSearchResults.map(
                                        (item) => (
                                          <li key={String(item.id || item.label)}>
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className="invoice-xero-desc-suggest__option"
                                              onMouseDown={(event) => {
                                                event.preventDefault();
                                                onReferringAgentSearchSelect?.(
                                                  item,
                                                );
                                              }}
                                            >
                                              {item.label}
                                            </button>
                                          </li>
                                        ),
                                      )
                                    ) : (
                                      <li
                                        className="invoice-xero-desc-suggest__option"
                                        aria-disabled
                                      >
                                        No agencies found
                                      </li>
                                    )}
                                  </ul>
                                ) : null}
                              </div>
                            ) : (
                              <select
                                className="invoice-xero-icon-control__select"
                                value={selectedReferringAgentId}
                                onChange={(e) =>
                                  onReferringAgentChange?.(e.target.value)
                                }
                              >
                                <option value="">Please select</option>
                                {referringAgentOptions.map((agent) => (
                                  <option
                                    key={String(agent.id)}
                                    value={String(agent.id)}
                                  >
                                    {agent.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </XeroIconField>

                          <XeroIconField
                            label="Processing agency"
                            icon={FaUser}
                            className="invoice-xero-field--agent"
                          >
                            {softLoading ? (
                              <span
                                className="invoice-hub-skeleton__bone"
                                style={{ height: 32, width: "100%" }}
                              />
                            ) : agentSearchMode ? (
                              <div className="invoice-xero-desc-suggest">
                                <input
                                  type="text"
                                  className="invoice-xero-icon-control__input"
                                  placeholder="Type to search agency..."
                                  value={processingAgentSearchValue}
                                  onChange={(e) =>
                                    onProcessingAgentSearchChange?.(
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() =>
                                    onProcessingAgentSearchFocus?.()
                                  }
                                  onBlur={() => onProcessingAgentSearchBlur?.()}
                                />
                                {processingAgentSearchOpen &&
                                processingAgentSearchValue.trim().length >= 2 ? (
                                  <ul
                                    className="invoice-xero-desc-suggest__list"
                                    role="listbox"
                                  >
                                    {processingAgentSearchLoading ? (
                                      <li
                                        className="invoice-xero-desc-suggest__option"
                                        aria-disabled
                                      >
                                        Searching...
                                      </li>
                                    ) : processingAgentSearchResults.length ? (
                                      processingAgentSearchResults.map(
                                        (item) => (
                                          <li key={String(item.id || item.label)}>
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className="invoice-xero-desc-suggest__option"
                                              onMouseDown={(event) => {
                                                event.preventDefault();
                                                onProcessingAgentSearchSelect?.(
                                                  item,
                                                );
                                              }}
                                            >
                                              {item.label}
                                            </button>
                                          </li>
                                        ),
                                      )
                                    ) : (
                                      <li
                                        className="invoice-xero-desc-suggest__option"
                                        aria-disabled
                                      >
                                        No agencies found
                                      </li>
                                    )}
                                  </ul>
                                ) : null}
                              </div>
                            ) : (
                              <select
                                className="invoice-xero-icon-control__select"
                                value={selectedProcessingAgentId}
                                onChange={(e) =>
                                  onProcessingAgentChange?.(e.target.value)
                                }
                              >
                                <option value="">Please select</option>
                                {processingAgentOptions.map((agent) => (
                                  <option
                                    key={String(agent.id)}
                                    value={String(agent.id)}
                                  >
                                    {agent.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </XeroIconField>
                        </div>
                      ) : null}

                      <div className="invoice-xero-toolbar invoice-xero-toolbar--xero-create-row2">
                        <XeroField
                          label="Branding theme"
                          required
                          error={errors.brandingId}
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 36, width: "100%" }}
                            />
                          ) : (
                            <select
                              className={selectClass("brandingId")}
                              value={brandingId}
                              onChange={(e) => {
                                setBrandingId(e.target.value);
                                setErrors((prev) => ({
                                  ...prev,
                                  brandingId: "",
                                }));
                              }}
                              disabled={effectiveBrandings.length === 0}
                            >
                              <option value="">
                                {effectiveBrandings.length === 0
                                  ? "No themes"
                                  : "Choose theme…"}
                              </option>
                              {effectiveBrandings.map((branding) => (
                                <option
                                  key={String(branding.id)}
                                  value={String(branding.id)}
                                >
                                  {branding.name || "Untitled theme"}
                                </option>
                              ))}
                            </select>
                          )}
                        </XeroField>

                        <XeroField
                          label="Document type"
                          required
                          error={errors.documentType}
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 36, width: "100%" }}
                            />
                          ) : (
                            <select
                              className={selectClass("documentType")}
                              value={documentType}
                              onChange={(e) => {
                                setDocumentType(e.target.value);
                                setErrors((prev) => ({
                                  ...prev,
                                  documentType: "",
                                }));
                              }}
                            >
                              <option value="">Choose document type…</option>
                              {INVOICE_DOCUMENT_TYPES.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </XeroField>

                        <XeroField
                          label="Branch"
                          required
                          error={errors.branchId}
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 36, width: "100%" }}
                            />
                          ) : (
                            <select
                              className={selectClass("branchId")}
                              value={branchId}
                              onChange={(e) => {
                                setBranchId(e.target.value);
                                setErrors((prev) => ({
                                  ...prev,
                                  branchId: "",
                                }));
                              }}
                              disabled={effectiveBranches.length === 0}
                            >
                              <option value="">
                                {effectiveBranches.length === 0
                                  ? "No branches"
                                  : "Choose branch…"}
                              </option>
                              {effectiveBranches.map((branch) => {
                                const branchOptionId = String(branch.id);
                                const label =
                                  branch.name || branch.agencyname || "Branch";
                                return (
                                  <option
                                    key={branchOptionId}
                                    value={branchOptionId}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                        </XeroField>
                      </div>

                      <div className="invoice-xero-toolbar invoice-xero-toolbar--xero-create-row3">
                        <XeroField
                          label="Currency"
                          className="invoice-xero-field--compact"
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 36, width: "100%" }}
                            />
                          ) : (
                            <select
                              id="invoice-create-currency"
                              name="currency"
                              className={`invoice-xero-control invoice-xero-control--compact invoice-xero-control--select ${selectClass("currency")}`}
                              value={invoiceCurrency}
                              onChange={handleInvoiceCurrencyChange}
                            >
                              <option value="">Please Select</option>
                              {CURRENCY_OPTIONS.map((code) => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              ))}
                            </select>
                          )}
                        </XeroField>

                        <XeroField
                          label="Amounts are"
                          className="invoice-xero-field--compact"
                        >
                          {softLoading ? (
                            <span
                              className="invoice-hub-skeleton__bone"
                              style={{ height: 36, width: "100%" }}
                            />
                          ) : (
                            <select
                              id="invoice-create-amounts-are"
                              className={`invoice-xero-control invoice-xero-control--compact invoice-xero-control--select ${selectClass("amountsAre")}`}
                              value={amountsAreType}
                              onChange={(e) =>
                                handleAmountsAreChange(e.target.value)
                              }
                            >
                              {INVOICE_AMOUNTS_ARE_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </XeroField>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="invoice-xero-toolbar invoice-xero-toolbar--row1">
                        <XeroField
                          label="Contact"
                          className="invoice-xero-field--contact"
                        >
                          {suggestionLinesOnly ? (
                            <div className="invoice-xero-contact-trigger-wrap">
                              <button
                                type="button"
                                ref={commissionContactAnchorRef}
                                className="invoice-xero-contact-trigger invoice-xero-control invoice-xero-control--contact"
                                onClick={() =>
                                  setCommissionBillToPopoverOpen((open) => !open)
                                }
                                aria-expanded={commissionBillToPopoverOpen}
                              >
                                <span
                                  className="invoice-xero-contact__avatar"
                                  aria-hidden="true"
                                >
                                  {clientInitials}
                                </span>
                                <span className="invoice-xero-contact__name">
                                  {billToPartyProfile.fullName || ""}
                                </span>
                              </button>
                              <CommissionBillToContactPopover
                                open={commissionBillToPopoverOpen}
                                anchorRef={commissionContactAnchorRef}
                                onClose={() =>
                                  setCommissionBillToPopoverOpen(false)
                                }
                                providerId={commissionProviderId}
                                profile={billToPartyProfile}
                                printOptions={commissionBillToPrintOptions}
                                onPrintOptionsChange={
                                  handleCommissionBillToPrintOptionsChange
                                }
                                savingPrintOptions={savingBillToPrintOptions}
                                onBillingAddressSaved={
                                  handleSaveProviderBillingAddress
                                }
                                savingAddress={savingProviderBillingAddress}
                                printOptionsReadOnly={
                                  isCommissionLineItemsReadOnly
                                }
                                addressReadOnly={isHeaderFormReadOnly}
                              />
                            </div>
                          ) : (
                            <div className="invoice-xero-control invoice-xero-control--contact">
                              <span
                                className="invoice-xero-contact__avatar"
                                aria-hidden="true"
                              >
                                {clientInitials}
                              </span>
                              <span className="invoice-xero-contact__name">
                                {billToPartyProfile.fullName || ""}
                              </span>
                            </div>
                          )}
                        </XeroField>

                        <XeroField
                          label="Issue date"
                          required
                          error={errors.issueDate}
                        >
                          <div
                            className={`invoice-xero-date-field ${
                              errors.issueDate ? "is-invalid" : ""
                            }`}
                          >
                            <input
                              type="date"
                              className="invoice-xero-date-field__input"
                              value={issueDate}
                              onChange={(e) =>
                                handleIssueDateChange(e.target.value)
                              }
                            />
                          </div>
                        </XeroField>

                        <XeroField label="Due date" error={errors.dueDate}>
                          <div
                            className={`invoice-xero-date-field ${
                              errors.dueDate ? "is-invalid" : ""
                            }`}
                          >
                            <input
                              type="date"
                              className="invoice-xero-date-field__input"
                              value={dueDate}
                              min={issueDate || undefined}
                              onChange={(e) =>
                                handleDueDateChange(e.target.value)
                              }
                            />
                          </div>
                        </XeroField>

                        <XeroField
                          label="Invoice number"
                          className="invoice-xero-field--number"
                        >
                          <div className="invoice-xero-control invoice-xero-control--readonly">
                            <span className="invoice-xero-control__text">
                              {invoiceNumber ||
                                (isEditMode ? "—" : "Select document type")}
                            </span>
                          </div>
                        </XeroField>

                        <XeroField label="Reference">
                          {isEditMode ? (
                            <input
                              type="text"
                              className="invoice-xero-control invoice-xero-lines-table__input"
                              value={reference}
                              onChange={(e) => setReference(e.target.value)}
                              placeholder="Reference"
                            />
                          ) : (
                            <div className="invoice-xero-control invoice-xero-control--readonly">
                              <span className="invoice-xero-control__text">
                                {reference || "—"}
                              </span>
                            </div>
                          )}
                        </XeroField>
                      </div>

                      <div className="invoice-xero-toolbar invoice-xero-toolbar--row2">
                        <XeroField
                          label="Document type"
                          required
                          error={errors.documentType}
                        >
                          <select
                            className={selectClass("documentType")}
                            value={documentType}
                            onChange={(e) => {
                              setDocumentType(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                documentType: "",
                              }));
                            }}
                          >
                            <option value="">Choose document type…</option>
                            {INVOICE_DOCUMENT_TYPES.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </XeroField>

                        <XeroField
                          label="Branding theme"
                          required
                          error={errors.brandingId}
                        >
                          <select
                            className={selectClass("brandingId")}
                            value={brandingId}
                            onChange={(e) => {
                              setBrandingId(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                brandingId: "",
                              }));
                            }}
                            disabled={effectiveBrandings.length === 0}
                          >
                            <option value="">
                              {effectiveBrandings.length === 0
                                ? "No themes"
                                : "Choose theme…"}
                            </option>
                            {effectiveBrandings.map((branding) => (
                              <option
                                key={String(branding.id)}
                                value={String(branding.id)}
                              >
                                {branding.name || "Untitled theme"}
                              </option>
                            ))}
                          </select>
                        </XeroField>

                        <XeroField
                          label="Branch"
                          required
                          error={errors.branchId}
                        >
                          <select
                            className={selectClass("branchId")}
                            value={branchId}
                            onChange={(e) => {
                              setBranchId(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                branchId: "",
                              }));
                            }}
                            disabled={effectiveBranches.length === 0}
                          >
                            <option value="">
                              {effectiveBranches.length === 0
                                ? "No branches"
                                : "Choose branch…"}
                            </option>
                            {effectiveBranches.map((branch) => {
                              const id = String(branch.id);
                              const label =
                                branch.name || branch.agencyname || "Branch";
                              return (
                                <option key={id} value={id}>
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                        </XeroField>
                      </div>
                    </>
                  )}
                </fieldset>

                <div
                  className={`invoice-xero-lines-card${
                    isEditMode || isXeroPageMode
                      ? " invoice-xero-lines-card--edit"
                      : ""
                  }`}
                >
                  {softLoading && isXeroPageMode ? (
                    <div className="invoice-hub-skeleton px-3 pt-3 pb-0">
                      <span
                        className="invoice-hub-skeleton__bone"
                        style={{ height: 12, width: "22%", marginBottom: 12 }}
                      />
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ marginBottom: 10 }}
                      >
                        <span
                          className="invoice-hub-skeleton__bone"
                          style={{ height: 34, width: "58%" }}
                        />
                        <span
                          className="invoice-hub-skeleton__bone"
                          style={{ height: 34, width: "12%" }}
                        />
                        <span
                          className="invoice-hub-skeleton__bone"
                          style={{ height: 34, width: "14%" }}
                        />
                        <span
                          className="invoice-hub-skeleton__bone"
                          style={{ height: 34, width: "10%" }}
                        />
                      </div>
                    </div>
                  ) : null}
                  <table
                    className={`invoice-xero-lines-table${
                      isXeroPageMode ? " invoice-xero-lines-table--edit" : ""
                    }`}
                    style={{
                      opacity: softLoading && isXeroPageMode ? 0.45 : 1,
                      pointerEvents:
                        softLoading && isXeroPageMode ? "none" : undefined,
                    }}
                  >
                    <thead>
                      <tr>
                        {isXeroPageMode ? (
                          <>
                            <th className="invoice-xero-lines-table__drag-col" />
                            <th>Description</th>
                            <th className="text-end">Qty.</th>
                            <th className="text-end">Price</th>
                            {suggestionLinesOnly ? (
                              <th className="text-end">Discount</th>
                            ) : (
                              <th className="text-end">Disc.</th>
                            )}
                            <th className="text-end">Tax rate</th>
                            <th className="text-end">Tax amount</th>
                            <th className="text-end">Total Amount</th>
                            <th className="invoice-xero-lines-table__actions-col" />
                          </>
                        ) : (
                          <>
                            <th>Description</th>
                            <th className="text-end">Quantity</th>
                            <th className="text-end">Unit Price</th>
                            <th className="text-end">Discount</th>
                            <th className="text-end">Tax Type</th>
                            <th className="text-end">Tax Rate</th>
                            <th className="text-end">Tax Amount</th>
                            <th className="text-end">Total Amount</th>
                            <th className="invoice-xero-lines-table__actions-col" />
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((row, lineIndex) => {
                        const computed = computedLineById.get(row.id);
                        const isFirstLine = lineIndex === 0;
                        const lineGross = getLineTotalBeforeDiscount(row);
                        const appliedDiscount = Number(row.discount) || 0;
                        const lineLabel =
                          String(row.description || "Line item").trim() ||
                          "Line item";
                        const rowSuggestionOptions =
                          getDescriptionSuggestionOptionsForRow(row.id);
                        const canOpenLineSuggestions =
                          rowSuggestionOptions.some(
                            (option) => option.kind === "suggestion",
                          ) ||
                          (suggestionLinesOnly && hasCommissionDescriptionPicker);

                        return (
                          <Fragment key={row.id}>
                            <tr>
                              {isXeroPageMode ? (
                                <td className="invoice-xero-lines-table__drag-col">
                                  <FaGripVertical
                                    className="invoice-xero-lines-table__drag-icon"
                                    aria-hidden="true"
                                  />
                                </td>
                              ) : null}
                              <td className="invoice-xero-lines-table__desc-cell">
                                {isCommissionLineItemsReadOnly ? (
                                  <div
                                    className="invoice-xero-lines-table__readonly invoice-xero-lines-table__readonly--desc"
                                    style={{
                                      whiteSpace: "pre-wrap",
                                      lineHeight: 1.35,
                                    }}
                                    title={row.description || undefined}
                                  >
                                    {row.description || "—"}
                                  </div>
                                ) : isXeroPageMode && suggestionLinesOnly ? (
                                  <div
                                    className={`invoice-xero-desc-suggest${
                                      descriptionSuggestionRowId ===
                                      String(row.id)
                                        ? " is-open"
                                        : ""
                                    }`}
                                    data-desc-suggest-anchor={row.id}
                                    ref={(node) =>
                                      bindDescSuggestAnchor(row.id, node)
                                    }
                                    onBlur={handleDescriptionBlur}
                                  >
                                    {row.commission_installment_id ? (
                                      <div className="d-flex flex-column gap-1">
                                        <textarea
                                          className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                          rows={7}
                                          value={row.description}
                                          title={row.description || undefined}
                                          readOnly={
                                            !isCommissionLineDescriptionEditable
                                          }
                                          style={{
                                            width: "100%",
                                            whiteSpace: "pre-wrap",
                                            resize: "vertical",
                                            minHeight: "7.5rem",
                                            lineHeight: 1.35,
                                          }}
                                          onChange={(e) =>
                                            handleLineDescriptionChange(
                                              row.id,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {canOpenLineSuggestions &&
                                        !isPaidInvoiceDescriptionOnlyEdit ? (
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-self-start text-decoration-none"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              toggleDescriptionSuggestions(
                                                row.id,
                                              );
                                            }}
                                          >
                                            Change
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : row.bonus_installment_id ? (
                                      <div className="d-flex flex-column gap-1">
                                        <textarea
                                          className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                          rows={7}
                                          value={row.description}
                                          title={row.description || undefined}
                                          readOnly={
                                            !isCommissionLineDescriptionEditable
                                          }
                                          style={{
                                            width: "100%",
                                            whiteSpace: "pre-wrap",
                                            resize: "vertical",
                                            minHeight: "7.5rem",
                                            lineHeight: 1.35,
                                          }}
                                          onChange={(e) =>
                                            handleLineDescriptionChange(
                                              row.id,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {canOpenLineSuggestions &&
                                        !isPaidInvoiceDescriptionOnlyEdit ? (
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-self-start text-decoration-none"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              toggleDescriptionSuggestions(
                                                row.id,
                                              );
                                            }}
                                          >
                                            Change
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : isInquiryInvoiceFeeType(row.fee_type) ? (
                                      <div className="d-flex flex-column gap-1">
                                        <input
                                          type="text"
                                          className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                          value={row.description}
                                          title={row.description || undefined}
                                          placeholder={
                                            row.fee_type === "application_fee"
                                              ? "Application Fee"
                                              : "Material Fee"
                                          }
                                          onChange={(e) =>
                                            handleLineDescriptionChange(
                                              row.id,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {canOpenLineSuggestions &&
                                        !isPaidInvoiceDescriptionOnlyEdit ? (
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-self-start text-decoration-none"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                              toggleDescriptionSuggestions(
                                                row.id,
                                              );
                                            }}
                                          >
                                            Change
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : canOpenLineSuggestions &&
                                      !isPaidInvoiceDescriptionOnlyEdit ? (
                                      <button
                                        type="button"
                                        className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc text-start"
                                        style={{
                                          width: "100%",
                                          cursor: "pointer",
                                          background: "#fff",
                                        }}
                                        title={row.description || undefined}
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          toggleDescriptionSuggestions(row.id);
                                        }}
                                      >
                                        {row.description ||
                                          "Choose line item (commissionable, bonus, application, material)"}
                                      </button>
                                    ) : (
                                      <input
                                        type="text"
                                        className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                        value={row.description}
                                        title={row.description || undefined}
                                        placeholder="Free line — type a description"
                                        onChange={(e) =>
                                          handleLineDescriptionChange(
                                            row.id,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                ) : isXeroPageMode &&
                                  descriptionSuggestionOptions.length > 0 ? (
                                  <div
                                    className={`invoice-xero-desc-suggest${
                                      descriptionSuggestionRowId ===
                                      String(row.id)
                                        ? " is-open"
                                        : ""
                                    }`}
                                    data-desc-suggest-anchor={row.id}
                                    ref={(node) =>
                                      bindDescSuggestAnchor(row.id, node)
                                    }
                                    onBlur={handleDescriptionBlur}
                                  >
                                    <input
                                      type="text"
                                      className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                      value={row.description}
                                      title={row.description || undefined}
                                      placeholder={
                                        Array.isArray(lineItemSuggestions) ||
                                        installmentSuggestionSource
                                          ? "Type or pick a commission/bonus installment"
                                          : "Type or pick an application suggestion"
                                      }
                                      onFocus={() =>
                                        openDescriptionSuggestions(row.id)
                                      }
                                      onChange={(e) =>
                                        handleLineDescriptionChange(
                                          row.id,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    className="invoice-xero-lines-table__input invoice-xero-lines-table__input--desc"
                                    value={row.description}
                                    title={row.description || undefined}
                                    placeholder="e.g. Professional Fees – SID 482 Visa"
                                    onChange={(e) =>
                                      updateLineItem(
                                        row.id,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </td>
                              <td className="text-end">
                                {(row.quantity_locked && suggestionLinesOnly) ||
                                isPaidInvoiceLineFieldLocked ? (
                                  <span className="invoice-xero-lines-table__readonly">
                                    {formatInvoiceNumberDisplay(row.quantity) ||
                                      "0.00"}
                                  </span>
                                ) : (
                                  <input
                                    type="text"
                                    inputMode={
                                      isXeroPageMode ? "decimal" : "numeric"
                                    }
                                    className="invoice-xero-lines-table__input invoice-xero-lines-table__input--num"
                                    value={displayLineItemInputValue(
                                      row.quantity,
                                    )}
                                    onChange={(e) =>
                                      handleLineNumberChange(
                                        row.id,
                                        "quantity",
                                        e.target.value,
                                        isXeroPageMode
                                          ? sanitizeDecimalInput
                                          : sanitizeIntegerInput,
                                      )
                                    }
                                    onBlur={() =>
                                      handleLineNumberBlur(row.id, "quantity")
                                    }
                                  />
                                )}
                              </td>
                              <td className="text-end">
                                {(row.unit_price_locked &&
                                  (!isXeroPageMode || suggestionLinesOnly)) ||
                                isPaidInvoiceLineFieldLocked ? (
                                  <span className="invoice-xero-lines-table__readonly">
                                    {formatInvoiceMoney(
                                      row.unit_price || 0,
                                      "",
                                      {},
                                    )}
                                  </span>
                                ) : (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="invoice-xero-lines-table__input invoice-xero-lines-table__input--num"
                                    value={displayLineItemInputValue(
                                      row.unit_price,
                                    )}
                                    onChange={(e) =>
                                      handleLineNumberChange(
                                        row.id,
                                        "unit_price",
                                        e.target.value,
                                        sanitizeDecimalInput,
                                      )
                                    }
                                    onBlur={() =>
                                      handleLineNumberBlur(row.id, "unit_price")
                                    }
                                  />
                                )}
                              </td>
                              <td className="text-end">
                                {isXeroPageMode ? (
                                  suggestionLinesOnly ? (
                                    isCommissionTuitionInvoiceLine(row) ? (
                                      <span
                                        className={`invoice-xero-lines-table__readonly${
                                          Number(
                                            row.student_discount ??
                                              row.discount,
                                          ) > 0
                                            ? " invoice-line-discount__value"
                                            : " invoice-line-discount__empty"
                                        }`}
                                      >
                                        {Number(
                                          row.student_discount ?? row.discount,
                                        ) > 0
                                          ? formatInvoiceMoney(
                                              Number(
                                                row.student_discount ??
                                                  row.discount,
                                              ),
                                              "",
                                            )
                                          : "—"}
                                      </span>
                                    ) : (
                                      <span className="invoice-xero-lines-table__readonly invoice-line-discount__empty">
                                        —
                                      </span>
                                    )
                                  ) : (
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="invoice-xero-lines-table__input invoice-xero-lines-table__input--num"
                                      value={displayLineItemInputValue(
                                        row.discount_custom_amount ||
                                          row.discount,
                                      )}
                                      onChange={(e) =>
                                        handleSimpleLineDiscountChange(
                                          row.id,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() =>
                                        handleSimpleLineDiscountBlur(row.id)
                                      }
                                      placeholder="0"
                                    />
                                  )
                                ) : usesSegregatedLineDiscounts ? (
                                  isEditMode ? (
                                    <div className="invoice-line-discount__cell">
                                      <span
                                        className={`invoice-xero-lines-table__readonly${
                                          appliedDiscount > 0
                                            ? " invoice-line-discount__value"
                                            : " invoice-line-discount__empty"
                                        }`}
                                      >
                                        {appliedDiscount > 0
                                          ? formatInvoiceMoney(
                                              appliedDiscount,
                                              "",
                                            )
                                          : "—"}
                                      </span>
                                      <button
                                        type="button"
                                        className="invoice-line-discount__edit-btn"
                                        onClick={() =>
                                          handleOpenLineDiscountEdit(row)
                                        }
                                        title="Edit discount"
                                        aria-label="Edit discount"
                                      >
                                        <FaPen size={10} />
                                      </button>
                                    </div>
                                  ) : (
                                    <span
                                      className={`invoice-xero-lines-table__readonly${
                                        appliedDiscount > 0
                                          ? " invoice-line-discount__value"
                                          : " invoice-line-discount__empty"
                                      }`}
                                    >
                                      {appliedDiscount > 0
                                        ? formatInvoiceMoney(
                                            appliedDiscount,
                                            "",
                                          )
                                        : "—"}
                                    </span>
                                  )
                                ) : isFirstLine ? (
                                  <div className="invoice-line-discount__cell">
                                    {showDiscountEditTrigger ? (
                                      <>
                                        <span className="invoice-xero-lines-table__readonly invoice-line-discount__value">
                                          {formatInvoiceMoney(
                                            savedDiscountAmount,
                                            "",
                                          )}
                                        </span>
                                        <button
                                          type="button"
                                          className="invoice-line-discount__edit-btn"
                                          onClick={handleOpenDiscountEditor}
                                          title="Edit discount"
                                          aria-label="Edit discount"
                                        >
                                          <FaPen size={10} />
                                        </button>
                                      </>
                                    ) : (
                                      <span
                                        className={`invoice-xero-lines-table__readonly${
                                          displayedLineDiscountAmount > 0 ||
                                          (showInvoiceLevelDiscountEditor &&
                                            isValidCustomDiscountAmount(
                                              discountCustomAmount,
                                            ))
                                            ? " invoice-line-discount__value"
                                            : " invoice-line-discount__empty"
                                        }`}
                                      >
                                        {displayedLineDiscountAmount > 0 ||
                                        (showInvoiceLevelDiscountEditor &&
                                          isValidCustomDiscountAmount(
                                            discountCustomAmount,
                                          ))
                                          ? formatInvoiceMoney(
                                              displayedLineDiscountAmount,
                                              "",
                                            )
                                          : "—"}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="invoice-xero-lines-table__readonly invoice-line-discount__empty">
                                    —
                                  </span>
                                )}
                              </td>
                              {!isXeroPageMode ? (
                                <td className="text-end">
                                  {row.tax_type_locked ? (
                                    <span className="invoice-xero-lines-table__readonly invoice-xero-lines-table__readonly--tax">
                                      {TAX_TYPE_OPTIONS.find(
                                        (item) =>
                                          item.value ===
                                          normalizeTaxType(row.tax_type),
                                      )?.label || "—"}
                                    </span>
                                  ) : (
                                    <select
                                      className="invoice-xero-lines-table__select"
                                      value={row.tax_type || "notax"}
                                      onChange={(e) =>
                                        handleLineTaxTypeChange(
                                          row.id,
                                          e.target.value,
                                        )
                                      }
                                    >
                                      {TAX_TYPE_OPTIONS.map((item) => (
                                        <option
                                          key={item.value}
                                          value={item.value}
                                        >
                                          {item.label}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                              ) : null}
                              <td className="text-end">
                                <div className="invoice-xero-lines-table__tax-rate">
                                  {isXeroPageMode ? (
                                    isPaidInvoiceLineFieldLocked ? (
                                      <span className="invoice-xero-lines-table__readonly">
                                        {normalizeTaxType(row.tax_type) ===
                                        "notax"
                                          ? ""
                                          : row.tax_rate
                                            ? `${row.tax_rate}%`
                                            : ""}
                                      </span>
                                    ) : (
                                      <div className="invoice-xero-lines-table__tax-controls">
                                        <select
                                          className="invoice-xero-lines-table__select invoice-xero-lines-table__select--tax-type"
                                          value={row.tax_type || "notax"}
                                          onChange={(e) =>
                                            handleLineTaxTypeChange(
                                              row.id,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          {TAX_TYPE_OPTIONS.map((item) => (
                                            <option
                                              key={item.value}
                                              value={item.value}
                                            >
                                              {item.label}
                                            </option>
                                          ))}
                                        </select>
                                        {normalizeTaxType(row.tax_type) !==
                                        "notax" ? (
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className="invoice-xero-lines-table__input invoice-xero-lines-table__input--tax-rate"
                                            value={displayLineItemInputValue(
                                              row.tax_rate,
                                            )}
                                            onChange={(e) =>
                                              handleLineTaxRateChange(
                                                row.id,
                                                e.target.value,
                                              )
                                            }
                                            onBlur={() =>
                                              handleLineTaxRateBlur(row.id)
                                            }
                                            placeholder="%"
                                            aria-label={`Tax rate for ${lineLabel}`}
                                          />
                                        ) : null}
                                      </div>
                                    )
                                  ) : (
                                    <span className="invoice-xero-lines-table__readonly">
                                      {row.tax_rate ? `${row.tax_rate}%` : "0%"}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-end invoice-xero-lines-table__amount">
                                {normalizeTaxType(row.tax_type) === "notax"
                                  ? "—"
                                  : formatInvoiceMoney(
                                      computed?.tax_amount ?? 0,
                                      "",
                                    )}
                              </td>
                              <td className="text-end fw-semibold invoice-xero-lines-table__amount">
                                {formatInvoiceMoney(
                                  suggestionLinesOnly &&
                                    isCommissionTuitionInvoiceLine(row)
                                    ? getLineGrossAmount(row)
                                    : getLineDisplayTotalAmount(row),
                                  "",
                                )}
                              </td>
                              <td className="text-end invoice-xero-lines-table__actions-col">
                                {!isPaidInvoiceLineFieldLocked ? (
                                  <button
                                    type="button"
                                    className="invoice-xero-lines-table__remove-btn"
                                    onClick={() => removeLineItem(row.id)}
                                    title="Remove line"
                                    aria-label="Remove line"
                                  >
                                    <FaTrashAlt />
                                  </button>
                                ) : null}
                              </td>
                            </tr>

                            {usesSegregatedLineDiscounts &&
                            (!isEditMode ||
                              editingLineDiscountId === row.id) ? (
                              <InvoiceLineItemDiscountSection
                                lineLabel={lineLabel}
                                discountTab={row.discount_tab || "predefined"}
                                onDiscountTabChange={(tab) =>
                                  updateLineDiscountMeta(row.id, {
                                    discount_tab: tab,
                                    ...(tab === "predefined"
                                      ? { discount_custom_amount: "" }
                                      : { discount_rule_id: "" }),
                                  })
                                }
                                discountRules={discountFeeRules}
                                selectedRuleId={row.discount_rule_id || ""}
                                onSelectedRuleIdChange={(value) =>
                                  updateLineDiscountMeta(row.id, {
                                    discount_rule_id: value,
                                  })
                                }
                                customAmount={row.discount_custom_amount || ""}
                                onCustomAmountChange={(value) =>
                                  updateLineDiscountMeta(row.id, {
                                    discount_custom_amount: value,
                                  })
                                }
                                reason={row.discount_reason || ""}
                                onReasonChange={(value) =>
                                  updateLineDiscountMeta(row.id, {
                                    discount_reason: value,
                                  })
                                }
                                lineGross={lineGross}
                                appliedAmount={appliedDiscount}
                                error={
                                  lineDiscountErrorId === row.id
                                    ? errors.lineItems
                                    : ""
                                }
                                editMode={isEditMode}
                                isDiscountUpdate={appliedDiscount > 0}
                                onUpdateDiscount={() =>
                                  handleUpdateLineDiscount(row)
                                }
                                onCancel={() =>
                                  handleCancelLineDiscountEdit(row.id)
                                }
                                updatingDiscount={updatingDiscount}
                              />
                            ) : null}
                          </Fragment>
                        );
                      })}

                      {showInvoiceLevelDiscountEditor && !isXeroPageMode ? (
                        <InvoiceLineDiscountRow
                          discountTab={discountTab}
                          onDiscountTabChange={handleDiscountTabChange}
                          discountRules={discountFeeRules}
                          selectedRuleId={discountRuleId}
                          onSelectedRuleIdChange={(value) => {
                            setDiscountRuleId(value);
                            setErrors((prev) => ({
                              ...prev,
                              invoiceDiscount: "",
                            }));
                          }}
                          customAmount={discountCustomAmount}
                          onCustomAmountChange={(value) => {
                            setDiscountCustomAmount(value);
                            setErrors((prev) => ({
                              ...prev,
                              invoiceDiscount: "",
                            }));
                          }}
                          reason={discountReason}
                          onReasonChange={(value) => {
                            setDiscountReason(value);
                            setErrors((prev) => ({
                              ...prev,
                              invoiceDiscount: "",
                            }));
                          }}
                          grossSubtotal={lineTotals.gross_subtotal}
                          totalAmount={lineTotals.total_amount}
                          error={errors.invoiceDiscount || discountExceedsError}
                          editMode={isEditMode}
                          isDiscountUpdate={Boolean(
                            activeDiscountTransaction?.id ||
                            getInvoiceDiscountLedgerId(existingInvoice),
                          )}
                          onUpdateDiscount={handleUpdateDiscount}
                          onCancelDiscountEdit={
                            hasExistingDiscount
                              ? handleCancelDiscountEdit
                              : null
                          }
                          updatingDiscount={updatingDiscount}
                        />
                      ) : null}
                    </tbody>
                    {!isPaidInvoiceLineFieldLocked ? (
                      <tfoot>
                        <tr>
                          <td
                            colSpan={isXeroPageMode ? 10 : 9}
                            className="invoice-xero-lines-table__add-cell"
                          >
                            <div className="invoice-xero-lines-table__add-actions">
                              <button
                                type="button"
                                className={`invoice-xero-lines-table__add-btn${
                                  isXeroPageMode
                                    ? " invoice-xero-lines-table__add-btn--xero"
                                    : ""
                                }`}
                                onClick={addLineItem}
                              >
                                <FaPlus
                                  className={
                                    isXeroPageMode ? undefined : "me-1"
                                  }
                                  size={isXeroPageMode ? 11 : undefined}
                                />
                                {isXeroPageMode ? "Add row" : "Add line item"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                  {errors.lineItems && (
                    <div className="invoice-xero-lines-table__error">
                      {errors.lineItems}
                    </div>
                  )}
                  {isXeroPageMode ? (
                    <div className="invoice-xero-page-footer invoice-xero-page-footer--stacked">
                      <div className="invoice-xero-page-footer__subtotal">
                        <span>Subtotal</span>
                        <strong>
                          {formatInvoiceMoney(
                            xeroExTaxDisplayTotals?.subtotalExTax ?? 0,
                            "",
                          )}
                        </strong>
                      </div>
                      {xeroExTaxDisplayTotals?.taxLabel &&
                      (xeroExTaxDisplayTotals?.gst ?? 0) > 0 ? (
                        <div className="invoice-xero-page-footer__subtotal">
                          <span>{xeroExTaxDisplayTotals.taxLabel}</span>
                          <strong>
                            {formatInvoiceMoney(
                              xeroExTaxDisplayTotals?.gst ?? 0,
                              "",
                            )}
                          </strong>
                        </div>
                      ) : null}
                      <div className="invoice-xero-page-footer__subtotal invoice-xero-page-footer__subtotal--total">
                        <span>Total</span>
                        <strong>
                          {formatInvoiceMoney(
                            xeroExTaxDisplayTotals?.total ?? 0,
                            "",
                          )}
                        </strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {isEditMode && !useCommissionDraftLikeFlow ? (
              <div className="invoice-edit-sections">
                {!isVoidInvoice && useCommissionApprovedWithPaymentPanel ? (
                  <>
                    {showCommissionDualApprovalStatus ? (
                      <div className="invoice-edit-sections__dual-status small text-muted mb-2">
                        <div>
                          <strong>Approval</strong> · Approved
                        </div>
                        <div>
                          <strong>Payment</strong> ·{" "}
                          {getInvoiceStatusLabel(invoiceWorkflowStatus)}
                        </div>
                      </div>
                    ) : null}
                    {invoiceFinancialSummary ? (
                      <InvoiceEditSummaryPanel
                        currency={currency}
                        showDiscount={showCommissionInvoiceSummaryDiscount}
                        totalAmount={invoiceFinancialSummary.total}
                        discountAmount={invoiceFinancialSummary.discount}
                        paidAmount={invoiceFinancialSummary.paid}
                        amountDue={invoiceFinancialSummary.remaining}
                      />
                    ) : null}
                    {!hidePaymentsPanel ? (
                      <InvoiceEditPaymentsPanel
                        currency={currency}
                        invoiceNumber={invoiceNumber}
                        transactions={invoiceTransactions}
                        applicationInvoices={applicationInvoices}
                        ledgerEntries={ledgerEntries}
                        allowAddPayment={
                          invoiceAmountDue > 0 &&
                          !isPaidInvoice &&
                          !invoicePaymentsReadOnly
                        }
                        onSaveFinanceInvoicePayment={
                          onSaveFinanceInvoicePayment
                        }
                        savingFinanceInvoicePayment={
                          savingFinanceInvoicePayment
                        }
                        onReverseFinanceInvoicePayment={null}
                        reversingFinanceInvoicePayment={false}
                        amountDue={invoiceAmountDue}
                        lineItems={paymentLineItems}
                        draftPayments={draftPayments}
                        onSaveFinanceInvoiceBulkPayment={
                          onSaveFinanceInvoiceBulkPayment
                        }
                        onReversePayment={null}
                        reversingPayment={false}
                        readOnly={invoicePaymentsReadOnly}
                      />
                    ) : null}
                  </>
                ) : !isVoidInvoice &&
                  (useCommissionPaymentViewFlow || useCommissionPaidFlow) ? (
                  <>
                    {invoiceFinancialSummary ? (
                      <InvoiceEditSummaryPanel
                        currency={currency}
                        showDiscount={showCommissionInvoiceSummaryDiscount}
                        totalAmount={invoiceFinancialSummary.total}
                        discountAmount={invoiceFinancialSummary.discount}
                        paidAmount={invoiceFinancialSummary.paid}
                        amountDue={invoiceFinancialSummary.remaining}
                      />
                    ) : null}
                    {!hidePaymentsPanel ? (
                      <InvoiceEditPaymentsPanel
                        currency={currency}
                        invoiceNumber={invoiceNumber}
                        transactions={invoiceTransactions}
                        applicationInvoices={applicationInvoices}
                        ledgerEntries={ledgerEntries}
                        allowAddPayment={
                          useCommissionPaymentViewFlow &&
                          invoiceAmountDue > 0 &&
                          !invoicePaymentsReadOnly
                        }
                        onSaveFinanceInvoicePayment={
                          onSaveFinanceInvoicePayment
                        }
                        savingFinanceInvoicePayment={
                          savingFinanceInvoicePayment
                        }
                        onReverseFinanceInvoicePayment={
                          invoicePaymentsReadOnly
                            ? null
                            : onReverseFinanceInvoicePayment
                        }
                        reversingFinanceInvoicePayment={
                          reversingFinanceInvoicePayment
                        }
                        amountDue={invoiceAmountDue}
                        lineItems={paymentLineItems}
                        draftPayments={draftPayments}
                        onSaveFinanceInvoiceBulkPayment={
                          onSaveFinanceInvoiceBulkPayment
                        }
                        onReversePayment={
                          invoicePaymentsReadOnly ? null : onReversePayment
                        }
                        reversingPayment={reversingPayment}
                        readOnly={invoicePaymentsReadOnly}
                      />
                    ) : null}
                  </>
                ) : !isVoidInvoice &&
                  (isWorkflowEditableInvoice || isInvoiceLocked) ? (
                  <>
                    {(
                      isWorkflowEditableInvoice
                        ? createFinancialSummary
                        : invoiceFinancialSummary
                    ) ? (
                      <InvoiceEditSummaryPanel
                        currency={currency}
                        showDiscount={showCommissionInvoiceSummaryDiscount}
                        totalAmount={
                          (isWorkflowEditableInvoice
                            ? createFinancialSummary
                            : invoiceFinancialSummary
                          ).total
                        }
                        discountAmount={
                          (isWorkflowEditableInvoice
                            ? createFinancialSummary
                            : invoiceFinancialSummary
                          ).discount
                        }
                        paidAmount={
                          (isWorkflowEditableInvoice
                            ? createFinancialSummary
                            : invoiceFinancialSummary
                          ).paid
                        }
                        amountDue={
                          (isWorkflowEditableInvoice
                            ? createFinancialSummary
                            : invoiceFinancialSummary
                          ).remaining
                        }
                      />
                    ) : null}
                    {!hidePaymentsPanel ? (
                      <InvoiceEditPaymentsPanel
                        currency={currency}
                        invoiceNumber={invoiceNumber}
                        transactions={
                          isWorkflowEditableInvoice
                            ? [
                                ...invoiceTransactions,
                                ...draftPaymentTransactions,
                              ]
                            : invoiceTransactions
                        }
                        applicationInvoices={applicationInvoices}
                        ledgerEntries={ledgerEntries}
                        paymentForm={
                          isWorkflowEditableInvoice ? null : paymentForm
                        }
                        allowAddPayment={
                          !invoicePaymentsReadOnly &&
                          (useWorkflowInvoiceFlow
                            ? commissionPaymentCap > 0
                            : isWorkflowEditableInvoice
                              ? createAmountDue > 0
                              : invoiceAmountDue > 0)
                        }
                        onSaveFinanceInvoicePayment={
                          useWorkflowInvoiceFlow && onSaveFinanceInvoicePayment
                            ? onSaveFinanceInvoicePayment
                            : isWorkflowEditableInvoice
                              ? handleAddDraftPayment
                              : onSaveFinanceInvoicePayment
                        }
                        savingFinanceInvoicePayment={
                          useWorkflowInvoiceFlow && onSaveFinanceInvoicePayment
                            ? savingFinanceInvoicePayment
                            : isWorkflowEditableInvoice
                              ? false
                              : savingFinanceInvoicePayment
                        }
                        onRemoveDraftPayment={
                          useWorkflowInvoiceFlow && onSaveFinanceInvoicePayment
                            ? null
                            : allowInvoicePayment
                              ? handleRemoveDraftPayment
                              : null
                        }
                        onReverseFinanceInvoicePayment={
                          invoicePaymentsReadOnly
                            ? null
                            : onReverseFinanceInvoicePayment
                        }
                        reversingFinanceInvoicePayment={
                          reversingFinanceInvoicePayment
                        }
                        // amountDue={
                        //   suggestionLinesOnly
                        //     ? commissionPaymentCap
                        //     : isWorkflowEditableInvoice
                        //       ? Math.max(
                        //           paymentHeadroom,
                        //           createAmountDue,
                        //           invoiceAmountDue,
                        //         )
                        //       : invoiceAmountDue
                        // }
                        amountDue={
                          useWorkflowInvoiceFlow
                            ? commissionPaymentCap
                            : isWorkflowEditableInvoice
                              ? createAmountDue
                              : invoiceAmountDue
                        }
                        lineItems={paymentLineItems}
                        draftPayments={draftPayments}
                        onSaveFinanceInvoiceBulkPayment={
                          useWorkflowInvoiceFlow && onSaveFinanceInvoicePayment
                            ? onSaveFinanceInvoiceBulkPayment
                            : undefined
                        }
                        onReversePayment={
                          invoicePaymentsReadOnly ? null : onReversePayment
                        }
                        reversingPayment={reversingPayment}
                        readOnly={invoicePaymentsReadOnly}
                      />
                    ) : null}
                  </>
                ) : !isVoidInvoice ? (
                  <>
                    {invoiceFinancialSummary ? (
                      <InvoiceEditSummaryPanel
                        currency={currency}
                        showDiscount={showCommissionInvoiceSummaryDiscount}
                        totalAmount={invoiceFinancialSummary.total}
                        discountAmount={invoiceFinancialSummary.discount}
                        paidAmount={invoiceFinancialSummary.paid}
                        amountDue={invoiceFinancialSummary.remaining}
                      />
                    ) : null}
                    {!hidePaymentsPanel ? (
                      <InvoiceEditPaymentsPanel
                        currency={currency}
                        invoiceNumber={invoiceNumber}
                        transactions={invoiceTransactions}
                        applicationInvoices={applicationInvoices}
                        ledgerEntries={ledgerEntries}
                        paymentForm={paymentForm}
                        allowAddPayment={false}
                        readOnly={invoicePaymentsReadOnly}
                        onSaveFinanceInvoicePayment={
                          onSaveFinanceInvoicePayment
                        }
                        savingFinanceInvoicePayment={
                          savingFinanceInvoicePayment
                        }
                        onReverseFinanceInvoicePayment={
                          onReverseFinanceInvoicePayment
                        }
                        reversingFinanceInvoicePayment={
                          reversingFinanceInvoicePayment
                        }
                        amountDue={invoiceAmountDue}
                        lineItems={paymentLineItems}
                        draftPayments={draftPayments}
                        onSaveFinanceInvoiceBulkPayment={
                          onSaveFinanceInvoiceBulkPayment
                        }
                        onReversePayment={onReversePayment}
                        reversingPayment={reversingPayment}
                      />
                    ) : null}
                  </>
                ) : null}
                <InvoiceEditHistoryNotesPanel
                  key={existingInvoice?.id || "new"}
                  historyEntries={invoiceActivityHistory}
                  onAddNote={
                    isVoidInvoice || !invoicePaymentsReadOnly
                      ? onAddInvoiceNote
                      : null
                  }
                  addingNote={addingInvoiceNote}
                  loading={loadingInvoiceNotes}
                  followupConfig={invoiceFollowupConfig}
                  onVisible={onHistoryVisible}
                />
              </div>
            ) : (
              <div className="invoice-edit-sections">
                {useCommissionDraftLikeFlow && isEditMode ? (
                  <>
                    {createFinancialSummary ? (
                      <InvoiceEditSummaryPanel
                        currency={currency}
                        showDiscount={showCommissionInvoiceSummaryDiscount}
                        totalAmount={createFinancialSummary.total}
                        discountAmount={createFinancialSummary.discount}
                        paidAmount={createFinancialSummary.paid}
                        amountDue={createFinancialSummary.remaining}
                      />
                    ) : null}
                    <InvoiceEditHistoryNotesPanel
                      key={existingInvoice?.id || "new"}
                      historyEntries={invoiceActivityHistory}
                      onAddNote={onAddInvoiceNote}
                      addingNote={addingInvoiceNote}
                      loading={loadingInvoiceNotes}
                      followupConfig={invoiceFollowupConfig}
                      onVisible={onHistoryVisible}
                    />
                  </>
                ) : createFinancialSummary ? (
                  <>
                    <InvoiceEditSummaryPanel
                      currency={currency}
                      showDiscount={showCommissionInvoiceSummaryDiscount}
                      totalAmount={createFinancialSummary.total}
                      discountAmount={createFinancialSummary.discount}
                      paidAmount={createFinancialSummary.paid}
                      amountDue={createFinancialSummary.remaining}
                    />
                    {!useWorkflowInvoiceFlow ? (
                      <InvoiceEditPaymentsPanel
                        currency={currency}
                        invoiceNumber={invoiceNumber}
                        transactions={draftPaymentTransactions}
                        allowAddPayment
                        onSaveFinanceInvoicePayment={handleAddDraftPayment}
                        onRemoveDraftPayment={handleRemoveDraftPayment}
                        amountDue={createAmountDue}
                        lineItems={paymentLineItems}
                        draftPayments={draftPayments}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      {!pageMode ? (
        <div
          className={`invoice-wizard-footer${
            isEditMode ? " invoice-wizard-footer--edit" : ""
          }`}
        >
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          {!isEditMode && (
            <button
              type="button"
              className="btn btn-primary px-4 invoice-wizard-header-btn"
              onClick={() => handleSave(saveStatus)}
              disabled={saving || loadingOptions}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {pageMode ? (
        <div className="invoice-wizard-page">{wizardContent}</div>
      ) : (
        <>
          <div
            className="modal-backdrop fade show invoice-wizard-backdrop"
            onClick={onClose}
          />
          <div
            className="modal fade show d-block invoice-wizard-modal"
            tabIndex={-1}
          >
            <div
              className={`modal-dialog modal-dialog-centered modal-dialog-scrollable invoice-wizard-dialog${
                isEditMode ? " invoice-wizard-dialog--edit" : ""
              }`}
            >
              {wizardContent}
            </div>
          </div>
        </>
      )}

      {descriptionSuggestionRowId && !isPaidInvoiceLineFieldLocked ? (
        <DescriptionSuggestionList
          open
          getAnchorElement={getOpenDescSuggestAnchor}
          rowId={descriptionSuggestionRowId}
          options={getDescriptionSuggestionOptionsForRow(
            descriptionSuggestionRowId,
          )}
          onSelect={handleDescriptionSuggestionSelect}
        />
      ) : null}

      {showPreview && (
        <>
          <div
            className="modal-backdrop fade show invoice-xero-preview-backdrop"
            onClick={handleClosePreview}
          />
          <div
            className="invoice-xero-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="invoice-xero-preview-modal__layout">
              {showInvoicePreviewCustomize ? (
                <div className="invoice-xero-preview-modal__sidebar no-print">
                  <InvoicePreviewCustomizePanel
                    displayOptions={invoiceDisplayOptions}
                    onChange={handleInvoiceDisplayOptionsChange}
                    onClose={handleClosePreview}
                    readOnly={isHeaderFormReadOnly}
                    saving={savingBillToPrintOptions}
                  />
                </div>
              ) : null}
              <div className="invoice-xero-preview-modal__sheet-wrap">
                <InvoiceXeroPreview
                  draft={previewDraft}
                  onClose={handleClosePreview}
                  interactiveCommissionPreview={suggestionLinesOnly}
                  previewUiReadOnly={isHeaderFormReadOnly}
                  commissionProviderId={commissionProviderId}
                  onSaveProviderName={handleSaveProviderNameFromPreview}
                  onSaveAbn={handleSaveAbnFromPreview}
                  invoiceSettingId={invoiceSetting?.id}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {previewAutoPrint ? (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: -1,
            opacity: 0,
            pointerEvents: "none",
            width: "210mm",
            background: "#fff",
          }}
        >
          <InvoiceXeroPreview
            draft={previewDraft}
            embedded
            showToolbar={false}
            autoPrint
            onClose={handlePdfExportComplete}
          />
        </div>
      ) : null}
      {approveEmailModal ? (
        <FinanceInvoiceApproveEmailModal
          open={Boolean(approveEmailModal)}
          invoiceId={approveEmailModal?.invoiceId}
          clientId={approveEmailModal?.clientId}
          isCommission={Boolean(approveEmailModal?.isCommission)}
          fallbackRecipientEmail={
            approveEmailModal?.fallbackRecipientEmail || ""
          }
          fallbackRecipientName={approveEmailModal?.fallbackRecipientName || ""}
          fallbackInvoiceNumber={approveEmailModal?.fallbackInvoiceNumber || ""}
          onClose={() => {
            setApproveEmailModal(null);
            pendingApprovePayloadRef.current = null;
          }}
          onApprove={
            approveEmailModal?.requiresApprove === false
              ? undefined
              : async () => {
                  const invoiceId = approveEmailModal?.invoiceId;
                  const combinedPayload = pendingApprovePayloadRef.current;
                  if (!invoiceId || !combinedPayload) return;

                  const payload = buildInvoiceUpdatePayload({
                    ...combinedPayload,
                    existingInvoice:
                      existingInvoice?.id === invoiceId
                        ? existingInvoice
                        : { ...(existingInvoice || {}), id: invoiceId },
                  });

                  await onUpdate?.({ id: invoiceId, payload }, { approve: true });
                  pendingApprovePayloadRef.current = null;
                }
          }
          onSent={() => {
            onInvoiceEmailSent?.();
          }}
        />
      ) : null}
    </>
  );
}

export default InvoiceCreateWizard;
